import React, { useEffect, useRef, useState } from "react";
import { Reorder, useDragControls } from "framer-motion";
import { AppShell } from "@/components/AppShell";
import { checkoutOrigin } from "@/lib/checkout";
import { api } from "@/lib/api";
import { DEFAULT_HIDDEN_SECTIONS, readRoleSectionsDraft, clearRoleSectionsDraft, ROLE_SECTIONS_SAVED_EVENT, levelsWithGrants } from "@/lib/adminSections";
import { useGuard } from "@/lib/guards"; // lot 758 — interrupteurs de repli (carte d'associé)
import { latencyStats, latencyDomainMax } from "@/lib/latencyStats"; // lot 760 — échelle Santé système
import { useModerationFocus } from "@/lib/useModerationFocus";
import { AdminPager } from "@/components/admin/AdminPager";
import { usePager, PagerBar, ADMIN_PAGE_SIZE } from "@/components/admin/usePager";
import { useApp } from "@/context/AppContext";
import { FileUpload, resolveMediaUrl, onMediaImgError } from "@/components/FileUpload";
import { imageVariant } from "@/lib/mediaUrl";
import { AdStatsDialog } from "@/pages/AdsManager";
import VideoPlayer from "@/components/VideoPlayer";
import { NEUTRAL_POSTER } from "@/lib/neutralPoster";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter, DialogTrigger } from "@/components/ui/dialog";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import { AppCampaignDialog } from "@/components/admin/AppCampaignDialog";
import { UpdateCampaignDialog } from "@/components/admin/UpdateCampaignDialog";
import { AppVersionsDiagnosticDialog } from "@/components/admin/AppVersionsDiagnosticDialog";
import {
  Users, LayoutDashboard, Layers, Languages, ShieldAlert, IdCard, BookOpen, Church, Globe,
  MessagesSquare, Wallet, Activity, Eye, EyeOff, Save, RotateCcw, GripVertical, Check, X, Sparkles, Coins,
  Megaphone, MousePointerClick, TrendingUp, Settings, Bell, KeyRound, Lock, ListChecks, Plus, Minus,
  Newspaper, LifeBuoy, HeartHandshake, Receipt, Mail, Bot, ShieldCheck, FileText, Clock, Radio, Inbox,
  Server, Database, Gauge, RefreshCw, AlertTriangle, Trash2, UserPlus, Link2, Download, Pencil, Ban, CalendarDays, Loader2, HardDrive, Phone, Search, Play, ChevronDown, Paperclip, ExternalLink, Music, Scale, BarChart3, Smartphone, Monitor, HelpCircle, Crosshair, Percent, Landmark, ArrowLeftRight, BellRing, History, Handshake, UserCheck,
} from "lucide-react";
import { AreaChart, Area, LineChart, Line, BarChart, Bar, XAxis, YAxis, Tooltip as ChartTooltip, ResponsiveContainer, CartesianGrid, ReferenceArea, ReferenceLine } from "recharts";
import { Navigate, useSearchParams, useParams, useNavigate, Link } from "react-router-dom";
import { timeAgo } from "@/lib/timeAgo";
import { SettingsPanel } from "@/components/admin/SettingsPanel";
import { LiveStorageCard } from "@/components/admin/LiveStorageCard";
import { AlgorithmsPanel } from "@/components/admin/AlgorithmsPanel";
import { IntegrationsPanel } from "@/components/admin/IntegrationsPanel";
import { NotificationsPanel } from "@/components/admin/NotificationsPanel";
import { AccountListManager } from "@/components/admin/AccountListManager";
import { TranslationsPanel } from "@/components/admin/TranslationsPanel";
import { ChurchesPanel } from "@/components/admin/ChurchesPanel";
import { DenominationsPanel } from "@/components/admin/DenominationsPanel";
import { ReadingPlansPanel } from "@/components/admin/ReadingPlansPanel";
import { ContentsPanel } from "@/components/admin/ContentsPanel";
import { ModerationTriagePanel } from "@/components/admin/ModerationTriagePanel";
import { TicketsPanel } from "@/components/admin/TicketsPanel";
import { InterestsPanel } from "@/components/admin/InterestsPanel";
import { EmailsPanel } from "@/components/admin/EmailsPanel";
import { AiPanel } from "@/components/admin/AiPanel";
import { PrayersPanel } from "@/components/admin/PrayersPanel";
import { RolesPanel } from "@/components/admin/RolesPanel";
import { AdCreativeExtras } from "@/components/AdCreativeExtras";
import { AdPreview, ReelsAdPreview } from "@/components/AdPreview";
import { LegalPanel } from "@/components/admin/LegalPanel";
import { StoragePanel } from "@/components/admin/StoragePanel";
import { ShareLinksPanel } from "@/components/admin/ShareLinksPanel";
import { SignupsPanel } from "@/components/admin/SignupsPanel";
import { InstallsByCountryPanel } from "@/components/admin/InstallsByCountryPanel";
import { ActiveUsersPanel } from "@/components/admin/ActiveUsersPanel";
import { UsersHubTabs } from "@/components/admin/UsersHubTabs";
import { UsersSummaryPanel } from "@/components/admin/UsersSummaryPanel";
import { FaqVotesPanel } from "@/components/admin/FaqVotesPanel";
import { ResourcesPanel } from "@/components/admin/ResourcesPanel";
import { MusicPanel } from "@/components/admin/MusicPanel";
import { UserIdentifiersEditor } from "@/components/admin/UserIdentifiersEditor";
import { FollowLockCard, followLockActive } from "@/components/admin/FollowLockCard";
import { VerifiedSubscriptionsAdmin, AdCreditsAdmin, RevenueLedgerAdmin } from "@/components/admin/Finance152Panels";
import { WithdrawalsAdmin, BeneficiariesAdmin, KycChip, identityOf } from "@/components/admin/WalletAdminPanels";
import { UserPrivilegesCard } from "@/components/admin/UserPrivilegesCard";
import { UserAgeAssuranceCard } from "@/components/admin/UserAgeAssuranceCard";
import { IdentityAdminPanel } from "@/components/admin/IdentityAdminPanel";
import { FinanceResetCard } from "@/components/admin/FinanceResetCard";
import { LaunchReportCard, WeeklyReportCard } from "@/components/admin/FinanceReportsCards";
import { RevenueByChannel } from "@/components/admin/RevenueByChannel";
import { SilentFavoritesCard } from "@/components/admin/SilentFavoritesCard";
import { RemindersCard } from "@/components/admin/RemindersCard";
import { AdmobDiagnosticsCard } from "@/components/admin/AdmobDiagnosticsCard";
import { ModerationMessageContext } from "@/components/admin/ModerationMessageContext";
import { ModerationStoryPreview, ModerationReporter, ModerationReason, ModerationBlockBanner } from "@/components/admin/ModerationStoryPreview";
import { ModerationMedia } from "@/components/admin/ModerationMediaViewer";
import { ModerationDecision } from "@/components/admin/ModerationDecision";
import { ChatAuditPanel } from "@/components/admin/ChatAuditPanel";
import { IncidentsPanel, IncidentPreview } from "@/components/admin/IncidentsPanel";
import { MarketplacePromosPanel } from "@/components/admin/MarketplacePromosPanel"; // lot 698 — Promos Marketplace
import { MonetizationPanel } from "@/components/admin/MonetizationPanel"; // lot 705 — Monétisation (parrainage & points, leviers futurs)
import { AcquiredRightsPanel } from "@/components/admin/AcquiredRightsPanel"; // lot 708 — Droits acquis (anciens membres protégés)
import { AssociatesPanel } from "@/components/admin/AssociatesPanel"; // lot 710 — Finances › Associés
import { MyAssociateShareCard } from "@/components/admin/MyAssociateShareCard"; // lot 710 — part de l'associé connecté (repli du lot 758)
import { MyAssociatePanel } from "@/components/admin/MyAssociatePanel"; // lot 758 — Finances › Associés : SA carte complète
import { PublicDeletionsCard } from "@/components/admin/PublicDeletionsCard"; // lot 710 — historique des suppressions (formulaire public)
import { SecureDocImage } from "@/components/kyc/SecureDocImage";
import { kycDocRef } from "@/lib/kyc";
import { AdminSkeleton } from "@/components/FeedSkeleton";
import { denominationLabel } from "@/lib/denominations";

const ADMIN_ROLES = ["super_admin", "manager", "moderator", "associate", "support"];
// Non-super admin roles whose capabilities are configured in the role matrix.
const CONFIGURABLE_ADMIN_ROLES = ["manager", "moderator", "associate", "support"];
const ALL_ADMIN_CAPS = ["dashboard", "users", "kyc", "moderation", "tickets", "churches", "church_linking", "finance", "notifications", "ai"];
// Labels for the "Voir en tant que" switcher pseudo-roles.
const VIEW_LABELS = { super_admin: "Super admin (tout)", all: "Tout (combiné)" };
// Console title shown in the header, adapted to the role currently being viewed.
const CONSOLE_TITLE_BY_VIEW = {
  super_admin: "Console admin", all: "Console admin",
  manager: "Console de gestion", moderator: "Console de modération",
  associate: "Tableau de bord", support: "Console de support",
};

const ROLE_LABELS = {
  super_admin: "Super admin", manager: "Manager", moderator: "Modérateur", associate: "Associé",
  support: "Support", preacher: "Prédicateur", artist: "Artiste", influencer: "Influenceur", user: "Membre",
};

// Roles an admin may assign, in display order. super_admin is exclusive (never combined).
const ASSIGNABLE_ROLES = ["super_admin", "manager", "moderator", "support", "associate", "preacher", "artist", "influencer", "user"];
const MAX_ROLES = 3;

// Normalized role list for a user (backward compatible: falls back to [role]).
const rolesOf = (u) => (Array.isArray(u?.roles) && u.roles.length ? u.roles : (u?.role ? [u.role] : []));
const roleSummary = (roles) => {
  if (!roles?.length) return "—";
  const [first, ...rest] = roles;
  return (ROLE_LABELS[first] || first) + (rest.length ? ` +${rest.length}` : "");
};

// Multi-role picker (up to 3, super_admin exclusive). Commits the selected list.
const RoleMultiSelect = ({ value, onCommit, testId }) => {
  const current = Array.isArray(value) && value.length ? value : [];
  const [open, setOpen] = useState(false);
  const [sel, setSel] = useState(current);
  useEffect(() => { if (open) setSel(current); /* eslint-disable-next-line */ }, [open]);
  const isSuper = sel.includes("super_admin");
  const toggle = (r) => setSel((prev) => {
    if (r === "super_admin") return prev.includes("super_admin") ? [] : ["super_admin"];
    if (prev.includes(r)) return prev.filter((x) => x !== r);
    if (prev.includes("super_admin") || prev.length >= MAX_ROLES) return prev;
    return [...prev, r];
  });
  const dirty = JSON.stringify([...sel].sort()) !== JSON.stringify([...current].sort());
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button variant="outline" size="sm" className="h-8 w-40 justify-between rounded-full font-normal" data-testid={testId}>
          <span className="truncate">{roleSummary(current)}</span><ChevronDown size={14} className="opacity-60 shrink-0" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-64 p-2" align="start" data-testid={`${testId}-content`}>
        <div className="text-[11px] mb-1 px-1" style={{ color: "var(--yalola-text-muted)" }}>Jusqu'à {MAX_ROLES} rôles · super admin exclusif</div>
        <div className="space-y-0.5 max-h-64 overflow-y-auto">
          {ASSIGNABLE_ROLES.map((r) => {
            const checked = sel.includes(r);
            const disabled = !checked && r !== "super_admin" && (isSuper || sel.length >= MAX_ROLES);
            return (
              <label key={r} className={`flex items-center gap-2 px-2 py-1.5 rounded-lg text-sm ${disabled ? "opacity-40 cursor-not-allowed" : "cursor-pointer hover:bg-[var(--yalola-surface-alt)]"}`}>
                <Checkbox checked={checked} disabled={disabled} onCheckedChange={() => !disabled && toggle(r)} data-testid={`${testId}-opt-${r}`} />
                {ROLE_LABELS[r] || r}
              </label>
            );
          })}
        </div>
        <div className="flex justify-end gap-2 pt-2">
          <Button variant="ghost" size="sm" className="rounded-full" onClick={() => { setSel(current); setOpen(false); }}>Annuler</Button>
          <Button size="sm" className="pill-primary rounded-full" disabled={!dirty || !sel.length} onClick={() => { onCommit(sel); setOpen(false); }} data-testid={`${testId}-save`}>Enregistrer</Button>
        </div>
      </PopoverContent>
    </Popover>
  );
};

export const Admin = () => {
  const { user } = useApp();
  if (!user) return <Navigate to="/?tab=login" replace />;
  if (!rolesOf(user).some((r) => ADMIN_ROLES.includes(r))) return <Navigate to="/home" replace />;
  return (
    <AppShell showSidebars={false}>
      <AdminInner user={user} />
    </AppShell>
  );
};

const NavItemBtn = ({ n, active, onClick, testId, indent }) => {
  const Icon = n.icon;
  return (
    <button
      onClick={onClick}
      data-testid={testId}
      className={`flex items-center gap-2.5 px-3 py-2.5 rounded-xl text-sm font-medium whitespace-nowrap transition-colors shrink-0 ${indent ? "md:ml-2" : ""}`}
      style={active ? { background: "var(--yalola-primary)", color: "#fff" } : { color: "var(--yalola-text)" }}
      onMouseEnter={(e) => { if (!active) e.currentTarget.style.background = "var(--yalola-surface-alt)"; }}
      onMouseLeave={(e) => { if (!active) e.currentTarget.style.background = "transparent"; }}
    >
      <Icon size={17} /> <span className="flex-1 text-left">{n.label}</span>
      {n.badge > 0 && (
        <span
          className="ml-1.5 min-w-[18px] h-[18px] px-1 rounded-full grid place-items-center text-[10px] font-bold shrink-0"
          style={active ? { background: "#fff", color: "#DC2626" } : { background: "#DC2626", color: "#fff" }}
          data-testid={`admin-nav-badge-${n.key}`}
        >
          {n.badge > 99 ? "99+" : n.badge}
        </span>
      )}
    </button>
  );
};

const AdminInner = ({ user }) => {
  const [searchParams] = useSearchParams();
  // La section vit dans l'URL : /admin/<clé> (ou ?tab=<clé> pour les liens profonds existants) →
  // un rechargement / F5 rouvre EXACTEMENT la même section au lieu de « Vue d'ensemble ».
  const { tab: pathTab } = useParams();
  const navigate = useNavigate();
  const urlTab = pathTab || searchParams.get("tab") || "";
  const myRoles = rolesOf(user);
  const hasSuper = myRoles.includes("super_admin");
  const myAdminRoles = myRoles.filter((r) => ADMIN_ROLES.includes(r) && r !== "super_admin");

  // "Voir en tant que" — super admin previews any role; a combined-role admin
  // switches between their own roles (or the merged "Tout" view). The role matrix
  // (Gestion des rôles) stays the single source of truth for what each role can do;
  // this only changes what the console PRESENTS.
  const viewOptions = hasSuper
    ? ["super_admin", ...CONFIGURABLE_ADMIN_ROLES]
    : (myAdminRoles.length >= 2 ? [...myAdminRoles, "all"] : []);
  const defaultView = hasSuper ? "super_admin" : (myAdminRoles[0] || "all");
  const [viewRole, setViewRole] = useState(() => {
    const saved = localStorage.getItem("yalola.admin.viewRole");
    return saved && viewOptions.includes(saved) ? saved : defaultView;
  });
  const view = viewOptions.includes(viewRole) ? viewRole : defaultView;
  useEffect(() => { localStorage.setItem("yalola.admin.viewRole", view); }, [view]);

  const [roleMaps, setRoleMaps] = useState(null);
  const [hiddenSections, setHiddenSections] = useState(null); // lot 749 — sections masquées par rôle (Paramètres › Privilèges)
  useEffect(() => {
    api.get("/admin/role-permissions")
      .then(({ data }) => { setRoleMaps(data.roles || {}); setHiddenSections(data.hidden_sections || {}); })
      .catch(() => { setRoleMaps({}); setHiddenSections({}); });
  }, []);
  // Lot 750 — « Voir en tant que » depuis la carte Sections par rôle : `?viewAs=<rôle>` + brouillon des cases EN COURS
  // (sessionStorage) appliqué à la place des sections enregistrées tant que l'aperçu est ouvert ; Enregistrer dans la
  // carte applique aussitôt le réglage à la console (événement) et ferme l'aperçu.
  const [sectionsDraft, setSectionsDraft] = useState(() => (hasSuper ? readRoleSectionsDraft() : null));
  useEffect(() => {
    const onSaved = (e) => { setHiddenSections(e.detail?.hidden || {}); if (e.detail?.roles) setRoleMaps(e.detail.roles); setSectionsDraft(null); }; // lot 758 — permissions accordées au passage
    window.addEventListener(ROLE_SECTIONS_SAVED_EVENT, onSaved);
    return () => window.removeEventListener(ROLE_SECTIONS_SAVED_EVENT, onSaved);
  }, []);
  const viewAs = searchParams.get("viewAs");
  useEffect(() => {
    if (!viewAs) return;
    if (viewOptions.includes(viewAs)) { setViewRole(viewAs); if (hasSuper) setSectionsDraft(readRoleSectionsDraft()); }
    navigate(`/admin/${pathTab || "dashboard"}`, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [viewAs]);
  const quitSectionsPreview = () => { clearRoleSectionsDraft(); setSectionsDraft(null); setViewRole("super_admin"); };
  const backToSectionsCard = () => { setSectionsDraft(null); setViewRole("super_admin"); navigate("/admin/roles?focus=settings-role-sections"); }; // lot 758 — la carte vit dans Gestion des rôles
  const effHidden = sectionsDraft ? sectionsDraft.hidden : hiddenSections;
  const myCardOn = useGuard("associate_my_card"); // lot 758
  const hubOn = useGuard("users_hub_tabs"); // lot 765 — Par pays / Par jour / Connectés en onglets sous l'en-tête Utilisateurs
  const activeTabOn = useGuard("inactive_accounts"); // lot 765 — onglet « Connectés »

  // Capability map for the CURRENT view (super = manage-all; "all" = real union; a
  // specific role = exactly what the matrix grants that role).
  const effIsSuper = view === "super_admin";
  const baseLevels = effIsSuper
    ? Object.fromEntries(ALL_ADMIN_CAPS.map((c) => [c, "manage"]))
    : (view === "all" ? (user.permission_levels || {}) : ((roleMaps && roleMaps[view]) || user.permission_levels || {}));
  // Lot 758 — aperçu « Voir en tant que » : les permissions que la carte accordera à l'enregistrement comptent déjà.
  const effLevels = sectionsDraft?.grants?.[view] ? levelsWithGrants(baseLevels, sectionsDraft.grants[view]) : baseLevels;
  const effPerms = Object.keys(effLevels);
  const isSupportView = view === "support";

  const [tab, setTabState] = useState(urlTab || (defaultView === "support" ? "tickets" : "dashboard"));
  useEffect(() => { if (urlTab) setTabState(urlTab); }, [urlTab]);
  // Clic dans le menu : l'URL devient /admin/<clé> (historique navigateur = sections visitées).
  const setTab = (k) => { setTabState(k); if (k !== urlTab) navigate(`/admin/${k}`); };
  // A capability can be "view" (read-only) or "manage" (read + write). Super view manages all.
  const canManage = (mod) => effIsSuper || effLevels[mod] === "manage";
  const canSee = (it) => effIsSuper || (it.superOnly ? false : (
    (!it.perm || (effPerms.includes(it.perm) && (!it.needsManage || effLevels[it.perm] === "manage")))
    && (!it.broad || view === "all")
    && !(view !== "all" && (effHidden ? (effHidden[view] || []) : (DEFAULT_HIDDEN_SECTIONS[view] || [])).includes(it.key)) // lot 749 — réglable rôle par rôle · lot 750 — brouillon de l'aperçu
  ));

  // Lot 626 — compteurs « à traiter » (KYC, modération, églises, retraits, tickets, pubs) → pastilles rouges
  // sur les onglets et leurs groupes, rafraîchis toutes les 45 s et à chaque changement d'onglet.
  const [pending, setPending] = useState({});
  const modPending = pending.moderation || 0;
  useEffect(() => {
    let alive = true;
    const fetchPending = () => api.get("/admin/pending-counts")
      .then(({ data }) => { if (alive) setPending(data || {}); })
      .catch(() => {});
    fetchPending();
    const iv = setInterval(fetchPending, 45000);
    return () => { alive = false; clearInterval(iv); };
  }, [tab]);

  // Lot 708 — menu regroupé par nature : Utilisateurs · Communauté · Contenu · Modération & Support · Monétisation ·
  // Finances · Communication · Configuration · Supervision (réorganisation seule : aucun écran modifié).
  const rawGroups = [
    {
      key: "g-users", label: "Utilisateurs",
      items: [
        { key: "users", label: "Utilisateurs", icon: Users, perm: "users" },
        // lot 766 — « Résumés » : stats des membres en un clin d'œil (top pays / villes, nouveaux pays, installations → inscriptions)
        { key: "summary", label: "Résumés", icon: BarChart3, perm: "users", hub: hubOn ? "users" : undefined },
        { key: "kyc", label: "Vérifications KYC", icon: IdCard, perm: "kyc", badge: pending.kyc },
        // lot 765 — `hub: "users"` : accessible (droits, URL /admin/<clé>) mais rangé en onglet sous l'en-tête Utilisateurs, pas dans le menu
        { key: "installs", label: "Installations par pays", icon: Globe, perm: "users", hub: hubOn ? "users" : undefined }, // lot 751
        { key: "roles", label: "Gestion des rôles", icon: ShieldCheck, superOnly: true },
        { key: "signups", label: "Inscriptions par jour", icon: UserPlus, superOnly: true, hub: hubOn ? "users" : undefined },
        ...(activeTabOn ? [{ key: "active", label: "Connectés", icon: UserCheck, perm: "users", hub: hubOn ? "users" : undefined }] : []),
      ],
    },
    {
      key: "g-community", label: "Communauté",
      items: [
        { key: "churches", label: "Églises", icon: Church, perm: "churches", badge: pending.churches },
        { key: "denominations", label: "Obédiences", icon: ListChecks, perm: "churches", broad: true },
        { key: "interests", label: "Points en commun", icon: HeartHandshake, perm: "churches", broad: true },
      ],
    },
    {
      key: "g-content", label: "Contenu",
      items: [
        { key: "contents", label: "Contenus", icon: Newspaper, perm: "moderation", broad: true },
        { key: "resources", label: "Ressources (articles)", icon: BookOpen, perm: "resources", needsManage: true },
        { key: "live", label: "Directs (Live)", icon: Radio, superOnly: true },
        { key: "prayers", label: "Prières", icon: HeartHandshake, superOnly: true },
        { key: "verses", label: "Versets du jour", icon: BookOpen, perm: "churches", broad: true },
        { key: "reading", label: "Plans de lecture", icon: BookOpen, superOnly: true },
        { key: "music", label: "Musique", icon: Music, superOnly: true },
      ],
    },
    {
      key: "g-moderation", label: "Modération & Support",
      items: [
        { key: "triage", label: "À traiter", icon: Inbox, perm: "moderation", badge: modPending },
        { key: "moderation", label: "Modération", icon: ShieldAlert, perm: "moderation", badge: modPending, noGroupCount: true },
        { key: "tickets", label: "Tickets support", icon: LifeBuoy, perm: "tickets", badge: pending.tickets },
        { key: "evidence", label: "Quarantaine médias", icon: ShieldAlert, superOnly: true },
        { key: "chats", label: "Messagerie (audit)", icon: MessagesSquare, superOnly: true },
        { key: "faq", label: "FAQ — retours", icon: HelpCircle, superOnly: true },
      ],
    },
    {
      // Lot 705 — leviers de monétisation : parrainage & points, publicités, promos Marketplace (d'autres à venir)
      key: "g-monetization", label: "Monétisation",
      items: [
        { key: "monetization", label: "Parrainage & points", icon: Coins, perm: "finance" },
        { key: "ads", label: "Publicités", icon: Megaphone, perm: "finance", needsManage: true, badge: pending.ads },
        { key: "promos", label: "Promos Marketplace", icon: Percent, perm: "moderation" },
      ],
    },
    {
      key: "g-finance", label: "Finances",
      items: (view === "associate" || view === "manager")
        ? [
            { key: "accounting", label: "Comptabilité", icon: Wallet, perm: "finance" },
            // Lot 758 — l'associé voit SA carte (lecture seule) dans Finances › Associés ; garde-fou `associate_my_card`
            ...(view === "associate" && myCardOn ? [{ key: "associates", label: "Associés", icon: Handshake, perm: "finance" }] : []),
          ]
        : [
            { key: "donations", label: "Dons · Vérifié · Crédits pub", icon: Wallet, perm: "finance", badge: pending.withdrawals },
            { key: "transactions", label: "Comptabilité", icon: Receipt, perm: "finance" },
            // super admin : tous les associés ; vue « Tout » d'un admin multi-rôles qui est aussi associé : sa carte
            ...(effIsSuper || !(myCardOn && view === "all" && myRoles.includes("associate"))
              ? [{ key: "associates", label: "Associés", icon: Handshake, superOnly: true }] // lot 710
              : [{ key: "associates", label: "Associés", icon: Handshake, perm: "finance" }]),
          ],
    },
    {
      key: "g-communication", label: "Communication",
      items: [
        { key: "notifications", label: "Notifications", icon: Bell, perm: "notifications" },
        { key: "emails", label: "E-mails", icon: Mail, superOnly: true },
      ],
    },
    {
      key: "g-config", label: "Configuration",
      items: [
        { key: "settings", label: "Paramètres", icon: Settings, superOnly: true },
        { key: "acquired", label: "Droits acquis", icon: History, superOnly: true },
        { key: "integrations", label: "Intégrations / API", icon: KeyRound, superOnly: true },
        { key: "legal", label: "Documents légaux", icon: FileText, superOnly: true },
        { key: "ai", label: "Assistant IA", icon: Bot, perm: "ai" },
        { key: "layout", label: "Mise en page", icon: Layers, superOnly: true },
        { key: "i18n", label: "Traductions", icon: Languages, superOnly: true },
        { key: "algorithms", label: "Algorithmes", icon: Activity, superOnly: true },
      ],
    },
    ...(effIsSuper ? [{
      key: "g-supervision", label: "Supervision",
      items: [
        { key: "storage", label: "Stockage vidéo", icon: HardDrive },
        { key: "sharelinks", label: "Liens de partage", icon: Link2 },
        { key: "audit", label: "Journal d'audit", icon: Sparkles },
        { key: "incidents", label: "Historique des incidents", icon: AlertTriangle },
      ],
    }] : []),
  ];
  // Hide tabs the current role is not allowed to see, then drop empty groups.
  const navGroups = rawGroups
    .map((g) => ({ ...g, items: g.items.filter(canSee) }))
    .filter((g) => g.items.length > 0);
  const dashItem = { key: "dashboard", label: "Vue d'ensemble", icon: Activity, perm: "dashboard" };
  // Support is a tickets-only console (like Modérateur → Modération): never the overview.
  const showDash = canSee(dashItem) && !isSupportView;
  const allNav = [...(showDash ? [dashItem] : []), ...navGroups.flatMap((g) => g.items)];
  const visibleKeys = new Set(allNav.map((n) => n.key));
  const activeTab = visibleKeys.has(tab) ? tab : (allNav[0]?.key || "dashboard");

  const [openGroups, setOpenGroups] = useState(() => {
    const initial = urlTab || "dashboard";
    const g = navGroups.find((gr) => gr.items.some((i) => i.key === initial));
    return g ? { [g.key]: true } : {};
  });
  const toggleGroup = (k) => setOpenGroups((o) => ({ ...o, [k]: !o[k] }));
  // Changement de section : révéler son groupe et repartir du haut de la page. Changer seulement
  // « Voir en tant que » ne fait PAS sauter la page (on reste sur la même section, à la même hauteur).
  useEffect(() => {
    const g = navGroups.find((gr) => gr.items.some((i) => i.key === activeTab));
    if (g && !openGroups[g.key]) setOpenGroups((o) => ({ ...o, [g.key]: true }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [view, activeTab]);
  useEffect(() => { window.scrollTo(0, 0); }, [activeTab]);
  // Les menus / dialogues Radix verrouillent le body (overflow hidden) : la barre latérale « sticky » sauterait
  // hors écran dès que la page est défilée. Cette classe neutralise ce changement d'overflow sur la console (voir index.css).
  useEffect(() => {
    document.body.classList.add("admin-sticky-safe");
    return () => document.body.classList.remove("admin-sticky-safe");
  }, []);

  const panels = {
    dashboard: <DashboardPanel isSuper={effIsSuper} view={view} onOpen={setTab} visibleTabs={visibleKeys} />,
    triage: <ModerationTriagePanel canManage={canManage("moderation")} />,
    contents: <ContentsPanel canManage={canManage("moderation")} />,
    tickets: <TicketsPanel canManage={canManage("tickets")} />,
    ads: <AdsPanel canManage={canManage("finance")} />,
    notifications: <NotificationsPanel canManage={canManage("notifications")} isSuper={effIsSuper} />,
    settings: <SettingsPanel />,
    integrations: <IntegrationsPanel />,
    emails: <EmailsPanel isSuper={effIsSuper} />,
    ai: <AiPanel isSuper={effIsSuper} canManage={canManage("ai")} />,
    layout: <LayoutPanel isSuper={effIsSuper} />,
    i18n: <TranslationsPanel isSuper={effIsSuper} />,
    users: <UsersPanel isSuper={effIsSuper} />,
    roles: <RolesPanel isSuper={effIsSuper} />,
    legal: <LegalPanel isSuper={effIsSuper} />,
    kyc: <KycPanel canManage={canManage("kyc")} pendingRoles={pending.kyc_roles || 0} pendingIdentity={pending.kyc_identity || 0} />,
    moderation: <ModerationPanel canManage={canManage("moderation")} isSuper={effIsSuper} />,
    live: <LivePanel canManage={canManage("moderation")} />,
    verses: <VersesPanel isSuper={effIsSuper} canManage={canManage("churches")} />,
    churches: <ChurchesPanel isSuper={effIsSuper} canManage={canManage("churches")} canLink={canManage("church_linking")} />,
    denominations: <DenominationsPanel isSuper={effIsSuper} canManage={canManage("churches")} />,
    reading: <ReadingPlansPanel isSuper={effIsSuper} />,
    music: <MusicPanel />,
    interests: <InterestsPanel isSuper={effIsSuper} canManage={canManage("churches")} />,
    prayers: <PrayersPanel isSuper={effIsSuper} />,
    donations: <DonationsPanel canManage={canManage("finance")} />,
    transactions: <TransactionsPanel isSuper={effIsSuper} expenseViewRoles={effIsSuper ? ["super_admin"] : (view === "all" ? myAdminRoles : [view])} />,
    accounting: <AccountingPanel isSuper={effIsSuper} expenseViewRoles={effIsSuper ? ["super_admin"] : (view === "all" ? myAdminRoles : [view])} showMyShare={!myCardOn} />,
    algorithms: <AlgorithmsPanel />,
    chats: <ChatAuditPanel />,
    evidence: <EvidencePanel />,
    storage: <StoragePanel />,
    sharelinks: <ShareLinksPanel />,
    signups: <SignupsPanel />,
    installs: <InstallsByCountryPanel />,
    active: <ActiveUsersPanel isSuper={effIsSuper} />, // lot 765
    summary: <UsersSummaryPanel canManage={effIsSuper || effLevels?.users === "manage"} />, // lot 766-767
    faq: <FaqVotesPanel isSuper={effIsSuper} />,
    resources: <ResourcesPanel />,
    audit: <AuditPanel />,
    incidents: <IncidentsPanel />,
    promos: <MarketplacePromosPanel canManage={canManage("moderation")} canEditRules={effIsSuper} />,
    monetization: <MonetizationPanel canEdit={effIsSuper} />,
    acquired: <AcquiredRightsPanel />,
    associates: effIsSuper ? <AssociatesPanel /> : <MyAssociatePanel />, // lot 758 — un associé ne voit que SA carte
  };

  // Map each actionable tab to the capability that governs write access.
  const TAB_MODULE = {
    kyc: "kyc", moderation: "moderation", contents: "moderation", tickets: "tickets",
    live: "moderation", triage: "moderation", promos: "moderation", monetization: "finance",
    churches: "churches", denominations: "churches", interests: "churches", verses: "churches",
    ads: "finance", donations: "finance", notifications: "notifications", ai: "ai",
  };
  const activeModule = TAB_MODULE[activeTab];
  const activeReadOnly = !!activeModule && !canManage(activeModule);

  const current = allNav.find((n) => n.key === activeTab) || dashItem;
  // lot 765 — onglet d'un « hub » (Par pays / Par jour / Connectés) : l'en-tête et le menu montrent le parent (Utilisateurs)
  const hubParent = current.hub ? allNav.find((n) => n.key === current.hub) : null;
  const headerItem = hubParent || current;
  const navActiveKey = hubParent ? hubParent.key : activeTab;
  const hubTabs = (headerItem.key === "users" && hubOn) ? allNav.filter((n) => n.key === "users" || n.hub === "users") : [];
  const CurrentIcon = headerItem.icon;

  return (
    <div className="grid grid-cols-1 md:grid-cols-[248px_1fr] gap-4 min-w-0" data-testid="admin-page">
      {/* Sticky on desktop; when many groups are expanded the menu scrolls inside the
          viewport instead of pushing its bottom items out of reach. */}
      <aside className="md:sticky md:top-20 h-max md:max-h-[calc(100vh-6rem)] md:overflow-y-auto hide-scrollbar space-y-3 min-w-0" data-testid="admin-sidebar">
        <div className="yalola-card p-4">
          <div className="flex items-center gap-2.5">
            <div className="h-10 w-10 rounded-xl grid place-items-center" style={{ background: "var(--yalola-primary-soft)", color: "var(--yalola-primary)" }}>
              <LayoutDashboard size={20} />
            </div>
            <div className="min-w-0">
              <div className="font-bold font-display leading-tight truncate" data-testid="admin-console-title">{CONSOLE_TITLE_BY_VIEW[view] || "Console admin"}</div>
              <div className="text-[11px] leading-tight" style={{ color: "var(--yalola-text-muted)" }} data-testid="admin-header-roles">{rolesOf(user).map((r) => ROLE_LABELS[r] || r).join(" · ")}</div>
            </div>
          </div>
        </div>
        {viewOptions.length > 1 && (
          <div className="yalola-card p-3" data-testid="admin-view-switcher-card">
            <div className="text-[11px] font-semibold mb-1.5 flex items-center gap-1.5" style={{ color: "var(--yalola-text-muted)" }}>
              <Eye size={13} /> Voir en tant que
            </div>
            {/* <select> natif : le menu Radix verrouille le défilement du body (overflow hidden), ce qui casse la barre
                latérale « sticky » quand la page est défilée → saut visuel et liste hors écran. */}
            <div className="relative">
              <select value={view} onChange={(e) => setViewRole(e.target.value)} aria-label="Voir en tant que"
                className="h-9 w-full appearance-none rounded-lg border bg-white pl-3 pr-9 text-sm font-medium outline-none focus:ring-2"
                style={{ borderColor: "var(--yalola-border)", color: "var(--yalola-text)" }} data-testid="admin-view-switcher">
                {viewOptions.map((r) => (
                  <option key={r} value={r} data-testid={`admin-view-opt-${r}`}>{VIEW_LABELS[r] || ROLE_LABELS[r] || r}</option>
                ))}
              </select>
              <ChevronDown size={16} className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2" style={{ color: "var(--yalola-text-muted)" }} />
            </div>
            {hasSuper && view !== "super_admin" && (
              <div className="text-[10px] mt-1.5 leading-snug" style={{ color: "var(--yalola-text-muted)" }} data-testid="admin-view-preview-note">Aperçu du rôle · tes droits super admin restent actifs.</div>
            )}
          </div>
        )}
        <nav className="yalola-card p-2 hidden md:flex md:flex-col gap-0.5" data-testid="admin-nav">
          {showDash && <NavItemBtn n={dashItem} active={activeTab === "dashboard"} onClick={() => setTab("dashboard")} testId="admin-nav-dashboard" />}
          {navGroups.map((g) => {
            const open = !!openGroups[g.key];
            const hasActive = g.items.some((i) => i.key === activeTab);
            const shown = g.items.filter((i) => !i.hub); // lot 765 — les onglets d'un hub ne sont pas listés dans le menu
            const groupBadge = g.items.reduce((s, i) => s + (i.noGroupCount ? 0 : (i.badge || 0)), 0); // « À traiter » et « Modération » partagent le même compteur
            return (
              <div key={g.key} className="mt-1">
                <button
                  onClick={() => toggleGroup(g.key)}
                  data-testid={`admin-group-${g.key}`}
                  className="w-full flex items-center justify-between px-3 py-2 rounded-lg text-[11px] font-bold uppercase tracking-wider transition-colors hover:bg-[color:var(--yalola-surface-alt)]"
                  style={{ color: hasActive ? "var(--yalola-primary)" : "var(--yalola-text-muted)" }}
                >
                  <span>{g.label}</span>
                  <span className="flex items-center gap-1.5">
                    {!open && groupBadge > 0 && (
                      <span className="min-w-[18px] h-[18px] px-1 rounded-full grid place-items-center text-[10px] font-bold text-white" style={{ background: "#DC2626" }} data-testid={`admin-group-badge-${g.key}`}>{groupBadge > 99 ? "99+" : groupBadge}</span>
                    )}
                    {open ? <Minus size={14} /> : <Plus size={14} />}
                  </span>
                </button>
                {open && (
                  <div className="flex flex-col gap-0.5 mt-0.5">
                    {shown.map((n) => (
                      <NavItemBtn key={n.key} n={n} active={navActiveKey === n.key} onClick={() => setTab(n.key)} testId={`admin-nav-${n.key}`} indent />
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </nav>
        <nav className="yalola-card p-2 flex md:hidden gap-1 overflow-x-auto hide-scrollbar" data-testid="admin-nav-mobile">
          {allNav.filter((n) => !n.hub).map((n) => (
            <NavItemBtn key={n.key} n={n} active={navActiveKey === n.key} onClick={() => setTab(n.key)} testId={`admin-nav-m-${n.key}`} />
          ))}
        </nav>
      </aside>

      <div className="min-w-0">
        <div className="yalola-card p-5 mb-4">
          <div className="flex items-center gap-2 flex-wrap">
            <CurrentIcon size={20} style={{ color: "var(--yalola-primary)" }} />
            <h1 className="text-2xl font-bold font-display">{headerItem.label}</h1>
            <RoleLevelBadges
              role={effIsSuper ? "super_admin" : (view === "all" ? (myAdminRoles[0] || myRoles[0]) : view)}
              isSuper={effIsSuper}
              level={activeModule ? (effIsSuper ? "manage" : (effLevels[activeModule] || "none")) : null}
            />
          </div>
          {hubTabs.length > 1 ? (
            <UsersHubTabs items={hubTabs} active={activeTab} onPick={setTab} />
          ) : (
            <p className="text-sm mt-1" style={{ color: "var(--yalola-text-muted)" }}>
              {isSupportView
                ? `Bonjour ${user.name}. Voici les tickets de support à traiter.`
                : view === "associate"
                  ? `Bienvenue ${user.name}. Ta console d'associé : la comptabilité de la plateforme et ta carte d'associé, en consultation.`
                  : `Bienvenue ${user.name}. Gère la plateforme Yalola depuis cette console.`}
            </p>
          )}
        </div>
        {sectionsDraft && hasSuper && (
          <div className="yalola-card p-3 mb-4 flex flex-wrap items-center gap-2 text-sm" style={{ borderColor: "var(--yalola-primary)" }} data-testid="admin-sections-preview-banner">
            <Eye size={16} style={{ color: "var(--yalola-primary)" }} />
            <span className="flex-1 min-w-[12rem]">
              <b>Aperçu des sections</b> — cases en cours, pas encore enregistrées{sectionsDraft.role ? ` · ${ROLE_LABELS[sectionsDraft.role] || sectionsDraft.role}` : ""}.
            </span>
            <Button size="sm" variant="outline" className="rounded-full" onClick={backToSectionsCard} data-testid="admin-sections-preview-back">Revenir aux réglages</Button>
            <Button size="sm" variant="ghost" className="rounded-full" onClick={quitSectionsPreview} data-testid="admin-sections-preview-quit">Quitter l'aperçu</Button>
          </div>
        )}
        {isSupportView && <SupportWelcomeBanner name={user.name} onOpenTickets={() => setTab("tickets")} />}
        {activeReadOnly && <ReadOnlyBanner />}
        {panels[activeTab]}
      </div>
    </div>
  );
};

// ---------------- Panels ----------------
const RoleLevelBadges = ({ role, isSuper, level }) => (
  <div className="flex items-center gap-1.5 flex-wrap" data-testid="admin-role-badges">
    <span className="chip text-[11px]" data-testid="admin-role-chip"
      style={{ background: "var(--yalola-primary-soft)", color: "var(--yalola-primary)" }}>
      {ROLE_LABELS[role] || role}
    </span>
    {level && (
      <span className="chip text-[11px]" data-testid="admin-level-chip"
        style={level === "manage" ? { background: "#D1FAE5", color: "#065F46" } : { background: "#FEF3C7", color: "#92400E" }}>
        {level === "manage" ? "Gérer" : "Voir"}
      </span>
    )}
  </div>
);

const ReadOnlyBanner = () => (
  <div className="yalola-card p-4 mb-4 flex items-center gap-3" data-testid="admin-readonly-banner"
    style={{ background: "#FEF3C7", borderColor: "#F59E0B" }}>
    <div className="h-9 w-9 rounded-full grid place-items-center shrink-0" style={{ background: "#F59E0B22" }}>
      <Lock size={17} style={{ color: "#92400E" }} />
    </div>
    <div>
      <div className="font-semibold text-sm" style={{ color: "#92400E" }}>Accès en lecture seule</div>
      <p className="text-xs" style={{ color: "#92400E" }}>Votre rôle vous autorise à consulter cette section, mais pas à la modifier. Les actions sont désactivées.</p>
    </div>
  </div>
);

const SupportWelcomeBanner = ({ name, onOpenTickets }) => {
  const [open, setOpen] = useState(null);
  useEffect(() => {
    api.get("/admin/tickets/stats")
      .then(({ data }) => setOpen(data.open_count ?? 0))
      .catch(() => setOpen(null));
  }, []);
  return (
    <div className="yalola-card p-5 mb-4" data-testid="support-welcome" style={{ background: "var(--yalola-primary-soft)" }}>
      <div className="flex items-center gap-3 flex-wrap">
        <div className="h-12 w-12 rounded-full grid place-items-center shrink-0" style={{ background: "var(--yalola-primary)" }}>
          <LifeBuoy size={24} color="#fff" />
        </div>
        <div className="flex-1 min-w-[180px]">
          <div className="font-bold font-display text-lg" style={{ color: "var(--yalola-primary)" }} data-testid="support-open-count">
            {open === null ? "Tickets de support" : open === 0 ? "Aucun ticket ouvert 🎉" : `${open} ticket${open > 1 ? "s" : ""} en attente`}
          </div>
          <p className="text-xs" style={{ color: "var(--yalola-text-muted)" }}>Ton rôle Support te donne accès aux tickets de support.</p>
        </div>
        <Button className="pill pill-primary" onClick={onOpenTickets} data-testid="support-open-tickets">Voir les tickets</Button>
      </div>
    </div>
  );
};

const StatCard = ({ label, value, hint, color }) => (
  <div className="yalola-card p-5">
    <div className="text-xs uppercase font-semibold" style={{ color: "var(--yalola-text-muted)" }}>{label}</div>
    <div className="text-3xl font-bold mt-1" style={{ fontFamily: "Outfit", color: color || "var(--yalola-text)" }}>{value}</div>
    {hint && <div className="text-xs mt-1" style={{ color: "var(--yalola-text-muted)" }}>{hint}</div>}
  </div>
);


const HealthMetric = ({ icon: Icon, label, value, hint, tone }) => {
  const toneColor = { good: "#2D5A4C", warn: "#E0A63C", bad: "#D64545", idle: "var(--yalola-text-muted)" }[tone] || "var(--yalola-text)";
  return (
    <div className="flex items-start gap-3 rounded-xl p-3" style={{ background: "var(--yalola-bg)" }}>
      <span className="h-9 w-9 shrink-0 rounded-lg flex items-center justify-center" style={{ background: `${toneColor}1a`, color: toneColor }}>
        <Icon size={18} />
      </span>
      <div className="min-w-0">
        <div className="text-[11px] uppercase font-semibold tracking-wide" style={{ color: "var(--yalola-text-muted)" }}>{label}</div>
        <div className="text-lg font-bold leading-tight" style={{ fontFamily: "Outfit", color: toneColor }}>{value}</div>
        {hint && <div className="text-[11px] truncate" style={{ color: "var(--yalola-text-muted)" }}>{hint}</div>}
      </div>
    </div>
  );
};

const HealthWidget = () => {
  const [h, setH] = useState(null);
  const [history, setHistory] = useState([]);
  const [incidents, setIncidents] = useState([]);
  const [incidentsTotal, setIncidentsTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const load = () => {
    setLoading(true);
    Promise.all([
      api.get("/admin/health").then(({ data }) => setH(data)).catch(() => setH(null)),
      api.get("/admin/health/history?hours=24").then(({ data }) => setHistory(data.samples || [])).catch(() => setHistory([])),
      api.get("/admin/health/incidents?limit=3").then(({ data }) => { setIncidents(data.incidents || []); setIncidentsTotal(data.total || 0); }).catch(() => setIncidents([])),
    ]).finally(() => setLoading(false));
  };
  useEffect(() => {
    load();
    const iv = setInterval(load, 15000); // live refresh every 15s
    return () => clearInterval(iv);
  }, []);
  const scaleOn = useGuard("health_latency_scale"); // lot 760
  const lat = h?.db_latency_ms;
  const latTone = lat == null ? "idle" : lat < 80 ? "good" : lat <= 200 ? "warn" : "bad";
  const redis = h?.redis || {};
  const cache = redis.cache || {};
  const rPing = redis.ping_ms;
  const redisTone = redis.breaker_open ? "bad"
    : !redis.configured ? "idle"
    : !redis.connected ? "bad"
    : redis.cache_bypassed ? "warn"
    : rPing == null ? "good"
    : rPing > 500 ? "bad" : rPing > 150 ? "warn" : "good";
  const conn = h?.connections;
  const avail = conn?.available;
  const connTone = !conn ? "idle" : (avail != null && avail < 40) ? "bad" : (avail != null && avail < 120) ? "warn" : "good";
  const modAi = h?.moderation_ai || {};
  const modAiTone = modAi.down ? "bad" : "good";
  const isCrit = latTone === "bad" || connTone === "bad" || redisTone === "bad" || modAiTone === "bad";
  const isWarn = !isCrit && (latTone === "warn" || connTone === "warn" || redisTone === "warn");
  const alertMsgs = [];
  if (modAi.down) alertMsgs.push(`Modération IA en panne (${modAi.reason || "erreur"}) — les médias passent sans vérification IA`);
  if (latTone === "bad") alertMsgs.push(`Latence base élevée (${lat} ms)`);
  else if (latTone === "warn") alertMsgs.push(`Latence base à surveiller (${lat} ms)`);
  if (connTone === "bad") alertMsgs.push(`Connexions Atlas critiques (${avail} restantes)`);
  else if (connTone === "warn") alertMsgs.push(`Connexions Atlas en tension (${avail} restantes)`);
  if (redis.breaker_open) alertMsgs.push(`Cache Redis en panne — contourné temporairement (retour dans ${redis.breaker_seconds_left ?? "?"} s)`);
  else if (redis.cache_bypassed) alertMsgs.push(`Cache Redis lent (${rPing} ms) — contourné automatiquement, l'app sert depuis la base (aucun impact)`);
  else if (redisTone === "bad" && redis.configured) alertMsgs.push(redis.connected ? `Cache Redis lent (${rPing} ms)` : "Cache Redis injoignable");
  else if (redisTone === "warn") alertMsgs.push(`Cache Redis à surveiller (${rPing} ms)`);
  const chart = history.map((s) => ({
    t: (s.at || "").slice(11, 16),
    latence: s.db_latency_ms,
    cache: s.cache_hit_rate,
  }));
  // Lot 760 — échelle ancrée : l'axe part de 0 et monte au moins à 100 ms (zone normale < 80 ms visible), p95 / max des 24 h.
  const latStats = latencyStats(history.map((s) => s.db_latency_ms));
  const latDomainMax = latencyDomainMax(latStats);
  return (
    <div className="yalola-card p-5" data-testid="admin-health-widget">
      <div className="flex items-center justify-between flex-wrap gap-2 mb-3">
        <div className="flex items-center gap-2">
          <Server size={16} style={{ color: "var(--yalola-primary)" }} />
          <span className="text-xs uppercase font-semibold" style={{ color: "var(--yalola-text-muted)" }}>Santé système</span>
        </div>
        <button onClick={load} disabled={loading} data-testid="admin-health-refresh"
          className="text-[11px] flex items-center gap-1 rounded-full px-2.5 py-1 transition-colors hover:bg-black/5 disabled:opacity-50"
          style={{ color: "var(--yalola-text-muted)" }}>
          <RefreshCw size={12} className={loading ? "animate-spin" : ""} /> Actualiser
        </button>
      </div>

      {(isCrit || isWarn) && (
        <div data-testid="admin-health-alert"
          className="flex items-start gap-2 rounded-xl px-3 py-2.5 mb-3 text-sm font-medium"
          style={{
            background: isCrit ? "#D6454515" : "#E0A63C15",
            color: isCrit ? "#B83232" : "#9A7420",
            border: `1px solid ${isCrit ? "#D6454540" : "#E0A63C40"}`,
          }}>
          <AlertTriangle size={16} className="shrink-0 mt-0.5" />
          <div>
            <div className="font-bold">{isCrit ? "Alerte : ressources critiques" : "Attention : ressources sous tension"}</div>
            <div className="text-[12px] opacity-90">{alertMsgs.join(" · ")}{isCrit ? " — surveillez avant un grand direct." : ""}</div>
          </div>
        </div>
      )}

      {!h ? (
        <div className="text-sm py-4 text-center" style={{ color: "var(--yalola-text-muted)" }} data-testid="admin-health-loading">
          {loading ? "Vérification..." : "Indisponible"}
        </div>
      ) : (
        <>
          <div className="grid gap-3 grid-cols-1 sm:grid-cols-2 lg:grid-cols-4">
            <HealthMetric icon={Database} label="Latence base" tone={latTone}
              value={lat == null ? "—" : `${lat} ms`}
              hint={lat == null ? "ping échoué" : (scaleOn && latStats ? `p95 24 h ${latStats.p95} ms · max ${latStats.max} ms` : "Aller-retour MongoDB")} />
            <HealthMetric icon={Gauge} label="Cache Redis" tone={redisTone}
              value={redis.breaker_open ? "Contourné" : !redis.configured ? "Non configuré" : !redis.connected ? "Injoignable" : redis.cache_bypassed ? "Contourné (lent)" : rPing != null ? `${rPing} ms` : "Connecté"}
              hint={redis.breaker_open ? "Bascule auto sur la base" : redis.cache_bypassed ? `Ping ${rPing} ms — l'app sert depuis MongoDB` : redis.connected ? `${cache.hit_rate ?? 0}% hits · ${cache.hits ?? 0}/${(cache.hits ?? 0) + (cache.misses ?? 0)}${redis.used_memory_human ? ` · ${redis.used_memory_human}` : ""}` : "Actif en production"} />
            <HealthMetric icon={Activity} label="Connexions Atlas" tone={connTone}
              value={conn ? `${conn.current ?? "?"}` : "N/A"}
              hint={conn ? `${conn.available ?? "?"} dispo · pool ${h.pool?.max ?? "?"}` : "Non autorisé (Atlas)"} />
            <HealthMetric icon={ShieldAlert} label="Modération IA" tone={modAiTone}
              value={modAi.down ? "En panne" : "Opérationnelle"}
              hint={modAi.down ? `Échecs répétés (${modAi.reason || "erreur"})` : "Vérification des médias active"} />
          </div>

          {chart.length > 1 && (
            <div className="mt-4" data-testid="admin-health-history">
              <div className="flex items-center justify-between mb-1">
                <span className="text-[11px] uppercase font-semibold" style={{ color: "var(--yalola-text-muted)" }}>Tendance — 24 h</span>
                <div className="flex gap-3 text-[11px] flex-wrap" style={{ color: "var(--yalola-text-muted)" }}>
                  {scaleOn && <span className="flex items-center gap-1" data-testid="admin-health-normal-zone"><span className="h-2 w-3 inline-block rounded-sm" style={{ background: "#1B7A3D22", border: "1px solid #1B7A3D55" }} /> Zone normale &lt; 80 ms{latStats ? ` · p95 ${latStats.p95} ms` : ""}</span>}
                  <span className="flex items-center gap-1"><span className="h-2 w-2 rounded-full inline-block" style={{ background: "#D97757" }} /> Latence (ms)</span>
                  <span className="flex items-center gap-1"><span className="h-2 w-2 rounded-full inline-block" style={{ background: "#2D5A4C" }} /> Cache (%)</span>
                </div>
              </div>
              <div className="h-32" style={{ minHeight: 128 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={chart} margin={{ top: 5, right: 5, bottom: 0, left: scaleOn ? -12 : -26 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="var(--yalola-border)" vertical={false} />
                    <XAxis dataKey="t" tick={{ fontSize: 9 }} interval="preserveStartEnd" minTickGap={40} tickLine={false} axisLine={false} />
                    <YAxis yAxisId="lat" tick={{ fontSize: 9 }} tickLine={false} axisLine={false} width={40} domain={scaleOn ? [0, latDomainMax] : ["auto", "auto"]} />
                    <YAxis yAxisId="cache" orientation="right" domain={[0, 100]} tick={{ fontSize: 9 }} tickLine={false} axisLine={false} width={28} />
                    {scaleOn && <ReferenceArea yAxisId="lat" y1={0} y2={80} fill="#1B7A3D" fillOpacity={0.06} strokeOpacity={0} ifOverflow="hidden" />}
                    {scaleOn && latDomainMax >= 200 && <ReferenceLine yAxisId="lat" y={200} stroke="#D64545" strokeDasharray="4 3" ifOverflow="hidden" />}
                    <ChartTooltip contentStyle={{ borderRadius: 12, fontSize: 12 }} />
                    <Line yAxisId="lat" type="monotone" dataKey="latence" stroke="#D97757" strokeWidth={2} dot={false} isAnimationActive={false} />
                    <Line yAxisId="cache" type="monotone" dataKey="cache" stroke="#2D5A4C" strokeWidth={2} dot={false} isAnimationActive={false} />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </div>
          )}
        </>
      )}

      <IncidentPreview incidents={incidents} total={incidentsTotal} />
    </div>
  );
};

const AD_BILLING_MODES = [["free", "Gratuit / offert"], ["bank_transfer", "Virement bancaire"], ["cash", "Espèces"], ["paypal", "PayPal"], ["other", "Autre"]];
const AD_BILLING_LABEL = Object.fromEntries(AD_BILLING_MODES);
const PAID_WITH_LABEL = { stripe: "En ligne", ad_credit: "Crédits pub", comp: "Offerte" };
const adOfflineTotal = (a) => (a.offline_payments && a.offline_payments.length)
  ? a.offline_payments.reduce((s, p) => s + (Number(p.amount) || 0), 0)
  : (Number(a.manual_price) || 0);

const OFFLINE_AD_EMPTY = {
  title: "", body: "", media_url: "", media_type: "image", poster_url: "", advertiser_name: "", advertiser_email: "", as_church_id: "", manual_price: "", billing_mode: "", target_url: "", days: 30,
  cta_type: "none", cta_label: "learn_more", cta_value: "", cta_headline: "", cta_description: "",
  lead_fields: ["name", "phone"], custom_fields: [],
  t_genders: [], t_age_min: "", t_age_max: "", t_countries: "", t_cities: "", t_denominations: [], t_interests: [],
};

const DELIVERY_META = {
  live: { label: "En diffusion", emoji: "🟢", bg: "#E7F6EC", fg: "#1B7A3D", why: "Diffusée dans le fil et les reels." },
  paused: { label: "En pause", emoji: "⏸️", bg: "#EEF0F3", fg: "#475467", why: "Non diffusée — clique « Activer » pour la relancer." },
  expired: { label: "Expirée", emoji: "⌛", bg: "#FDF1E7", fg: "#9A5B12", why: "Durée écoulée — clique « Réactiver » pour la relancer." },
  unpaid: { label: "Paiement requis", emoji: "💳", bg: "#FEF3E2", fg: "#9A5B12", why: "En attente de paiement — non diffusée." },
  pending_review: { label: "À valider", emoji: "⏳", bg: "#FEF3E2", fg: "#9A5B12", why: "En attente de modération — approuve-la pour la diffuser." },
  rejected: { label: "Refusée", emoji: "🚫", bg: "#FDECEC", fg: "#B42318", why: "Refusée à la modération — non diffusée." },
  hidden_post: { label: "Publication masquée", emoji: "🙈", bg: "#FDECEC", fg: "#B42318", why: "La publication support est masquée — non diffusée." },
  archived: { label: "Corbeille", emoji: "🗑️", bg: "#EEF0F3", fg: "#475467", why: "Dans la corbeille — restaure-la pour la récupérer." },
};

const DeliveryBadge = ({ delivery, testId }) => {
  const status = delivery?.status || "paused";
  const m = DELIVERY_META[status] || DELIVERY_META.paused;
  return (
    <span className="chip text-[10px]" style={{ background: m.bg, color: m.fg }} title={m.why} data-testid={testId}>
      {m.emoji} {m.label}
    </span>
  );
};

const JOURNAL_META = {
  created: { label: "Créée", emoji: "✨" },
  activated: { label: "Activée", emoji: "▶️" },
  paused: { label: "Mise en pause", emoji: "⏸️" },
  edited: { label: "Modifiée", emoji: "✏️" },
  approved: { label: "Approuvée", emoji: "✅" },
  rejected: { label: "Refusée", emoji: "🚫" },
  renewed: { label: "Renouvelée / Réactivée", emoji: "🔄" },
  archived: { label: "Mise à la corbeille", emoji: "🗑️" },
  restored: { label: "Restaurée", emoji: "↩️" },
};

const AdJournal = ({ history }) => {
  const cur = useAppCurrency();
  const items = [...(history || [])].reverse();
  if (items.length === 0) return <div className="text-[11px]" style={{ color: "var(--yalola-text-muted)" }} data-testid="ad-journal-empty">Aucun évènement enregistré pour l'instant.</div>;
  return (
    <ol className="space-y-1.5">
      {items.map((h, i) => {
        const m = JOURNAL_META[h.action] || { label: h.action, emoji: "•" };
        const when = h.at ? new Date(h.at).toLocaleString("fr-FR", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }) : "";
        const extra = h.meta?.reason ? ` — ${h.meta.reason}` : (h.meta?.days ? ` — ${h.meta.days} j${h.meta.price != null ? ` · ${txFmt(h.meta.price, cur)}` : ""}` : "");
        return (
          <li key={i} className="flex items-start gap-2 text-[11px]" style={{ color: "var(--yalola-text-muted)" }} data-testid={`ad-journal-item-${i}`}>
            <span aria-hidden>{m.emoji}</span>
            <span><b style={{ color: "var(--yalola-text)" }}>{m.label}</b>{extra} · {h.actor_name || "—"} · {when}</span>
          </li>
        );
      })}
    </ol>
  );
};

const AdSparkline = ({ daily }) => {
  const data = (daily || []).slice(-14);
  if (!data.length) return null;
  const totalI = data.reduce((s, d) => s + (d.i || 0), 0);
  const totalC = data.reduce((s, d) => s + (d.c || 0), 0);
  if (totalI === 0 && totalC === 0) {
    return (
      <div className="mt-2" data-testid="ad-sparkline">
        <div className="text-[10px] font-semibold mb-1" style={{ color: "var(--yalola-text-muted)" }}>Performance · 14 j</div>
        <div className="text-[11px] py-1.5" style={{ color: "var(--yalola-text-muted)" }} data-testid="ad-sparkline-empty">Aucune donnée de diffusion pour l'instant.</div>
      </div>
    );
  }
  const maxI = Math.max(1, ...data.map((d) => d.i || 0));
  return (
    <div className="mt-2" data-testid="ad-sparkline">
      <div className="flex items-center justify-between mb-1">
        <span className="text-[10px] font-semibold" style={{ color: "var(--yalola-text-muted)" }}>Performance · 14 j</span>
        <span className="text-[10px] flex items-center gap-2" style={{ color: "var(--yalola-text-muted)" }}>
          <span className="inline-flex items-center gap-1"><span className="inline-block w-2 h-2 rounded-sm" style={{ background: "var(--yalola-primary)", opacity: 0.4 }} />{totalI} vues</span>
          <span className="inline-flex items-center gap-1"><span className="inline-block w-2 h-2 rounded-sm" style={{ background: "var(--yalola-secondary)" }} />{totalC} clics</span>
        </span>
      </div>
      <div className="flex items-end gap-[3px] h-12">
        {data.map((d, i) => {
          const hI = Math.max((d.i / maxI) * 100, (d.i > 0 ? 8 : 0));
          const hC = d.c > 0 ? Math.max((d.c / maxI) * 100, 6) : 0;
          return (
            <div key={i} className="relative flex-1 h-full" title={`${d.d} · ${d.i || 0} vues · ${d.c || 0} clics`} data-testid={`ad-spark-bar-${i}`}>
              <div className="absolute bottom-0 left-0 right-0 rounded-t-sm" style={{ height: `${hI}%`, background: "var(--yalola-primary)", opacity: 0.4 }} />
              {hC > 0 && <div className="absolute bottom-0 left-0 right-0 rounded-t-sm" style={{ height: `${hC}%`, background: "var(--yalola-secondary)" }} />}
            </div>
          );
        })}
      </div>
    </div>
  );
};

const AD_FILTER_OPTIONS = [
  { v: "all", label: "Toutes" },
  { v: "live", label: "🟢 En diffusion" },
  { v: "paused", label: "⏸️ En pause" },
  { v: "expired", label: "⌛ Expirée" },
  { v: "pending_review", label: "⏳ À valider" },
  { v: "unpaid", label: "💳 Paiement requis" },
  { v: "rejected", label: "🚫 Refusée" },
];

// Revenue dashboard: Yalola Direct (real, paid campaigns) vs Google (estimated
// from counted impressions × configurable eCPM) over the last 30 days.
const AdsRevenueDashboard = () => {
  const [data, setData] = useState(null);
  const [ecpm, setEcpm] = useState("");
  const [savingEcpm, setSavingEcpm] = useState(false);
  const load = () => api.get("/admin/ads/revenue-dashboard")
    .then(({ data }) => { setData(data); setEcpm(String(data.google?.ecpm ?? 1.5)); })
    .catch(() => {});
  useEffect(() => { load(); }, []);
  const saveEcpm = async () => {
    setSavingEcpm(true);
    try {
      await api.patch("/admin/settings", { google_ads_ecpm: Math.max(0, parseFloat(ecpm) || 0) });
      toast.success("eCPM enregistré");
      load();
    } catch { toast.error("Réservé au super admin"); }
    finally { setSavingEcpm(false); }
  };
  if (!data) return null;
  const cur = data.currency || "USD";
  const sym = cur === "EUR" ? "€" : cur === "USD" ? "$" : cur;
  const y = data.yalola || {};
  const g = data.google || {};
  return (
    <div className="yalola-card p-5" data-testid="ads-revenue-dashboard">
      <div className="font-bold font-display text-lg mb-1">Revenus publicitaires — 30 derniers jours</div>
      <p className="text-xs mb-3" style={{ color: "var(--yalola-text-muted)" }}>
        Yalola Direct = encaissements réels (campagnes payées en ligne + hors-ligne). Google = <b>estimation</b> à partir des annonces servies comptées par l'app × eCPM — le chiffre exact est dans vos comptes AdSense/AdMob.
      </p>
      <div className="grid sm:grid-cols-2 gap-3">
        <div className="rounded-xl p-4" style={{ background: "var(--yalola-primary-soft)" }} data-testid="ads-rev-yalola">
          <div className="text-xs font-bold uppercase tracking-wide mb-1" style={{ color: "var(--yalola-primary)" }}>Yalola Direct (réel)</div>
          <div className="text-2xl font-bold" style={{ color: "var(--yalola-primary)" }}>{txFmt(y.revenue_30d, cur)}</div>
          <div className="text-xs mt-1" style={{ color: "var(--yalola-text-muted)" }}>
            Total historique : {txFmt(y.revenue_total, cur)} · {y.impressions_30d ?? 0} impressions · {y.clicks_30d ?? 0} clics ({y.ctr_30d ?? 0}% CTR)
          </div>
        </div>
        <div className="rounded-xl p-4" style={{ background: "var(--yalola-surface-alt)" }} data-testid="ads-rev-google">
          <div className="text-xs font-bold uppercase tracking-wide mb-1" style={{ color: "var(--yalola-text-muted)" }}>Google (estimation)</div>
          <div className="text-2xl font-bold">≈ {txFmt(g.estimated_revenue_30d, cur)}</div>
          <div className="text-xs mt-1" style={{ color: "var(--yalola-text-muted)" }}>
            {g.impressions_30d ?? 0} annonces servies · web {g.by_platform?.web ?? 0} · Android {g.by_platform?.android ?? 0} · iOS {g.by_platform?.ios ?? 0}
          </div>
          <div className="flex items-center gap-2 mt-2">
            <span className="text-xs" style={{ color: "var(--yalola-text-muted)" }}>eCPM ({sym}/1000)</span>
            <Input type="number" min="0" step="0.1" value={ecpm} onChange={(e) => setEcpm(e.target.value)} className="h-8 w-24" data-testid="ads-rev-ecpm-input" />
            <Button size="sm" variant="outline" className="rounded-full h-8" onClick={saveEcpm} disabled={savingEcpm} data-testid="ads-rev-ecpm-save">OK</Button>
          </div>
        </div>
      </div>
      {Array.isArray(data.daily) && data.daily.some((d) => d.yalola > 0 || d.google > 0) && (
        <div className="mt-4" data-testid="ads-rev-daily-chart">
          <div className="text-xs font-bold uppercase tracking-wide mb-2" style={{ color: "var(--yalola-text-muted)" }}>Évolution jour par jour (30 j)</div>
          <div style={{ width: "100%", height: 180 }}>
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={data.daily.map((d) => ({ ...d, label: d.date.slice(5) }))} margin={{ top: 5, right: 8, bottom: 0, left: -14 }}>
                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="var(--yalola-border)" />
                <XAxis dataKey="label" tick={{ fontSize: 10 }} axisLine={false} tickLine={false} interval={4} />
                <YAxis tick={{ fontSize: 10 }} axisLine={false} tickLine={false} width={44} />
                <ChartTooltip
                  formatter={(v, name) => [txFmt(v, cur), name === "yalola" ? "Yalola (réel)" : "Google (estimé)"]}
                  labelFormatter={(l) => `Jour : ${l}`}
                />
                <Line type="monotone" dataKey="yalola" stroke="var(--yalola-primary)" strokeWidth={2} dot={false} name="yalola" />
                <Line type="monotone" dataKey="google" stroke="#8A5A12" strokeWidth={2} strokeDasharray="5 4" dot={false} name="google" />
              </LineChart>
            </ResponsiveContainer>
          </div>
          <div className="flex items-center gap-4 mt-1 text-[11px]" style={{ color: "var(--yalola-text-muted)" }}>
            <span className="inline-flex items-center gap-1.5"><span style={{ width: 14, height: 3, background: "var(--yalola-primary)", display: "inline-block", borderRadius: 2 }} /> Yalola Direct (réel)</span>
            <span className="inline-flex items-center gap-1.5"><span style={{ width: 14, height: 3, background: "#8A5A12", display: "inline-block", borderRadius: 2 }} /> Google (estimé)</span>
          </div>
        </div>
      )}
    </div>
  );
};

const AdsPanel = ({ canManage = true }) => {
  const { config } = useApp();
  const appCur = useAppCurrency();
  const currency = (config?.settings?.default_currency || "USD").toUpperCase();
  const [ads, setAds] = useState([]);
  const [churches, setChurches] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(OFFLINE_AD_EMPTY);
  const [creating, setCreating] = useState(false);
  const [errors, setErrors] = useState({});
  const [emailingId, setEmailingId] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [renewTarget, setRenewTarget] = useState(null);
  const [renewDays, setRenewDays] = useState(30);
  const [renewPrice, setRenewPrice] = useState("");
  const [renewMode, setRenewMode] = useState("free");
  const [renewBusy, setRenewBusy] = useState(false);
  const [trash, setTrash] = useState([]);
  const [retentionDays, setRetentionDays] = useState(config?.settings?.ads_trash_retention_days || 30);
  const [showTrash, setShowTrash] = useState(false);
  const [previewAd, setPreviewAd] = useState(null);
  const [statsAd, setStatsAd] = useState(null);
  const [previewMode, setPreviewMode] = useState("feed");
  const [openJournal, setOpenJournal] = useState({});
  const [adFilter, setAdFilter] = useState("all");
  const [adQuery, setAdQuery] = useState("");
  const load = () => api.get("/admin/ads").then(({ data }) => setAds(data.ads || [])).catch(() => {});
  const loadTrash = () => api.get("/admin/ads/archived").then(({ data }) => { setTrash(data.ads || []); if (data.retention_days) setRetentionDays(data.retention_days); }).catch(() => {});
  useEffect(() => { load(); loadTrash(); }, []);
  useEffect(() => { api.get("/churches/mine").then(({ data }) => setChurches(data.churches || [])).catch(() => {}); }, []);
  const toggle = async (ad) => { try { await api.patch(`/ads/${ad.id}`, { active: !ad.active }); load(); } catch { toast.error("Erreur"); } };
  const archive = async (ad) => {
    if (!window.confirm(`Mettre la publicité « ${ad.title || ""} » à la corbeille ?\n\nElle sera retirée du fil et des reels, mais RÉCUPÉRABLE pendant ${retentionDays} jours depuis la Corbeille.`)) return;
    try { await api.delete(`/ads/${ad.id}`); toast.success("Publicité mise à la corbeille"); load(); loadTrash(); } catch { toast.error("Erreur"); }
  };
  const restore = async (ad) => {
    try { await api.post(`/ads/${ad.id}/restore`); toast.success("Publicité restaurée (en pause)"); load(); loadTrash(); } catch { toast.error("Erreur"); }
  };
  const purge = async (ad) => {
    if (!window.confirm(`Supprimer DÉFINITIVEMENT « ${ad.title || ""} » ?\n\nIrréversible : la publicité, son visuel, ses réactions et commentaires seront effacés.`)) return;
    try { await api.delete(`/ads/${ad.id}/purge`); toast.success("Publicité supprimée définitivement"); loadTrash(); } catch { toast.error("Erreur"); }
  };
  const downloadJournal = async (ad) => {
    try {
      const { data } = await api.get(`/ads/${ad.id}/journal.pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(data);
      const el = document.createElement("a"); el.href = url; el.download = `journal-pub-${(ad.id || "").slice(0, 8)}.pdf`;
      document.body.appendChild(el); el.click(); el.remove(); URL.revokeObjectURL(url);
    } catch { toast.error("Erreur lors de l'export du journal"); }
  };
  const downloadReceipt = async (ad, fmt, paymentIndex) => {
    try {
      const q = paymentIndex != null ? `?payment=${paymentIndex}` : "";
      const { data } = await api.get(`/ads/${ad.id}/receipt.${fmt}${q}`, { responseType: "blob" });
      const url = URL.createObjectURL(data);
      const a = document.createElement("a");
      const suffix = paymentIndex != null ? `-p${paymentIndex}` : "";
      a.href = url; a.download = `recu-pub-${(ad.id || "").slice(0, 8)}${suffix}.${fmt}`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch { toast.error("Impossible de générer le reçu"); }
  };
  const sendReceipt = async (ad) => {
    let email = ad.advertiser_email;
    if (!email) {
      email = window.prompt("E-mail de l'annonceur pour envoyer le reçu :", "");
      if (!email) return;
    }
    setEmailingId(ad.id);
    try {
      const { data } = await api.post(`/ads/${ad.id}/receipt/email`, { email });
      toast.success(`Reçu envoyé à ${data.email} ✅`);
      load();
    } catch (e) { toast.error(e?.friendlyMessage || "Envoi impossible (vérifie la config Resend)"); }
    finally { setEmailingId(null); }
  };
  const renewAd = (ad) => {
    setRenewTarget(ad);
    setRenewDays(ad.days || 30);
    setRenewMode(ad.billing_mode && ad.billing_mode !== "free" ? ad.billing_mode : "free");
    setRenewPrice(ad.billing_mode && ad.billing_mode !== "free" && ad.manual_price ? String(ad.manual_price) : "");
  };
  const doRenew = async () => {
    if (!renewTarget) return;
    const days = parseInt(renewDays, 10);
    if (!days || days < 1) { toast.error("Nombre de jours invalide"); return; }
    let manual_price = null;
    if (renewMode !== "free") {
      const p = Number(String(renewPrice).trim().replace(",", "."));
      if (!(p > 0)) { toast.error("Indique le montant facturé (supérieur à 0) ou choisis « Gratuit / offert »."); return; }
      manual_price = p;
    }
    setRenewBusy(true);
    try {
      await api.post(`/ads/${renewTarget.id}/reactivate`, { days, billing_mode: renewMode, manual_price });
      toast.success(manual_price != null
        ? `Réactivée ${days} j · ${manual_price} ${currency} — ${AD_BILLING_LABEL[renewMode]} (comptabilité) ✅`
        : `Réactivée gratuitement pour ${days} jour(s) ✅`);
      setRenewTarget(null);
      load();
    } catch (e) { toast.error(e?.friendlyMessage || "Réactivation impossible"); }
    finally { setRenewBusy(false); }
  };
  const onMedia = (url, results) => {
    const r = Array.isArray(results) ? results[0] : results;
    setForm((f) => ({ ...f, media_url: url, media_type: r?.media_type === "video" ? "video" : "image", poster_url: r?.poster_url || "" }));
    setErrors((e) => ({ ...e, media: false }));
  };
  const adToForm = (a) => {
    const cta = a.cta || {};
    const tg = a.targeting || {};
    return {
      title: a.title || "", body: a.body || "", media_url: a.media_url || "", media_type: a.media_type || "image",
      poster_url: a.poster_url || "",
      advertiser_name: a.advertiser_name || "", advertiser_email: a.advertiser_email || "",
      as_church_id: a.as_church_id || "", manual_price: a.manual_price != null ? String(a.manual_price) : "",
      billing_mode: a.billing_mode || "",
      target_url: a.target_url || "", days: a.days || 30,
      cta_type: cta.type || "none", cta_label: cta.label || "learn_more", cta_value: cta.value || "",
      cta_headline: cta.headline || "", cta_description: cta.description || "",
      lead_fields: cta.lead_fields || ["name", "phone"],
      custom_fields: (cta.custom_fields || []).map((c) => ({ ...c, options: Array.isArray(c.options) ? c.options.join(", ") : (c.options || "") })),
      t_genders: tg.genders || [], t_age_min: tg.age_min ? String(tg.age_min) : "", t_age_max: tg.age_max ? String(tg.age_max) : "",
      t_countries: (tg.countries || []).join(", "), t_cities: (tg.cities || []).join(", "),
      t_denominations: tg.denominations || [], t_interests: tg.interests || [],
    };
  };
  const startEdit = (a) => {
    setEditingId(a.id);
    setForm(adToForm(a));
    setErrors({});
    setShowForm(true);
    setTimeout(() => window.scrollTo({ top: 0, behavior: "smooth" }), 50);
  };
  const cancelForm = () => { setForm(OFFLINE_AD_EMPTY); setErrors({}); setShowForm(false); setEditingId(null); };
  const approveAd = async (a) => {
    try { await api.post(`/ads/${a.id}/approve`); toast.success("Publicité approuvée ✅"); load(); }
    catch (e) { toast.error(e?.friendlyMessage || "Erreur"); }
  };
  const rejectAd = async (a) => {
    const reason = window.prompt("Motif du refus (envoyé à l'annonceur) :", "");
    if (reason === null) return;
    try { await api.post(`/ads/${a.id}/reject`, { reason }); toast.success("Publicité refusée"); load(); }
    catch (e) { toast.error(e?.friendlyMessage || "Erreur"); }
  };
  const submitAd = async () => {
    const errs = {};
    if (!form.title.trim()) errs.title = true;
    if (!form.media_url) errs.media = true;
    if (!editingId && !form.as_church_id && !form.advertiser_name.trim()) errs.advertiser = true;
    if (!editingId && !form.billing_mode) errs.billing = true;
    if (!editingId && form.billing_mode && form.billing_mode !== "free" && !(Number(form.manual_price) > 0)) errs.price = true;
    if (Object.keys(errs).length) {
      setErrors(errs);
      toast.error("Complète les champs obligatoires surlignés en rouge");
      return;
    }
    setErrors({});
    setCreating(true);
    try {
      const cta = form.cta_type === "none" ? null : {
        label: form.cta_label, type: form.cta_type, value: form.cta_value,
        headline: form.cta_headline, description: form.cta_description,
        lead_fields: form.lead_fields,
        custom_fields: form.cta_type === "lead_form"
          ? form.custom_fields.filter((c) => c.label.trim()).slice(0, 5).map((c) => ({
              id: c.id, label: c.label.trim(), type: c.type,
              options: c.type === "select" ? String(c.options || "").split(",").map((s) => s.trim()).filter(Boolean) : [],
              required: !!c.required,
            }))
          : [],
      };
      const targeting = {
        genders: form.t_genders, age_min: Number(form.t_age_min) || 0, age_max: Number(form.t_age_max) || 0,
        countries: form.t_countries.split(",").map((s) => s.trim()).filter(Boolean),
        cities: form.t_cities.split(",").map((s) => s.trim()).filter(Boolean),
        denominations: form.t_denominations, interests: form.t_interests,
      };
      if (editingId) {
        await api.patch(`/ads/${editingId}`, {
          title: form.title.trim(), body: form.body,
          media_url: form.media_url, media_type: form.media_type, poster_url: form.poster_url || null,
          target_url: form.target_url || null, cta, targeting,
        });
        toast.success("Publicité modifiée ✅");
      } else {
        await api.post("/ads", {
          title: form.title.trim(), body: form.body, media_url: form.media_url, media_type: form.media_type,
          poster_url: form.poster_url || null,
          target_url: form.target_url || null, budget: 0, days: Number(form.days) || 30,
          cta, targeting, origin_url: checkoutOrigin(),
          advertiser_name: form.as_church_id ? "" : form.advertiser_name.trim(),
          as_church_id: form.as_church_id || null,
          advertiser_email: (form.advertiser_email || "").trim() || null,
          billing_mode: form.billing_mode,
          manual_price: form.billing_mode === "free" ? 0 : Number(form.manual_price),
        });
        toast.success(form.billing_mode === "free" ? "Publicité offerte créée ✅" : "Publicité hors-ligne créée ✅");
      }
      cancelForm(); load();
    } catch (e) { toast.error(e?.friendlyMessage || "Erreur"); }
    finally { setCreating(false); }
  };
  const now = new Date();
  const totalImpr = ads.reduce((s, a) => s + (a.impressions || 0), 0);
  const totalClicks = ads.reduce((s, a) => s + (a.clicks || 0), 0);
  const adQ = adQuery.trim().toLowerCase();
  const filteredAds = ads.filter((a) => {
    if (adFilter !== "all" && (a.delivery?.status || "paused") !== adFilter) return false;
    if (adQ) {
      const hay = `${a.title || ""} ${a.advertiser_name || a.advertiser?.name || ""}`.toLowerCase();
      if (!hay.includes(adQ)) return false;
    }
    return true;
  });
  return (
    <div className="space-y-3" data-testid="admin-ads-panel">
      <AdsRevenueDashboard />
      <AdmobDiagnosticsCard />
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <StatCard label="Campagnes" value={ads.length} color="var(--yalola-primary)" />
        <StatCard label="Actives" value={ads.filter((a) => a.active && new Date(a.expires_at) > now).length} />
        <StatCard label="Impressions" value={totalImpr} />
        <StatCard label="Clics" value={totalClicks} color="var(--yalola-secondary)" />
      </div>

      {canManage && (
        <div className="flex justify-end">
          <Button className="rounded-full" onClick={() => { if (showForm) { cancelForm(); } else { setEditingId(null); setForm(OFFLINE_AD_EMPTY); setErrors({}); setShowForm(true); } }} data-testid="admin-ad-offline-new">
            <Plus size={16} className="mr-1" /> Créer une pub hors-ligne
          </Button>
        </div>
      )}

      {showForm && canManage && (
        <div className="grid lg:grid-cols-[1fr_340px] gap-4 items-start" data-testid="admin-ad-offline-form">
          <div className="yalola-card p-5 space-y-3">
          <div className="font-bold font-display">{editingId ? "Modifier la publicité" : "Nouvelle publicité hors-ligne"}</div>
          <p className="text-xs" style={{ color: "var(--yalola-text-muted)" }}>
            {editingId
              ? "Modifie le visuel et les textes comme le ferait l'annonceur, puis approuve la publicité."
              : "Publiée immédiatement, sans paiement en ligne. Le mode de facturation et le montant sont enregistrés pour la comptabilité — une publicité offerte ne crée aucun revenu."}
          </p>
          <div>
            <Label className={errors.title ? "text-red-600" : ""}>Titre *</Label>
            <Input value={form.title} onChange={(e) => { setForm({ ...form, title: e.target.value }); if (errors.title) setErrors((x) => ({ ...x, title: false })); }} placeholder="Ex. Concert de louange 2026" className={errors.title ? "border-red-500 focus-visible:ring-red-500" : ""} data-testid="admin-ad-title" />
            {errors.title && <p className="text-xs text-red-600 mt-1" data-testid="admin-ad-title-error">Le titre est obligatoire.</p>}
          </div>
          <div>
            <Label>Description</Label>
            <Textarea rows={2} value={form.body} onChange={(e) => setForm({ ...form, body: e.target.value })} placeholder="Texte de la publicité..." data-testid="admin-ad-body" />
          </div>
          <div>
            <Label className={errors.media ? "text-red-600" : ""}>Visuel (image ou vidéo) *</Label>
            {form.media_url ? (
              <div className="relative mt-1 rounded-xl overflow-hidden border" style={{ borderColor: "var(--yalola-border)" }}>
                {form.media_type === "video"
                  ? <VideoPlayer mode="controls" src={form.media_url} poster={form.poster_url} className="w-full aspect-video" testId="admin-ad-media-preview-video" />
                  : <img src={resolveMediaUrl(form.media_url)} onError={onMediaImgError} alt="" className="w-full max-h-[70vh] object-contain bg-black/5" />}
                <button onClick={() => setForm({ ...form, media_url: "" })} className="absolute top-2 right-2 rounded-full bg-black/60 text-white px-2 py-1 text-xs" data-testid="admin-ad-media-remove">Retirer</button>
              </div>
            ) : (
              <div className="mt-1"><FileUpload accept="image/*,video/*" onUploaded={onMedia} variant="hero" context="ad" capturePoster buttonLabel="Ajouter un visuel" testId="admin-ad-media" /></div>
            )}
            {errors.media && <p className="text-xs text-red-600 mt-1" data-testid="admin-ad-media-error">Ajoute une image ou une vidéo.</p>}
          </div>
          {!editingId && (<>
          <div className="grid sm:grid-cols-2 gap-3">
            <div>
              <Label className={errors.advertiser ? "text-red-600" : ""}>Nom de l'annonceur (personnalisé)</Label>
              <Input value={form.advertiser_name} onChange={(e) => { setForm({ ...form, advertiser_name: e.target.value, as_church_id: "" }); if (errors.advertiser) setErrors((x) => ({ ...x, advertiser: false })); }} placeholder="Ex. Boulangerie Emmanuel" disabled={!!form.as_church_id} className={errors.advertiser ? "border-red-500 focus-visible:ring-red-500" : ""} data-testid="admin-ad-advertiser" />
            </div>
            <div>
              <Label className={errors.advertiser ? "text-red-600" : ""}>Ou au nom d'une église</Label>
              <select value={form.as_church_id} onChange={(e) => { setForm({ ...form, as_church_id: e.target.value, advertiser_name: e.target.value ? "" : form.advertiser_name }); if (errors.advertiser && e.target.value) setErrors((x) => ({ ...x, advertiser: false })); }} className="w-full h-10 rounded-xl border px-3 text-sm bg-white" style={{ borderColor: errors.advertiser ? "#ef4444" : "var(--yalola-border)" }} data-testid="admin-ad-church">
                <option value="">— Aucune (nom personnalisé) —</option>
                {churches.map((c) => <option key={c.id} value={c.id}>{c.name}{c.city ? ` — ${c.city}` : ""}</option>)}
              </select>
            </div>
          </div>
          {errors.advertiser && <p className="text-xs text-red-600 -mt-1" data-testid="admin-ad-advertiser-error">Indique un nom d'annonceur ou choisis une église.</p>}
          <div>
            <Label>E-mail de l'annonceur (facultatif — pour envoyer le reçu automatiquement)</Label>
            <Input type="email" value={form.advertiser_email} onChange={(e) => setForm({ ...form, advertiser_email: e.target.value })} placeholder="annonceur@example.com" data-testid="admin-ad-advertiser-email" />
          </div>
          </>)}
          <AdCreativeExtras form={form} setForm={setForm} />
          {!editingId && (
            <div className="grid sm:grid-cols-2 gap-3">
              <div>
                <Label className={errors.billing ? "text-red-600" : ""}>Mode de facturation *</Label>
                <select value={form.billing_mode} onChange={(e) => { setForm({ ...form, billing_mode: e.target.value, manual_price: e.target.value === "free" ? "" : form.manual_price }); setErrors((x) => ({ ...x, billing: false, price: false })); }} className="w-full h-10 rounded-xl border px-3 text-sm bg-white" style={{ borderColor: errors.billing ? "#ef4444" : "var(--yalola-border)" }} data-testid="admin-ad-billing-mode">
                  <option value="">— Choisir —</option>
                  {AD_BILLING_MODES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                </select>
                {errors.billing && <p className="text-xs text-red-600 mt-1" data-testid="admin-ad-billing-error">Choisis le mode de facturation.</p>}
                {form.billing_mode === "free" && <p className="text-xs mt-1" style={{ color: "var(--yalola-text-muted)" }} data-testid="admin-ad-billing-free-hint">Offerte — aucun revenu enregistré.</p>}
              </div>
              {form.billing_mode !== "free" && (
                <div>
                  <Label className={errors.price ? "text-red-600" : ""}>Montant facturé ({currency}) *</Label>
                  <Input type="number" min="0.01" step="0.01" value={form.manual_price} onChange={(e) => { setForm({ ...form, manual_price: e.target.value }); if (errors.price) setErrors((x) => ({ ...x, price: false })); }} placeholder="Ex. 50" className={errors.price ? "border-red-500 focus-visible:ring-red-500" : ""} data-testid="admin-ad-price" />
                  {errors.price && <p className="text-xs text-red-600 mt-1" data-testid="admin-ad-price-error">Indique un montant supérieur à 0.</p>}
                </div>
              )}
            </div>
          )}
          <div className="grid sm:grid-cols-2 gap-3">
            {!editingId && <div><Label>Durée (jours)</Label><Input type="number" min="1" value={form.days} onChange={(e) => setForm({ ...form, days: e.target.value })} data-testid="admin-ad-days" /></div>}
            <div><Label>Lien (facultatif)</Label><Input value={form.target_url} onChange={(e) => setForm({ ...form, target_url: e.target.value })} placeholder="https://..." data-testid="admin-ad-target" /></div>
          </div>
          <div className="flex gap-2 justify-end pt-1">
            <Button variant="outline" className="rounded-full" onClick={cancelForm}>Annuler</Button>
            <Button className="rounded-full" onClick={submitAd} disabled={creating} data-testid="admin-ad-submit">
              {creating ? "Enregistrement..." : (editingId ? "Enregistrer les modifications" : "Créer la publicité")}
            </Button>
          </div>
          </div>
          <div className="lg:sticky lg:top-20">
            <div className="text-xs font-bold uppercase tracking-wide mb-2" style={{ color: "var(--yalola-text-muted)" }}>Aperçu en direct</div>
            <AdPreview form={form} user={{ name: form.as_church_id ? (churches.find((c) => c.id === form.as_church_id)?.name || "Église") : (form.advertiser_name || "Annonceur") }} />
          </div>
        </div>
      )}

      {ads.length === 0 && <div className="yalola-card p-8 text-center" style={{ color: "var(--yalola-text-muted)" }} data-testid="admin-ads-empty">Aucune publicité pour le moment.</div>}
      {ads.some((a) => (a.review_status || "approved") === "pending") && (
        <div className="rounded-xl px-4 py-2.5 text-sm font-semibold flex items-center gap-2" style={{ background: "#FEF3E2", color: "#9A5B12" }} data-testid="admin-ads-pending-banner">
          <AlertTriangle size={16} /> {ads.filter((a) => (a.review_status || "approved") === "pending").length} publicité(s) en attente de validation — vérifie le visuel et le texte, puis approuve ou refuse.
        </div>
      )}
      {ads.length > 0 && (
        <div className="flex flex-wrap items-center gap-2" data-testid="admin-ads-filterbar">
          <div className="relative flex-1 min-w-[180px]">
            <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2" style={{ color: "var(--yalola-text-muted)" }} />
            <Input value={adQuery} onChange={(e) => setAdQuery(e.target.value)} placeholder="Rechercher par titre ou annonceur..." className="rounded-full pl-9" data-testid="admin-ads-search" />
          </div>
          <select value={adFilter} onChange={(e) => setAdFilter(e.target.value)} className="h-10 rounded-full border px-3 text-sm bg-white" style={{ borderColor: "var(--yalola-border)" }} data-testid="admin-ads-filter-status">
            {AD_FILTER_OPTIONS.map((o) => <option key={o.v} value={o.v}>{o.label}</option>)}
          </select>
          <span className="text-xs whitespace-nowrap" style={{ color: "var(--yalola-text-muted)" }} data-testid="admin-ads-filter-count">{filteredAds.length} / {ads.length}</span>
        </div>
      )}
      {ads.length > 0 && filteredAds.length === 0 && <div className="yalola-card p-6 text-center text-sm" style={{ color: "var(--yalola-text-muted)" }} data-testid="admin-ads-filter-empty">Aucune publicité ne correspond à ta recherche.</div>}
      <div className="grid md:grid-cols-2 gap-3">
        {[...filteredAds].sort((a, b) => (((a.review_status || "approved") === "pending") ? 0 : 1) - (((b.review_status || "approved") === "pending") ? 0 : 1)).map((a) => {
          const expired = new Date(a.expires_at) < now;
          const ctr = a.impressions ? ((a.clicks / a.impressions) * 100).toFixed(1) : "0.0";
          const rs = a.review_status || "approved";
          const pending = rs === "pending";
          const rejected = rs === "rejected";
          return (
            <div key={a.id} className="yalola-card p-4" style={pending ? { boxShadow: "0 0 0 2px #F0A94B inset" } : rejected ? { boxShadow: "0 0 0 2px #EF9A9A inset" } : {}} data-testid={`admin-ad-${a.id}`}>
              <div className="flex gap-3">
                {a.media_url && <img src={resolveMediaUrl(a.media_type === "video" ? (a.poster_url || a.media_url) : a.media_url)} onError={onMediaImgError} alt="" className="h-16 w-16 rounded-lg object-cover shrink-0" />}
                <div className="flex-1 min-w-0">
                  <div className="font-semibold truncate">{a.title}</div>
                  <div className="text-xs" style={{ color: "var(--yalola-text-muted)" }}>par {a.advertiser_name || a.advertiser?.name || "—"}</div>
                  <div className="flex flex-wrap gap-2 mt-1">
                    <DeliveryBadge delivery={a.delivery} testId={`admin-ad-delivery-${a.id}`} />
                    {a.delivery?.targeting_narrow && a.delivery?.status === "live" && <span className="chip text-[10px]" style={{ background: "#EEF4FF", color: "#3538CD" }} title="Cette publicité ne s'affiche qu'aux personnes correspondant au ciblage (genre / âge / pays)." data-testid={`admin-ad-targeting-${a.id}`}>🎯 Ciblage restreint</span>}
                    <span className="chip text-[10px]">{txFmt(a.budget, appCur)} budget</span>
                    {(a.offline_payment || a.manual_price != null || a.billing_mode) ? (
                      <span className="chip text-[10px]" style={{ background: "var(--yalola-primary-soft)", color: "var(--yalola-primary)" }} data-testid={`admin-ad-billing-${a.id}`}>
                        {adOfflineTotal(a) > 0
                          ? `Hors-ligne · ${AD_BILLING_LABEL[a.billing_mode] || "mode non précisé"} · ${txFmt(adOfflineTotal(a), appCur)}`
                          : "Offerte · aucun revenu"}
                      </span>
                    ) : (PAID_WITH_LABEL[a.paid_with] && <span className="chip text-[10px]" data-testid={`admin-ad-paidwith-${a.id}`}>{PAID_WITH_LABEL[a.paid_with]}</span>)}
                  </div>
                  <div className="text-[11px] mt-1" style={{ color: "var(--yalola-text-muted)" }} data-testid={`admin-ad-delivery-why-${a.id}`}>{(DELIVERY_META[a.delivery?.status || "paused"] || DELIVERY_META.paused).why}</div>
                  {rejected && a.review_note && <div className="text-[11px] mt-1" style={{ color: "#B42318" }} data-testid={`admin-ad-review-note-${a.id}`}>Motif : {a.review_note}</div>}
                </div>
              </div>
              <div className="flex items-center gap-4 mt-3 text-xs" style={{ color: "var(--yalola-text-muted)" }}>
                <span className="flex items-center gap-1"><Eye size={13} /> {a.impressions || 0}</span>
                <span className="flex items-center gap-1"><MousePointerClick size={13} /> {a.clicks || 0}</span>
                <span className="flex items-center gap-1"><TrendingUp size={13} /> {ctr}% CTR</span>
              </div>
              <AdSparkline daily={a.daily} />
              {(pending || rejected) && (
                <div className="flex gap-2 mt-3 flex-wrap">
                  <Button size="sm" className="rounded-full pill-primary" onClick={() => approveAd(a)} disabled={!canManage} data-testid={`admin-ad-approve-${a.id}`}><Check size={14} className="mr-1" /> Approuver</Button>
                  {pending && <Button size="sm" variant="outline" className="rounded-full text-red-600" onClick={() => rejectAd(a)} disabled={!canManage} data-testid={`admin-ad-reject-${a.id}`}><Ban size={14} className="mr-1" /> Refuser</Button>}
                </div>
              )}
              <div className="flex gap-2 mt-2 flex-wrap">
                <Button size="sm" variant="outline" className="rounded-full" onClick={() => setPreviewAd(a)} data-testid={`admin-ad-preview-${a.id}`}><Eye size={14} className="mr-1" /> Aperçu</Button>
                <Button size="sm" variant="outline" className="rounded-full" onClick={() => setStatsAd(a)} data-testid={`admin-ad-stats-${a.id}`}><BarChart3 size={14} className="mr-1" /> Stats</Button>
                <Button size="sm" variant="outline" className="rounded-full" onClick={() => startEdit(a)} disabled={!canManage} data-testid={`admin-ad-edit-${a.id}`}><Pencil size={14} className="mr-1" /> Modifier</Button>
                <Button size="sm" variant="outline" className="rounded-full" onClick={() => toggle(a)} disabled={expired || !canManage} data-testid={`admin-ad-toggle-${a.id}`}>{a.active ? "Mettre en pause" : "Activer"}</Button>
                <Button size="sm" variant={expired ? "default" : "outline"} className={`rounded-full ${expired ? "pill-primary" : ""}`} onClick={() => renewAd(a)} disabled={!canManage} data-testid={`admin-ad-renew-${a.id}`}><RefreshCw size={13} className="mr-1" /> {expired ? "Réactiver" : "Renouveler"}</Button>
                <Button size="sm" variant="outline" className="rounded-full text-red-700" onClick={() => archive(a)} disabled={!canManage} title="Mettre à la corbeille (récupérable)" data-testid={`admin-ad-del-${a.id}`}><Trash2 size={14} /></Button>
              </div>
              <button type="button" onClick={() => setOpenJournal((s) => ({ ...s, [a.id]: !s[a.id] }))} className="text-[11px] font-semibold inline-flex items-center gap-1 mt-2" style={{ color: "var(--yalola-text-muted)" }} data-testid={`admin-ad-journal-toggle-${a.id}`}>
                <Clock size={12} /> Journal{a.history?.length ? ` (${a.history.length})` : ""}
              </button>
              {openJournal[a.id] && (
                <div className="mt-1.5 rounded-lg border p-2.5" style={{ borderColor: "var(--yalola-border)" }} data-testid={`admin-ad-journal-${a.id}`}>
                  <div className="flex items-center justify-between mb-1.5">
                    <div className="text-[11px] font-semibold" style={{ color: "var(--yalola-text-muted)" }}>Historique</div>
                    <button type="button" onClick={() => downloadJournal(a)} className="text-[11px] inline-flex items-center gap-1 font-semibold" style={{ color: "var(--yalola-primary)" }} data-testid={`admin-ad-journal-pdf-${a.id}`}><FileText size={12} /> Exporter PDF</button>
                  </div>
                  <AdJournal history={a.history} />
                </div>
              )}
              {(a.offline_payment || a.manual_price != null || a.billing_mode) && (
                <div className="flex gap-2 mt-2 flex-wrap">
                  <Button size="sm" variant="outline" className="rounded-full" onClick={() => downloadReceipt(a, "pdf")} data-testid={`admin-ad-receipt-pdf-${a.id}`}><FileText size={13} className="mr-1" /> Reçu PDF</Button>
                  <Button size="sm" variant="outline" className="rounded-full" onClick={() => downloadReceipt(a, "csv")} data-testid={`admin-ad-receipt-csv-${a.id}`}><Download size={13} className="mr-1" /> Export CSV</Button>
                  <Button size="sm" variant="outline" className="rounded-full" onClick={() => sendReceipt(a)} disabled={!canManage || emailingId === a.id} data-testid={`admin-ad-receipt-email-${a.id}`}><Mail size={13} className="mr-1" /> {emailingId === a.id ? "Envoi..." : "Envoyer par e-mail"}</Button>
                </div>
              )}
              {(() => {
                const pays = (a.offline_payments && a.offline_payments.length)
                  ? a.offline_payments
                  : (a.manual_price > 0 ? [{ amount: a.manual_price, at: a.created_at, kind: "create", days: a.days, billing_mode: a.billing_mode }] : []);
                if (pays.length <= 1) return null; // single creation receipt already covered above
                return (
                  <div className="mt-2 rounded-lg border p-2" style={{ borderColor: "var(--yalola-border, #e5e7eb)" }} data-testid={`admin-ad-payments-${a.id}`}>
                    <div className="text-[11px] font-bold uppercase tracking-wide mb-1.5" style={{ color: "var(--yalola-text-muted)" }}>Reçus par encaissement ({pays.length})</div>
                    <div className="space-y-1">
                      {pays.map((p, idx) => (
                        <div key={idx} className="flex items-center justify-between gap-2 text-xs" data-testid={`admin-ad-payment-row-${a.id}-${idx}`}>
                          <span className="truncate">
                            <b>{p.kind === "renew" ? "Renouvellement" : "Création"}</b> · {Number(p.amount || 0)} {currency} · {AD_BILLING_LABEL[p.billing_mode] || "Hors-ligne"} · {(p.at || "").slice(0, 10)} · {p.days || a.days} j
                          </span>
                          <Button size="sm" variant="ghost" className="h-6 px-2 rounded-full shrink-0" onClick={() => downloadReceipt(a, "pdf", idx)} data-testid={`admin-ad-payment-receipt-${a.id}-${idx}`}><FileText size={12} className="mr-1" /> Reçu</Button>
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })()}
            </div>
          );
        })}
      </div>

      <Dialog open={!!renewTarget} onOpenChange={(v) => !v && setRenewTarget(null)}>
        <DialogContent className="max-w-sm" data-testid="admin-ad-renew-dialog">
          <DialogHeader>
            <DialogTitle>Renouveler / Réactiver la campagne</DialogTitle>
            <DialogDescription>
              Prolonge la campagne. Choisis <b>Gratuit / offert</b> pour une prolongation sans facturation, ou le mode de règlement et le montant encaissé — il est enregistré dans les revenus hors-ligne (comptabilité).
            </DialogDescription>
          </DialogHeader>
          {renewTarget && (
            <div className="space-y-3">
              <div className="font-semibold truncate text-sm" data-testid="admin-ad-renew-title">{renewTarget.title}</div>
              <div>
                <Label className="text-xs">Durée de prolongation (jours)</Label>
                <Input type="number" min="1" value={renewDays} onChange={(e) => setRenewDays(e.target.value)} data-testid="admin-ad-renew-days" />
                <div className="flex gap-1.5 mt-1.5 flex-wrap">
                  {[7, 14, 30].map((d) => (
                    <button key={d} type="button" onClick={() => setRenewDays(d)} className={`chip text-[11px] ${Number(renewDays) === d ? "chip-primary" : ""}`} data-testid={`admin-ad-renew-days-${d}`}>{d} j</button>
                  ))}
                </div>
              </div>
              <div>
                <Label className="text-xs">Mode de facturation *</Label>
                <select value={renewMode} onChange={(e) => { setRenewMode(e.target.value); setRenewPrice(""); }} className="w-full h-10 rounded-xl border px-3 text-sm bg-white" style={{ borderColor: "var(--yalola-border)" }} data-testid="admin-ad-renew-mode">
                  {AD_BILLING_MODES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                </select>
              </div>
              {renewMode !== "free" && (
                <div>
                  <Label className="text-xs">Montant facturé ({currency}) *</Label>
                  <Input type="number" min="0.01" step="0.01" placeholder="Ex. 50" value={renewPrice} onChange={(e) => setRenewPrice(e.target.value)} data-testid="admin-ad-renew-price" />
                </div>
              )}
              <div className="text-[11px]" style={{ color: "var(--yalola-text-muted)" }} data-testid="admin-ad-renew-mode-hint">
                {renewMode === "free" ? "Mode : prolongation gratuite (offerte) — aucun revenu enregistré" : `Mode : ${AD_BILLING_LABEL[renewMode]}${Number(renewPrice) > 0 ? ` · ${renewPrice} ${currency} encaissés (comptabilité)` : ""}`}
              </div>
              <Button className="w-full pill-primary rounded-full" onClick={doRenew} disabled={renewBusy} data-testid="admin-ad-renew-confirm">
                {renewBusy ? "Traitement..." : (renewMode === "free" ? "Prolonger gratuitement" : "Encaisser & prolonger")}
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <div className="mt-4" data-testid="admin-ads-trash-section">
        <button type="button" onClick={() => setShowTrash((v) => !v)} className="flex items-center gap-2 text-sm font-semibold" data-testid="admin-ads-trash-toggle">
          <Trash2 size={15} /> Corbeille {trash.length > 0 && <span className="chip text-[10px]">{trash.length}</span>}
          <span className="text-[11px] font-normal" style={{ color: "var(--yalola-text-muted)" }}>· récupérable {retentionDays} j</span>
        </button>
        {showTrash && (
          trash.length === 0
            ? <div className="text-xs mt-2" style={{ color: "var(--yalola-text-muted)" }} data-testid="admin-ads-trash-empty">La corbeille est vide.</div>
            : <div className="grid md:grid-cols-2 gap-3 mt-2" data-testid="admin-ads-trash-list">
                {trash.map((a) => {
                  const daysLeft = (a.days_left != null) ? a.days_left : retentionDays;
                  return (
                    <div key={a.id} className="yalola-card p-4 opacity-90" data-testid={`admin-trash-ad-${a.id}`}>
                      <div className="flex gap-3">
                        {a.media_url && <img src={resolveMediaUrl(a.media_type === "video" ? (a.poster_url || a.media_url) : a.media_url)} onError={onMediaImgError} alt="" className="h-14 w-14 rounded-lg object-cover shrink-0 grayscale" />}
                        <div className="flex-1 min-w-0">
                          <div className="font-semibold truncate">{a.title}</div>
                          <div className="text-xs" style={{ color: "var(--yalola-text-muted)" }}>par {a.advertiser_name || a.advertiser?.name || "—"}</div>
                          <div className="text-[11px] mt-1" style={{ color: daysLeft <= 3 ? "#B42318" : "var(--yalola-text-muted)" }} data-testid={`admin-trash-daysleft-${a.id}`}>🗑️ Suppression définitive dans {daysLeft} j</div>
                        </div>
                      </div>
                      <div className="flex gap-2 mt-3 flex-wrap">
                        <Button size="sm" className="rounded-full pill-primary" onClick={() => restore(a)} disabled={!canManage} data-testid={`admin-ad-restore-${a.id}`}><RotateCcw size={14} className="mr-1" /> Restaurer</Button>
                        <Button size="sm" variant="outline" className="rounded-full text-red-700" onClick={() => purge(a)} disabled={!canManage} data-testid={`admin-ad-purge-${a.id}`}><Trash2 size={14} className="mr-1" /> Supprimer définitivement</Button>
                      </div>
                    </div>
                  );
                })}
              </div>
        )}
      </div>

      <AdStatsDialog ad={statsAd} open={!!statsAd} onOpenChange={(v) => !v && setStatsAd(null)} />
      <Dialog open={!!previewAd} onOpenChange={(v) => !v && setPreviewAd(null)}>
        <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto" data-testid="admin-ad-preview-dialog">
          <DialogHeader>
            <DialogTitle>Aperçu de la publicité</DialogTitle>
            <DialogDescription>Voici exactement comment cette publicité apparaît aux utilisateurs.</DialogDescription>
          </DialogHeader>
          {previewAd && (() => {
            const f = adToForm(previewAd);
            const u = { name: previewAd.as_church_id ? (churches.find((c) => c.id === previewAd.as_church_id)?.name || "Église") : (previewAd.advertiser_name || previewAd.advertiser?.name || "Annonceur"), avatar_url: previewAd.advertiser?.avatar_url };
            return (
              <div className="space-y-3">
                <div className="flex gap-2">
                  <button type="button" onClick={() => setPreviewMode("feed")} className={`chip text-xs ${previewMode === "feed" ? "chip-primary" : ""}`} data-testid="admin-ad-preview-tab-feed">Fil</button>
                  <button type="button" onClick={() => setPreviewMode("reels")} className={`chip text-xs ${previewMode === "reels" ? "chip-primary" : ""}`} data-testid="admin-ad-preview-tab-reels">Reels</button>
                </div>
                {previewMode === "feed" ? <AdPreview form={f} user={u} /> : <ReelsAdPreview form={f} user={u} />}
              </div>
            );
          })()}
        </DialogContent>
      </Dialog>
    </div>
  );
};

const SectionTitle = ({ children }) => (
  <div className="text-xs uppercase font-bold tracking-wider pt-1" style={{ color: "var(--yalola-text-muted)" }}>{children}</div>
);

// Lot 626 — carte « à traiter » cliquable : ouvre l'onglet concerné ; accent rouge tant qu'il reste des demandes.
const PendingCard = ({ label, value, hint, tab, onOpen }) => {
  const n = Number(value) || 0;
  return (
    <button type="button" onClick={() => tab && onOpen?.(tab)} className="yalola-card p-5 text-left transition-transform hover:-translate-y-0.5 focus:outline-none focus-visible:ring-2" style={n > 0 ? { borderColor: "#DC2626", boxShadow: "0 0 0 1px #DC2626 inset" } : undefined} data-testid={`pending-card-${tab}`} data-count={n}>
      <div className="flex items-start justify-between gap-2">
        <div className="text-xs uppercase font-semibold" style={{ color: "var(--yalola-text-muted)" }}>{label}</div>
        {n > 0 && <span className="min-w-[18px] h-[18px] px-1 rounded-full grid place-items-center text-[10px] font-bold text-white shrink-0" style={{ background: "#DC2626" }}>{n > 99 ? "99+" : n}</span>}
      </div>
      <div className="text-3xl font-bold mt-1" style={{ fontFamily: "Outfit", color: n > 0 ? "#DC2626" : "var(--yalola-text-muted)" }}>{n}</div>
      <div className="text-xs mt-1" style={{ color: "var(--yalola-text-muted)" }}>{hint || (n > 0 ? "Ouvrir pour traiter →" : "Rien en attente")}</div>
    </button>
  );
};

const DashboardPanel = ({ isSuper, view, onOpen, visibleTabs = null }) => {
  // Money figures live under the Finance tab; the overview keeps the other stats.
  // Only super admin, manager and the merged "Tout" view show finance at a glance.
  const showOverviewFinance = view === "super_admin" || view === "all";
  const isFullAdminView = view === "super_admin" || view === "all";
  const cur = useAppCurrency();
  const [stats, setStats] = useState(null);
  const [err, setErr] = useState(null);
  useEffect(() => {
    api.get("/admin/stats").then(({ data }) => setStats(data)).catch((e) => setErr(e?.friendlyMessage || "Erreur"));
  }, []);
  if (err) return <div className="yalola-card p-8 text-center text-sm" style={{ color: "var(--yalola-text-muted)" }}>Erreur : {err}</div>;
  if (!stats) return <AdminSkeleton />;
  const daily = (stats.daily_30d || []).map((d) => ({ ...d, label: d.date.slice(5) }));
  // Lot 758 — « À traiter » ne montre que les files que ce rôle peut ouvrir (un associé ne voit ni KYC, ni modération, ni tickets).
  const pendingCards = [
    { label: "KYC en attente", value: stats.kyc_pending, tab: "kyc" },
    { label: "Modération", value: stats.moderation_pending, tab: "triage" },
    { label: "Églises en attente", value: stats.churches_pending, hint: `${stats.churches_total} au total`, tab: "churches" },
    { label: "Retraits en attente", value: stats.withdrawals_pending, tab: "donations" },
    { label: "Tickets ouverts", value: stats.tickets_open ?? 0, tab: "tickets" },
  ].filter((c) => !visibleTabs || visibleTabs.has(c.tab));
  const pendingTotal = pendingCards.reduce((s, c) => s + (c.value || 0), 0);
  return (
    <div className="space-y-4" data-testid="admin-dashboard-grid">
      {/* Lot 626 — « À traiter » en tête de page : les demandes en attente sont la 1ʳᵉ chose que voit un admin. */}
      {pendingCards.length > 0 && (
        <>
          <div className="flex items-center justify-between" data-testid="dashboard-pending-section">
            <SectionTitle>À traiter{pendingTotal > 0 ? ` · ${pendingTotal}` : ""}</SectionTitle>
            {pendingTotal === 0 && <span className="text-xs" style={{ color: "#15803D" }}>Tout est à jour ✓</span>}
          </div>
          <div className="grid gap-3 grid-cols-2 lg:grid-cols-5">
            {pendingCards.map((c) => <PendingCard key={c.tab} label={c.label} value={c.value} hint={c.hint} tab={c.tab} onOpen={onOpen} />)}
          </div>
        </>
      )}

      <div className="grid gap-3 grid-cols-2 lg:grid-cols-4">
        <StatCard label="Utilisateurs" value={stats.users_total} hint={`${stats.users_active} actifs`} color="var(--yalola-primary)" />
        <StatCard label="Nouveaux (24 h)" value={stats.users_today} hint={`+${stats.users_week} sur 7 jours`} color="var(--yalola-secondary)" />
        <StatCard label="Publications" value={stats.posts_total} hint={`${stats.posts_today} aujourd'hui · ${stats.posts_hidden} masquées`} />
        <StatCard label="Lives en cours" value={stats.live_now} color="var(--yalola-accent)" />
      </div>

      {/* Mes églises favorites silencieuses (30 j) → relance du responsable. Masquée quand il n'y en a aucune. */}
      <SilentFavoritesCard />

      {/* Relances paiement (badge Vérifié / dons abandonnés) : combien de rappels, combien de paiements obtenus. Finance → vues super admin / tout. */}
      {showOverviewFinance && <RemindersCard />}

      {/* System health (DB latency, Redis, Atlas) is technical → super admin only.
          Support/Moderator/Associate/Manager get stats + money, no technical noise. */}
      {isSuper && <HealthWidget />}

      <SectionTitle>Audience</SectionTitle>
      <div className="grid gap-3 grid-cols-2 lg:grid-cols-4" data-testid="admin-audience-grid">
        <StatCard label="En ligne maintenant" value={stats.online_now ?? 0} hint="Visiteurs actifs (5 min)" color="var(--yalola-secondary)" />
        <StatCard label="Visiteurs uniques (30 j)" value={stats.visitors_30d ?? 0} />
        <StatCard label="Visites (30 j)" value={stats.visits_30d ?? 0} />
        <StatCard label="Durée moy. de session" value={`${stats.avg_session_minutes ?? 0} min`} hint="Sur 30 jours" color="var(--yalola-accent)" />
      </div>

      <SectionTitle>Contenus</SectionTitle>
      <div className="grid gap-3 grid-cols-2 lg:grid-cols-4" data-testid="admin-contents-grid">
        <StatCard label="Photos" value={stats.photos_total ?? 0} />
        <StatCard label="Stories" value={stats.stories_total ?? 0} />
        <StatCard label="Reels" value={stats.reels_total ?? 0} />
        <StatCard label="Lives" value={stats.lives_total ?? 0} />
      </div>

      <SectionTitle>Directs</SectionTitle>
      <div className="grid gap-3 grid-cols-2 lg:grid-cols-4" data-testid="admin-lives-grid">
        <StatCard label="Directs caméra" value={stats.lives_camera_total ?? 0} />
        <StatCard label="Directs externes" value={stats.lives_external_total ?? 0} hint="YouTube / Facebook" />
        <StatCard label="Spectateurs cumulés" value={stats.live_unique_viewers_total ?? 0} hint="Spectateurs uniques" />
        <StatCard label="Stories actives" value={stats.stories_active} />
        {showOverviewFinance && <StatCard label="Offrandes en direct" value={txFmt(stats.live_offerings_total ?? 0, cur)} color="var(--yalola-primary)" />}
        {/* Stats réservées au super admin — masquées en vue Manager / Associé. */}
        {isFullAdminView && (
          <>
            <StatCard label="Commentaires" value={stats.comments_total} />
            <StatCard label="Réactions" value={stats.reactions_total} />
            <StatCard label="Messages échangés" value={stats.messages_total} />
          </>
        )}
      </div>
      {stats.top_live && view !== "associate" && view !== "manager" && (
        <div className="yalola-card p-4 flex items-center justify-between gap-3 flex-wrap" data-testid="admin-top-live">
          <div className="min-w-0">
            <div className="text-xs uppercase font-semibold" style={{ color: "var(--yalola-text-muted)" }}>Direct le plus regardé</div>
            <div className="font-bold truncate">{stats.top_live.type === "external" ? "🔴" : "📷"} {stats.top_live.title}</div>
            <div className="text-xs" style={{ color: "var(--yalola-text-muted)" }}>{stats.top_live.host_name}</div>
          </div>
          <div className="text-right shrink-0">
            <div className="text-2xl font-bold font-display">{stats.top_live.peak_viewers}</div>
            <div className="text-[11px]" style={{ color: "var(--yalola-text-muted)" }}>pic de spectateurs</div>
          </div>
        </div>
      )}

      <div className="yalola-card p-5" data-testid="admin-stats-chart">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <div className="text-xs uppercase font-semibold" style={{ color: "var(--yalola-text-muted)" }}>Activité — 30 derniers jours</div>
          <div className="flex gap-3 text-[11px]" style={{ color: "var(--yalola-text-muted)" }}>
            <span className="flex items-center gap-1"><span className="h-2 w-2 rounded-full inline-block" style={{ background: "#D97757" }} /> Inscriptions</span>
            <span className="flex items-center gap-1"><span className="h-2 w-2 rounded-full inline-block" style={{ background: "#2D5A4C" }} /> Publications</span>
          </div>
        </div>
        <div className="h-52 mt-3" style={{ minHeight: 208 }}>
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={daily} margin={{ top: 5, right: 5, bottom: 0, left: -22 }}>
              <defs>
                <linearGradient id="gUsers" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#D97757" stopOpacity={0.35} />
                  <stop offset="100%" stopColor="#D97757" stopOpacity={0} />
                </linearGradient>
                <linearGradient id="gPosts" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#2D5A4C" stopOpacity={0.3} />
                  <stop offset="100%" stopColor="#2D5A4C" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="var(--yalola-border)" vertical={false} />
              <XAxis dataKey="label" tick={{ fontSize: 10 }} interval={4} tickLine={false} axisLine={false} />
              <YAxis tick={{ fontSize: 10 }} allowDecimals={false} tickLine={false} axisLine={false} />
              <ChartTooltip contentStyle={{ borderRadius: 12, fontSize: 12 }} />
              <Area type="monotone" dataKey="users" name="Inscriptions" stroke="#D97757" fill="url(#gUsers)" strokeWidth={2} />
              <Area type="monotone" dataKey="posts" name="Publications" stroke="#2D5A4C" fill="url(#gPosts)" strokeWidth={2} />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      </div>

      <SectionTitle>Engagement</SectionTitle>
      <div className="grid gap-3 grid-cols-2 md:grid-cols-3 lg:grid-cols-4">
        <StatCard label="Groupes" value={stats.groups_total} />
        <StatCard label="Événements à venir" value={stats.events_upcoming} />
        <StatCard label="Annonces marketplace" value={stats.listings_total} />
        <StatCard label="Pubs actives" value={stats.ads_active} />
      </div>

      {showOverviewFinance && (
        <>
          <SectionTitle>Finances</SectionTitle>
          <div className="grid gap-3 grid-cols-2 lg:grid-cols-4">
            <StatCard label="Dons payés" value={stats.donations_paid} hint={`${txFmt(stats.donation_total, cur)} reçus`} color="var(--yalola-secondary)" />
            <StatCard label="Commission Yalola" value={txFmt(stats.commission_total, cur)} color="var(--yalola-accent)" />
            <StatCard label="Badges vérifiés actifs" value={stats.subscriptions_active ?? 0} hint={`${txFmt(stats.subscription_revenue ?? 0, cur)} de revenus`} color="var(--yalola-accent)" />
            <div className="yalola-card p-5">
              <div className="text-xs uppercase font-semibold" style={{ color: "var(--yalola-text-muted)" }}>Par rôle</div>
              <div className="flex flex-wrap gap-2 mt-3">
                {Object.entries(stats.roles || {}).map(([r, n]) => (
                  <div key={r} className="chip capitalize"><span className="font-bold mr-1" style={{ color: "var(--yalola-primary)" }}>{n}</span>{r}</div>
                ))}
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

const LayoutPanel = ({ isSuper }) => {
  const { lang, setConfig } = useApp();
  const [blocks, setBlocks] = useState([]);
  const [dirty, setDirty] = useState(false);
  const load = () => api.get("/admin/layout").then(({ data }) => {
    const sorted = (data.blocks || []).slice().sort((a, b) => (a.order || 0) - (b.order || 0));
    const counters = {};
    setBlocks(sorted.map((b) => { counters[b.zone] = (counters[b.zone] || 0) + 1; return { ...b, order: counters[b.zone] }; }));
  }).catch(() => {});
  useEffect(() => { load(); }, []);

  const refreshConfig = async () => {
    try { const c = await api.get(`/config/public?lang=${lang}`); setConfig(c.data); } catch { /* noop */ }
  };

  const change = (id, patch) => {
    setBlocks(blocks.map(b => b.id === id ? { ...b, ...patch } : b));
    setDirty(true);
  };

  const save = async () => {
    try {
      const payload = blocks.map(b => ({ id: b.id, zone: b.zone, order: b.order, visible: b.visible, enabled: b.enabled, label: b.label }));
      await api.patch("/admin/layout", payload);
      toast.success("Mise en page sauvegardée");
      setDirty(false);
      refreshConfig();
    } catch (e) { toast.error(e?.friendlyMessage || "Erreur"); }
  };

  const reset = async () => {
    try { const { data } = await api.post("/admin/layout/reset"); setBlocks(data.blocks); toast.success("Mise en page réinitialisée"); setDirty(false); refreshConfig(); }
    catch { toast.error("Erreur"); }
  };

  const reorderZone = (zone, list) => {
    const before = blocks.filter(b => b.zone === zone).sort((a, b) => a.order - b.order);
    const movable = list.filter(b => !b.locked);
    const merged = [];
    before.forEach((b, i) => { if (b.locked) merged[i] = b; });
    let mi = 0;
    for (let i = 0; i < before.length; i++) if (!merged[i]) merged[i] = movable[mi++];
    const orderById = Object.fromEntries(merged.map((b, i) => [b.id, i + 1]));
    setBlocks(blocks.map(b => b.zone === zone ? { ...b, order: orderById[b.id] } : b));
    setDirty(true);
  };

  const zones = ["left_sidebar", "main_feed", "right_sidebar", "create_menu", "account_menu", "drawer_create", "mobile_tabs"];
  const zoneTitle = { left_sidebar: "Menu de navigation (web + tiroir mobile)", main_feed: "Fil principal", right_sidebar: "Barre de droite (web)", create_menu: "Bouton + (mobile / PWA)", account_menu: "Menu compte (web + mobile)", drawer_create: "Tiroir mobile — raccourcis « Créer »", mobile_tabs: "Barre du bas (app mobile)" };
  const zoneHint = {
    left_sidebar: "Carte profil, menu de navigation et suggestions du web ; l'ordre et l'activation des entrées Groupes / Événements / Marketplace / Publicités / Enregistrements s'appliquent aussi au tiroir « Explorer » de l'app mobile. Le « Fil » est fixe.",
    main_feed: "Blocs de la colonne centrale : boutons, stories, composeur, En direct, publications.",
    right_sidebar: "Widgets de la colonne de droite.",
    create_menu: "Raccourcis du bouton « + » (mobile/PWA uniquement) : Prières, Témoignages, Plan de lecture, Direct… Active/désactive et réordonne.",
    account_menu: "Entrées du menu avatar (web) et de la section « Compte » du tiroir mobile : même ordre, même visibilité. Les entrées réservées (Gérer l'église, Mes directs, Tableau de bord) n'apparaissent qu'aux rôles concernés. « Paramètres » reste toujours visible (suppression de compte exigée par les stores).",
    drawer_create: "Tuiles « Créer » en haut du tiroir mobile (Direct et Reel par défaut). Active d'autres raccourcis (Publication, Story, Publicité, Événement, Groupe, Témoignage, Église) et glisse pour ordonner.",
    mobile_tabs: "Les onglets de la barre du bas de l'app mobile : Fil (toujours visible), Reels, Églises, Messages, Profil par défaut. Active au besoin Groupes, Événements, Marketplace, En direct ou Enregistrements, glisse pour ordonner. 5 onglets recommandés — 6 maximum affichés.",
  };

  return (
    <div className="space-y-4" data-testid="admin-layout-panel">
      <div className="flex items-center justify-between yalola-card p-4 gap-3 flex-wrap">
        <div className="text-sm" style={{ color: "var(--yalola-text-muted)" }}>
          Chaque zone reflète les modules réels de l'application, sur le web comme dans l'app mobile. Active/désactive les blocs et <b>glisse la poignée ⋮⋮ pour les réordonner</b> — le « Fil » reste fixe.
        </div>
        {isSuper && (
          <div className="flex gap-2">
            <Button variant="outline" className="rounded-full" onClick={reset} data-testid="layout-reset-btn"><RotateCcw size={14} className="mr-1" /> Réinitialiser</Button>
            <Button disabled={!dirty} className="pill-primary rounded-full" onClick={save} data-testid="layout-save-btn"><Save size={14} className="mr-1" /> Sauvegarder</Button>
          </div>
        )}
      </div>

      <div className="grid md:grid-cols-2 gap-4">
        {zones.map(z => (
          <div key={z} className="yalola-card p-4" data-testid={`layout-zone-${z}`}>
            <div className="font-semibold" style={{ fontFamily: "Outfit" }}>{zoneTitle[z]}</div>
            <p className="text-[11px] mt-0.5 mb-1" style={{ color: "var(--yalola-text-muted)" }}>{zoneHint[z]}</p>
            <Reorder.Group as="div" axis="y" values={blocks.filter(b => b.zone === z).sort((a,b) => a.order - b.order)} onReorder={(list) => reorderZone(z, list)} className="mt-3 space-y-2" data-testid={`layout-list-${z}`}>
              {blocks.filter(b => b.zone === z).sort((a,b) => a.order - b.order).map(b => (
                <LayoutBlockRow key={b.id} block={b} onChange={change} />
              ))}
            </Reorder.Group>
          </div>
        ))}
      </div>
    </div>
  );
};

// Ligne d'un bloc — glisser-déposer par la poignée (souris ET tactile), interrupteurs cliquables normalement.
const LayoutBlockRow = ({ block: b, onChange }) => {
  const controls = useDragControls();
  const draggable = !b.locked;
  return (
    <Reorder.Item
      as="div"
      value={b}
      dragListener={false}
      dragControls={controls}
      drag={draggable ? "y" : false}
      whileDrag={{ scale: 1.02, boxShadow: "0 12px 30px rgba(0,0,0,0.18)", zIndex: 5 }}
      className="border rounded-xl p-3 flex items-center gap-3 select-none"
      style={{ borderColor: "var(--yalola-border)", background: "var(--yalola-surface)", opacity: (!b.locked && !b.pinned && (!b.visible || !b.enabled)) ? 0.55 : 1, position: "relative" }}
      data-testid={`block-${b.key}`}
    >
      <button
        type="button"
        onPointerDown={(e) => { if (draggable) controls.start(e); }}
        className="shrink-0 -ml-1 p-1 rounded-md"
        style={{ touchAction: "none", cursor: draggable ? "grab" : "not-allowed", color: "var(--yalola-text-muted)" }}
        aria-label="Glisser pour réordonner"
        title={draggable ? "Glisser pour réordonner" : "Bloc fixe"}
        data-testid={`block-drag-${b.key}`}
      >
        <GripVertical size={16} />
      </button>
      <div className="flex-1 min-w-0">
        <div className="text-sm font-medium break-words" data-testid={`block-label-${b.key}`}>{b.label}</div>
        <div className="text-xs break-all" style={{ color: "var(--yalola-text-muted)" }}>{b.key}</div>
      </div>
      {b.locked ? (
        <span className="flex items-center gap-1 text-[11px] px-2.5 py-1 rounded-full font-medium shrink-0" style={{ background: "var(--yalola-surface-alt)", color: "var(--yalola-text-muted)" }} data-testid={`block-locked-${b.key}`}>
          <Lock size={12} /> Fixe
        </span>
      ) : b.pinned ? (
        <span className="flex items-center gap-1 text-[11px] px-2.5 py-1 rounded-full font-medium shrink-0" style={{ background: "var(--yalola-primary-soft)", color: "var(--yalola-primary)" }} data-testid={`block-pinned-${b.key}`}>
          <Eye size={12} /> Toujours visible
        </span>
      ) : (
        <>
          <button onClick={() => onChange(b.id, { visible: !b.visible })} title="Aperçu / visibilité" className="shrink-0" data-testid={`block-eye-${b.key}`}>
            {b.visible ? <Eye size={16} style={{ color: "var(--yalola-secondary)" }} /> : <EyeOff size={16} style={{ color: "var(--yalola-text-muted)" }} />}
          </button>
          <Switch checked={b.enabled} onCheckedChange={(v) => onChange(b.id, { enabled: v })} data-testid={`block-switch-${b.key}`} />
        </>
      )}
    </Reorder.Item>
  );
};

const GENDERS = [["male", "Homme"], ["female", "Femme"], ["other", "Autre"]];

const AutoFollowManager = ({ isSuper }) => (
  <AccountListManager isSuper={isSuper} endpoint="/admin/auto-follow" title="Suivi automatique des nouveaux membres" icon={UserPlus} testPrefix="auto-follow"
    successMsg="Suivi automatique mis à jour ✅"
    description={<>Chaque <b>nouvel inscrit</b> suivra automatiquement ces comptes (ex. <b>Yalola App</b>) pour ne jamais avoir un fil vide.</>} />
);


// Bilan d'adoption d'une version publiée : % d'appareils à jour PARMI CEUX QUI ONT DÉCLARÉ leur version (les
// « inconnues » n'entrent jamais dans le pourcentage), ancienneté de la publication — « Bilan 48 h » à partir de 48 h.
const AdoptionLine = ({ v, rel, testId }) => {
  if (!v.devices) return null;
  const hours = rel?.declared_at ? Math.max(0, Math.floor((Date.now() - new Date(rel.declared_at).getTime()) / 3600000)) : null;
  const age = hours === null ? "" : hours < 48 ? ` · publiée il y a ${hours} h` : ` · publiée il y a ${Math.floor(hours / 24)} j`;
  const ready = hours !== null && hours >= 48;
  const known = v.known ?? (v.up_to_date + v.outdated);
  if (!known) {
    return (
      <div className="text-[11px] mt-0.5 font-medium" style={{ color: "var(--yalola-text-muted)" }} data-testid={testId} data-known="0">
        Adoption {rel?.version} : aucune version déclarée ({v.unknown} inconnue{v.unknown > 1 ? "s" : ""}){age}
      </div>
    );
  }
  return (
    <div className="text-[11px] mt-0.5 font-medium" style={{ color: ready ? (v.percent_up_to_date >= 80 ? "#15803D" : "#B45309") : "var(--yalola-text-muted)" }} data-testid={testId} data-known={known}>
      {ready ? "Bilan 48 h" : "Adoption"} {rel?.version} : <b>{v.percent_up_to_date} %</b> à jour ({v.up_to_date}/{known} déclaré{known > 1 ? "s" : ""}) · {v.outdated} en retard{v.unknown ? ` · ${v.unknown} inconnue${v.unknown > 1 ? "s" : ""} (hors calcul)` : ""}{age}
    </div>
  );
};

// Members reached per platform — the in-house proxy for "downloads per store"
// (Play Console / App Store Connect only publish aggregate installs, never per member).
// `versions` (GET /admin/users/app-versions) : appareils pas à jour par rapport à la version publiée sur
// chaque store (Android et iOS numérotés séparément) → compteur sous chaque carte + campagne « Mets à jour ».
const PlatformStats = ({ stats, versions, active, onPick, onCampaign, onUpdateCampaign, onDiagnostic, playStats, appleStats }) => {
  const cards = [
    { key: "android", label: "Android (app)", value: stats.android, icon: Smartphone, hint: "Membres connectés depuis l'app Android" },
    { key: "ios", label: "iOS (app)", value: stats.ios, icon: Smartphone, hint: "Membres connectés depuis l'app iOS" },
    { key: "web", label: "Web seulement", value: stats.web_only, icon: Monitor, hint: "Jamais vus dans l'app → cible d'une campagne de téléchargement" },
  ];
  const outdatedTotal = (versions?.android?.outdated || 0) + (versions?.ios?.outdated || 0);
  return (
    <div className="grid grid-cols-2 md:grid-cols-4 gap-2" data-testid="users-platform-stats">
      {cards.map(({ key, label, value, icon: Icon, hint }) => {
        const v = versions?.[key];
        const rel = versions?.release?.[key];
        return (
          <button type="button" key={key} onClick={() => onPick(key)} title={hint} className="yalola-card p-3 text-left transition-shadow hover:shadow-md" style={active === key ? { outline: "2px solid var(--yalola-primary)" } : undefined} data-testid={`platform-stat-${key}`}>
            <div className="flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wide" style={{ color: "var(--yalola-text-muted)" }}><Icon size={12} /> {label}</div>
            <div className="text-2xl font-extrabold mt-1" style={{ fontFamily: "Outfit" }}>{value}</div>
            {v && (
              // Trois états distincts : à jour (prouvé) · en retard (prouvé par un build déclaré) · inconnue (non déclarée, à part).
              <div className="text-[11px] mt-1 space-x-1" data-testid={`platform-outdated-${key}`} data-outdated={v.outdated} data-unknown={v.unknown} data-up={v.up_to_date}>
                <span style={{ color: "#15803D" }}><b>{v.up_to_date}</b> à jour</span>
                <span style={{ color: "var(--yalola-text-muted)" }}>·</span>
                <span style={{ color: v.outdated ? "#B45309" : "var(--yalola-text-muted)" }}><b>{v.outdated}</b> en retard</span>
                <span style={{ color: "var(--yalola-text-muted)" }}>·</span>
                <span style={{ color: "var(--yalola-text-muted)" }}><b>{v.unknown}</b> inconnue{v.unknown > 1 ? "s" : ""}</span>
                <span style={{ color: "var(--yalola-text-muted)" }}>· store {rel?.version} ({rel?.build})</span>
              </div>
            )}
            {v && key !== "web" && <AdoptionLine v={v} rel={rel} testId={`platform-adoption-${key}`} />}
            {v && key !== "web" && onDiagnostic && (
              <span role="link" tabIndex={0} onClick={(e) => { e.stopPropagation(); onDiagnostic(key); }} onKeyDown={(e) => { if (e.key === "Enter") { e.stopPropagation(); onDiagnostic(key); } }}
                className="inline-block mt-1 text-[11px] font-semibold underline underline-offset-2" style={{ color: "var(--yalola-primary)" }} data-testid={`platform-diagnostic-${key}`}>
                Diagnostic ({v.rows_raw ?? "?"} ligne{(v.rows_raw ?? 0) > 1 ? "s" : ""} → {v.devices} appareil{v.devices > 1 ? "s" : ""})
              </span>
            )}
            {key === "web" && (
              <div className="text-[11px] mt-1" style={{ color: "var(--yalola-text-muted)" }} data-testid="platform-web-hint">
                Membres qui utilisent Yalola uniquement depuis le navigateur, sans l'app → cible de la campagne « Installe l'app »
              </div>
            )}
            {key === "android" && playStats && !playStats.connected && (
              <div className="text-[11px] mt-1" style={{ color: "var(--yalola-text-muted)" }} data-testid="platform-play-pending">
                Google Play : {playStats.reason === "access_pending" ? "accès en cours d'activation…" : "à connecter"}
              </div>
            )}
            {key === "ios" && appleStats && !appleStats.connected && (
              <div className="text-[11px] mt-1" style={{ color: "var(--yalola-text-muted)" }} data-testid="platform-apple-pending">
                App Store : {appleStats.reason === "not_configured" ? "à connecter" : appleStats.reason === "auth" ? "clé refusée par Apple" : appleStats.refreshing ? "récupération en cours…" : "en attente du rapport Apple"}
              </div>
            )}
          </button>
        );
      })}
      <div className="yalola-card p-3 flex flex-col justify-between gap-2" data-testid="platform-stat-total">
        <div>
          <div className="text-[11px] font-semibold uppercase tracking-wide" style={{ color: "var(--yalola-text-muted)" }}>Membres</div>
          <div className="text-2xl font-extrabold mt-1" style={{ fontFamily: "Outfit" }}>{stats.total}</div>
          <div className="text-[11px]" style={{ color: "var(--yalola-text-muted)" }}>{stats.app} avec l'app · {stats.unknown} sans connexion récente</div>
        </div>
        <Button size="sm" className="pill-primary rounded-full w-full" onClick={onCampaign} disabled={!stats.web_only} data-testid="app-campaign-open">
          <Smartphone size={13} className="mr-1" /> Campagne « Installe l'app »
        </Button>
        <Button size="sm" variant="outline" className="rounded-full w-full" onClick={onUpdateCampaign} disabled={!versions} data-testid="update-campaign-open" style={outdatedTotal ? { borderColor: "#B45309", color: "#B45309" } : undefined}>
          <RefreshCw size={13} className="mr-1" /> Campagne « Mets à jour l'app »{outdatedTotal ? ` (${outdatedTotal})` : ""}
        </Button>
      </div>
    </div>
  );
};

// Courbe des téléchargements par jour (App Store + Google Play, 30 j) — pour suivre l'effet d'une campagne.
// null = pas encore de rapport (Apple ~1 j, Google ~3-7 j de décalage) → trou dans la courbe, pas un zéro.
const DownloadsChart = () => {
  const [data, setData] = useState(null);
  useEffect(() => { api.get("/admin/downloads-series?days=30").then(({ data }) => setData(data)).catch(() => {}); }, []);
  if (!data) return null;
  const hasAny = data.days.some((d) => d.apple != null || d.play != null);
  const rows = data.days.map((d) => ({ ...d, label: d.date.slice(5) }));
  return (
    <div className="yalola-card p-3 mt-2" data-testid="downloads-chart">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div className="text-[11px] font-semibold uppercase tracking-wide" style={{ color: "var(--yalola-text-muted)" }}>Téléchargements par jour — 30 j (vrais chiffres des stores)</div>
        <div className="flex items-center gap-3 text-[11px]">
          <span className="flex items-center gap-1"><span className="inline-block w-2.5 h-2.5 rounded-full" style={{ background: "var(--yalola-primary)" }} /> App Store <b data-testid="downloads-chart-apple-total">{data.apple_total}</b></span>
          <span className="flex items-center gap-1"><span className="inline-block w-2.5 h-2.5 rounded-full" style={{ background: "#15803D" }} /> Google Play <b data-testid="downloads-chart-play-total">{data.play_connected ? data.play_total : "…"}</b></span>
        </div>
      </div>
      {hasAny ? (
        <div style={{ width: "100%", height: 150 }} className="mt-2">
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={rows} margin={{ top: 5, right: 8, bottom: 0, left: -18 }}>
              <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="var(--yalola-border)" />
              <XAxis dataKey="label" tick={{ fontSize: 10 }} axisLine={false} tickLine={false} interval={4} />
              <YAxis tick={{ fontSize: 10 }} axisLine={false} tickLine={false} width={40} allowDecimals={false} />
              <ChartTooltip formatter={(v, name) => [v ?? "—", name === "apple" ? "App Store" : "Google Play"]} labelFormatter={(l) => `Jour : ${l}`} />
              <Line type="monotone" dataKey="apple" stroke="var(--yalola-primary)" strokeWidth={2} dot={{ r: 2 }} connectNulls name="apple" />
              <Line type="monotone" dataKey="play" stroke="#15803D" strokeWidth={2} dot={{ r: 2 }} connectNulls name="play" />
            </LineChart>
          </ResponsiveContainer>
        </div>
      ) : (
        <div className="text-[11px] mt-2" style={{ color: "var(--yalola-text-muted)" }} data-testid="downloads-chart-empty">En attente des premiers rapports des stores (Apple ~1 j, Google ~3-7 j de décalage).</div>
      )}
      <div className="text-[10px] mt-1" style={{ color: "var(--yalola-text-muted)" }}>Un trou dans la courbe = rapport pas encore publié par le store (pas un zéro).</div>
    </div>
  );
};

const UsersPager = ({ page, pageCount, total, pageSize, shown, onPage }) => {
  if (!total) return null;
  const first = page * pageSize + 1;
  const last = page * pageSize + shown;
  const pages = [];
  for (let i = Math.max(0, page - 2); i <= Math.min(pageCount - 1, page + 2); i++) pages.push(i);
  return (
    <div className="flex flex-wrap items-center justify-between gap-2 px-4 py-3 border-t" style={{ borderColor: "var(--yalola-border)" }} data-testid="users-pager">
      <div className="text-xs" style={{ color: "var(--yalola-text-muted)" }} data-testid="users-pager-count">
        {shown ? <>{first}–{last} sur <b>{total.toLocaleString("fr-FR")}</b> membre{total > 1 ? "s" : ""}</> : <>Aucun membre sur cette page · <b>{total.toLocaleString("fr-FR")}</b> au total</>} · page {page + 1}/{pageCount}
      </div>
      <div className="flex items-center gap-1">
        <Button size="sm" variant="outline" className="rounded-full h-8 px-3" disabled={page === 0} onClick={() => onPage(0)} data-testid="users-pager-first">«</Button>
        <Button size="sm" variant="outline" className="rounded-full h-8 px-3" disabled={page === 0} onClick={() => onPage(page - 1)} data-testid="users-pager-prev">Précédent</Button>
        {pages[0] > 0 && <span className="px-1 text-xs">…</span>}
        {pages.map((p) => (
          <Button key={p} size="sm" variant={p === page ? "default" : "outline"} className={`rounded-full h-8 w-8 p-0 ${p === page ? "pill-primary" : ""}`} onClick={() => onPage(p)} data-testid={`users-pager-page-${p + 1}`}>{p + 1}</Button>
        ))}
        {pages[pages.length - 1] < pageCount - 1 && <span className="px-1 text-xs">…</span>}
        <Button size="sm" variant="outline" className="rounded-full h-8 px-3" disabled={page >= pageCount - 1} onClick={() => onPage(page + 1)} data-testid="users-pager-next">Suivant</Button>
        <Button size="sm" variant="outline" className="rounded-full h-8 px-3" disabled={page >= pageCount - 1} onClick={() => onPage(pageCount - 1)} data-testid="users-pager-last">»</Button>
      </div>
    </div>
  );
};

const UsersPanel = ({ isSuper }) => {
  const [users, setUsers] = useState([]);
  const [q, setQ] = useState("");
  const [detailId, setDetailId] = useState(() => new URLSearchParams(window.location.search).get("user") || null); // lot 709 : lien de l'alerte « compte supprimé via le formulaire public »
  const [roleFilter, setRoleFilter] = useState("all");
  const [platformFilter, setPlatformFilter] = useState("all");
  const [identityFilter, setIdentityFilter] = useState("all");
  const [platformStats, setPlatformStats] = useState(null);
  const [playStats, setPlayStats] = useState(null);
  const [appleStats, setAppleStats] = useState(null);
  const [appVersions, setAppVersions] = useState(null);
  const [campaignOpen, setCampaignOpen] = useState(false);
  const [updateCampaignOpen, setUpdateCampaignOpen] = useState(() => new URLSearchParams(window.location.search).get("campaign") === "update"); // lot 588 : lien de l'alerte admin
  const [diagnosticPlatform, setDiagnosticPlatform] = useState(null);
  const PAGE_SIZE = 50;
  const [page, setPage] = useState(0);
  const [total, setTotal] = useState(0);
  const loadVersions = () => api.get("/admin/users/app-versions").then(({ data }) => setAppVersions(data)).catch(() => {});
  const load = (p = page) => {
    const params = new URLSearchParams();
    if (q) params.set("q", q);
    if (roleFilter && roleFilter !== "all") params.set("role", roleFilter);
    if (platformFilter && platformFilter !== "all") params.set("platform", platformFilter);
    if (identityFilter && identityFilter !== "all") params.set("identity", identityFilter);
    params.set("limit", String(PAGE_SIZE));
    params.set("skip", String(p * PAGE_SIZE));
    return api.get(`/admin/users?${params.toString()}`).then(({ data }) => { setUsers(data.users || []); setTotal(data.total ?? (data.users || []).length); }).catch(() => {});
  };
  const search = () => { setPage(0); load(0); };
  const goPage = (p) => { setPage(p); load(p); window.scrollTo({ top: 0, behavior: "smooth" }); };
  const pageCount = Math.max(1, Math.ceil(total / PAGE_SIZE));
  useEffect(() => {
    setPage(0);
    load(0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [roleFilter, platformFilter, identityFilter]);
  useEffect(() => { api.get("/admin/users/platform-stats").then(({ data }) => setPlatformStats(data)).catch(() => {}); api.get("/admin/play-stats").then(({ data }) => setPlayStats(data)).catch(() => {}); api.get("/admin/apple-stats").then(({ data }) => setAppleStats(data)).catch(() => {}); loadVersions(); }, []);
  const updateRoles = async (u, roles) => {
    try { await api.patch(`/admin/users/${u.id}`, { roles }); toast.success("Rôles mis à jour"); load(); }
    catch (e) { toast.error(e?.friendlyMessage || "Erreur"); }
  };
  const toggleActive = async (u) => {
    try { await api.patch(`/admin/users/${u.id}`, { is_active: !u.is_active }); toast.success(u.is_active ? "Compte bloqué" : "Compte réactivé"); load(); }
    catch (e) { toast.error(e?.friendlyMessage || "Erreur"); }
  };
  const quickSuspend = async (u) => {
    try { await api.post(`/admin/users/${u.id}/suspend`, { duration: "7d", reason: "Suspension rapide (7 jours)" }); toast.success(`${u.name} suspendu 7 jours`); load(); }
    catch (e) { toast.error(e?.friendlyMessage || "Erreur"); }
  };
  const deleteUser = async (u) => {
    if (!window.confirm(`Supprimer définitivement « ${u.name} » ? Cette action est irréversible.`)) return;
    try { await api.delete(`/admin/users/${u.id}`); toast.success("Compte supprimé"); load(); }
    catch (e) { toast.error(e?.friendlyMessage || "Erreur"); }
  };
  return (
    <div className="space-y-3" data-testid="admin-users-panel">
      <AutoFollowManager isSuper={isSuper} />
      <div className="yalola-card p-4 flex gap-2 items-center flex-wrap">
        <Input placeholder="Rechercher email, nom ou téléphone" value={q} onChange={e => setQ(e.target.value)} onKeyDown={(e) => e.key === "Enter" && search()} className="flex-1 min-w-[180px]" data-testid="users-search" />
        <Select value={roleFilter} onValueChange={setRoleFilter}>
          <SelectTrigger className="w-48 rounded-full" data-testid="users-role-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tous les rôles</SelectItem>
            {ASSIGNABLE_ROLES.map((r) => <SelectItem key={r} value={r}>{ROLE_LABELS[r] || r}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={platformFilter} onValueChange={setPlatformFilter}>
          <SelectTrigger className="w-44 rounded-full" data-testid="users-platform-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Toutes plateformes</SelectItem>
            <SelectItem value="android">Android (app)</SelectItem>
            <SelectItem value="ios">iOS (app)</SelectItem>
            <SelectItem value="web">Web seulement</SelectItem>
          </SelectContent>
        </Select>
        <Select value={identityFilter} onValueChange={setIdentityFilter}>
          <SelectTrigger className="w-52 rounded-full" data-testid="users-identity-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all" data-testid="users-identity-all">Toute identité (KYC)</SelectItem>
            <SelectItem value="verified" data-testid="users-identity-verified">Identité vérifiée</SelectItem>
            <SelectItem value="pending_review" data-testid="users-identity-pending_review">Identité en revue</SelectItem>
            <SelectItem value="in_progress" data-testid="users-identity-in_progress">Identité en cours</SelectItem>
            <SelectItem value="requires_resubmission" data-testid="users-identity-requires_resubmission">Nouvelle pièce demandée</SelectItem>
            <SelectItem value="rejected" data-testid="users-identity-rejected">Identité refusée</SelectItem>
            <SelectItem value="expired" data-testid="users-identity-expired">Identité expirée</SelectItem>
            <SelectItem value="not_started" data-testid="users-identity-not_started">Identité non commencée</SelectItem>
            <SelectItem value="unverified" data-testid="users-identity-unverified">Toutes les non vérifiées</SelectItem>
          </SelectContent>
        </Select>
        <Button className="pill-primary rounded-full" onClick={search}>Rechercher</Button>
      </div>
      {platformStats && <PlatformStats stats={platformStats} versions={appVersions} playStats={playStats} appleStats={appleStats} active={platformFilter} onPick={(p) => setPlatformFilter(platformFilter === p ? "all" : p)} onCampaign={() => setCampaignOpen(true)} onUpdateCampaign={() => setUpdateCampaignOpen(true)} onDiagnostic={setDiagnosticPlatform} />}
      <DownloadsChart />
      <AppVersionsDiagnosticDialog platform={diagnosticPlatform} onOpenChange={(o) => { if (!o) setDiagnosticPlatform(null); }} />
      <AppCampaignDialog open={campaignOpen} onOpenChange={setCampaignOpen} />
      <UpdateCampaignDialog open={updateCampaignOpen} onOpenChange={(o) => { setUpdateCampaignOpen(o); if (!o) loadVersions(); }} isSuper={isSuper} />
      <div className="yalola-card overflow-x-auto">
        <Table>
          <TableHeader><TableRow>
            <TableHead>Nom</TableHead><TableHead>Email</TableHead><TableHead>Rôle</TableHead><TableHead>Plateforme</TableHead><TableHead>Identité (KYC)</TableHead><TableHead>Actif</TableHead><TableHead>Actions</TableHead>
          </TableRow></TableHeader>
          <TableBody>
            {users.map(u => (
              <TableRow key={u.id} data-testid={`user-row-${u.id}`}>
                <TableCell className="font-medium">
                  <div className="flex items-center gap-2">
                    <span>{u.name}</span>
                    {u.name_issue && <span className="chip text-[10px] flex items-center gap-1" style={{ background: "#FEF3C7", color: "#92400E" }} title={{ repeated: "Lettres répétées (ex. « AA AA »)", keyboard: "Suite de touches du clavier", no_vowel: "Aucune voyelle", generic: "Nom générique (Test, User…)", need_full_name: "Un seul mot", too_short: "Trop court", invalid_chars: "Chiffres ou symboles", org_keyword: "Nom d'église / organisation" }[u.name_issue] || u.name_issue} data-testid={`user-name-issue-${u.id}`}><AlertTriangle size={11} /> Nom suspect</span>}
                    {u.is_bot && <span className="chip text-[10px] flex items-center gap-1" style={{ background: "#EDE9FE", color: "#5B21B6" }} data-testid={`user-ai-badge-${u.id}`}><Bot size={11} /> IA</span>}
                    {u.is_active === false && (u.deletion_scheduled_for
                      ? <span className="chip text-[10px]" style={{ background: "#FEF3C7", color: "#92400E" }} title={`Suppression définitive le ${String(u.deletion_scheduled_for).slice(0, 10)} — se reconnecter réactive le compte`} data-testid={`user-deleting-badge-${u.id}`}>Suppression demandée</span>
                      : <span className="chip text-[10px]" style={{ background: "#FEE2E2", color: "#991B1B" }}>Bloqué</span>)}
                    {followLockActive(u) && <span className="chip text-[10px]" style={{ background: "#FEF3C7", color: "#92400E" }} title={u.follow_locked_until ? `Non suivable jusqu'au ${new Date(u.follow_locked_until).toLocaleString("fr-FR", { dateStyle: "short", timeStyle: "short" })}` : "Non suivable (illimité)"} data-testid={`user-follow-lock-badge-${u.id}`}>Non suivable</span>}
                  </div>
                </TableCell>
                <TableCell className="text-xs">
                  {u.email
                    ? u.email
                    : u.phone
                      ? <span className="inline-flex items-center gap-1" title="Inscrit via WhatsApp" data-testid={`user-phone-${u.id}`}><Phone size={11} /> {u.phone}</span>
                      : <span className="opacity-40">—</span>}
                </TableCell>
                <TableCell>
                  {isSuper ? (
                    <RoleMultiSelect value={rolesOf(u)} onCommit={(roles) => updateRoles(u, roles)} testId={`user-role-${u.id}`} />
                  ) : (
                    <div className="flex flex-wrap gap-1">
                      {rolesOf(u).map((r) => <span key={r} className="chip capitalize">{ROLE_LABELS[r] || r}</span>)}
                    </div>
                  )}
                </TableCell>
                <TableCell>
                  <div className="flex flex-wrap gap-1" data-testid={`user-platforms-${u.id}`}>
                    {(u.platforms || []).length === 0 && <span className="opacity-40 text-xs">—</span>}
                    {(u.platforms || []).map((p) => (
                      <span key={p} className="chip text-[10px] inline-flex items-center gap-1" title={u.last_platform === p ? "Dernière connexion" : ""} style={u.last_platform === p ? { background: "var(--yalola-primary-soft)", color: "var(--yalola-primary)" } : undefined}>
                        {p === "android" ? <><Smartphone size={11} /> Android</> : p === "ios" ? <><Smartphone size={11} /> iOS</> : <><Monitor size={11} /> Web</>}
                      </span>
                    ))}
                  </div>
                </TableCell>
                <TableCell>
                  {/* Identity (KYC Yalola, users.identity_status) — the only thing that unlocks withdrawals; the creator-role request is a secondary chip. */}
                  <div className="flex flex-col gap-1 items-start">
                    <KycChip status={identityOf(u)} testId={`user-identity-${u.id}`} />
                    {u.kyc_status && u.kyc_status !== "none" && (
                      <span className="chip text-[10px]" style={{ background: "#EEF0F3", color: "#475467" }} data-testid={`user-kyc-role-${u.id}`}>
                        Rôle : {{ requested: "Demandé", pending: "En attente", info_requested: "Complément", approved: "Validé", rejected: "Refusé" }[u.kyc_status] || u.kyc_status}
                      </span>
                    )}
                  </div>
                </TableCell>
                <TableCell>
                  <Switch checked={u.is_active} onCheckedChange={() => isSuper && toggleActive(u)} disabled={!isSuper} data-testid={`user-active-${u.id}`} />
                </TableCell>
                <TableCell>
                  {isSuper ? (
                    <div className="flex gap-1.5">
                      <Button size="sm" variant="outline" className="rounded-full" onClick={() => setDetailId(u.id)} data-testid={`user-manage-${u.id}`}>Gérer</Button>
                      {u.is_active && (
                        <Button size="sm" variant="outline" className="rounded-full" style={{ color: "#B45309" }} title="Suspendre 7 jours" onClick={() => quickSuspend(u)} data-testid={`user-quick-suspend-${u.id}`}>
                          <Clock size={13} className="mr-1" />7j
                        </Button>
                      )}
                      {u.role !== "super_admin" && (
                        <Button size="sm" variant="outline" className="rounded-full" style={{ color: "#B91C1C" }} title="Supprimer le compte" onClick={() => deleteUser(u)} data-testid={`user-delete-${u.id}`}>
                          <Trash2 size={13} />
                        </Button>
                      )}
                    </div>
                  ) : "—"}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        <UsersPager page={page} pageCount={pageCount} total={total} pageSize={PAGE_SIZE} shown={users.length} onPage={goPage} />
      </div>
      <PublicDeletionsCard onOpenUser={setDetailId} />
      {detailId && <UserDetailDialog userId={detailId} onClose={() => setDetailId(null)} onSaved={() => load()} />}
    </div>
  );
};

const UserDetailDialog = ({ userId, onClose, onSaved }) => {
  const { config, t, user: me } = useApp();
  const isSuperMe = rolesOf(me).includes("super_admin");
  const [data, setData] = useState(null);
  const [form, setForm] = useState(null);
  const [warnReason, setWarnReason] = useState("");
  const [warnMsg, setWarnMsg] = useState("");
  const [warnEmail, setWarnEmail] = useState(true);
  const [suspendDur, setSuspendDur] = useState("7d");
  const [suspendReason, setSuspendReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [roleHistory, setRoleHistory] = useState([]);
  const loadHistory = () => api.get(`/admin/users/${userId}/role-history`).then(({ data }) => setRoleHistory(data.history || [])).catch(() => {});
  const load = () => api.get(`/admin/users/${userId}`).then(({ data: d }) => {
    setData(d);
    const u = d.user || {};
    setForm({
      name: u.name || "", bio: u.bio || "", city: u.city || "", country: u.country || "",
      gender: u.gender || "other", birth_date: (u.birth_date || "").slice(0, 10),
      denomination: u.denomination || "",
      is_verified: !!u.is_verified,
      device_verification_exempt: !!u.device_verification_exempt,
    });
  }).catch(() => toast.error("Erreur de chargement"));
  useEffect(() => {
    load(); loadHistory();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId]);
  const setF = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const u = data?.user;
  const save = async () => {
    setBusy(true);
    try { await api.patch(`/admin/users/${userId}`, form); toast.success("Profil mis à jour"); onSaved?.(); load(); }
    catch (e) { toast.error(e?.friendlyMessage || "Erreur"); }
    finally { setBusy(false); }
  };
  const saveRoles = async (roles) => {
    try { await api.patch(`/admin/users/${userId}`, { roles }); toast.success("Rôles mis à jour"); onSaved?.(); load(); loadHistory(); }
    catch (e) { toast.error(e?.friendlyMessage || "Erreur"); }
  };
  const suspend = async (duration) => {
    try {
      await api.post(`/admin/users/${userId}/suspend`, { duration, reason: suspendReason });
      toast.success(duration === "none" ? "Compte réactivé" : "Compte suspendu");
      setSuspendReason(""); onSaved?.(); load();
    } catch (e) { toast.error(e?.friendlyMessage || "Erreur"); }
  };
  const sendWarn = async () => {
    if (warnMsg.trim().length < 3) { toast.error("Message trop court"); return; }
    setBusy(true);
    try {
      const { data: res } = await api.post(`/admin/users/${userId}/warn`, { reason: warnReason, message: warnMsg, send_email: warnEmail });
      toast.success(res.emailed ? "Avertissement envoyé (in-app + e-mail)" : "Avertissement envoyé (in-app)");
      setWarnMsg(""); setWarnReason(""); load();
    } catch (e) { toast.error(e?.friendlyMessage || "Erreur"); }
    finally { setBusy(false); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl max-h-[88vh] overflow-y-auto" data-testid="user-detail-dialog">
        <DialogHeader>
          <DialogTitle>Gérer le compte</DialogTitle>
          <DialogDescription>Modifie le profil, bloque ou réactive le compte, ou envoie un avertissement au membre.</DialogDescription>
        </DialogHeader>
        {!form ? <AdminSkeleton /> : (
          <div className="space-y-4">
            <div className="flex items-center gap-2 flex-wrap">
              <div className="font-semibold">{u?.name} <span className="text-xs" style={{ color: "var(--yalola-text-muted)" }}>{u?.email || u?.phone}</span></div>
              {u?.is_bot && <span className="chip text-[10px] flex items-center gap-1" style={{ background: "#EDE9FE", color: "#5B21B6" }}><Bot size={11} /> IA</span>}
              {rolesOf(u).map((r) => <span key={r} className="chip text-[10px]">{ROLE_LABELS[r] || r}</span>)}
              <span className="chip text-[10px]" data-testid="user-reports-count" style={(data?.reports_count > 0) ? { background: "#FEE2E2", color: "#991B1B" } : undefined}>
                <ShieldAlert size={11} className="inline mr-1" />{data?.reports_count || 0} signalement(s)
              </span>
              <span className="chip text-[10px]" style={u?.is_active ? { background: "#D1FAE5", color: "#065F46" } : u?.deletion_scheduled_for ? { background: "#FEF3C7", color: "#92400E" } : { background: "#FEE2E2", color: "#991B1B" }} data-testid="user-detail-status">
                {u?.is_active ? "Actif" : u?.deletion_scheduled_for ? `Suppression demandée (définitive le ${String(u.deletion_scheduled_for).slice(0, 10)})` : "Bloqué"}
              </span>
            </div>
            <UserIdentifiersEditor user={u} lastLogin={data?.last_login} isSuper={isSuperMe} onSaved={() => { onSaved?.(); load(); }} />
            <UserAgeAssuranceCard aa={u?.age_assurance} />
            <UserPrivilegesCard user={u} priv={data?.privileges} preview={data?.privileges_if_enabled} />
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="sm:col-span-2">
                <Label>Rôles <span className="text-xs font-normal" style={{ color: "var(--yalola-text-muted)" }}>(jusqu'à {MAX_ROLES} · super admin exclusif)</span></Label>
                <div className="mt-1"><RoleMultiSelect value={rolesOf(u)} onCommit={saveRoles} testId="user-edit-roles" /></div>
                {roleHistory.length > 0 && (
                  <div className="mt-2 rounded-xl p-2" style={{ background: "var(--yalola-surface-alt)" }} data-testid="user-role-history">
                    <div className="text-[11px] font-semibold mb-1" style={{ color: "var(--yalola-text-muted)" }}>Historique des rôles</div>
                    <ul className="space-y-1 max-h-40 overflow-y-auto pr-1">
                      {roleHistory.map((h, i) => (
                        <li key={i} className="text-[11px] leading-relaxed flex items-center gap-1.5 flex-wrap" data-testid={`role-history-${i}`}>
                          <span style={{ color: "var(--yalola-text-muted)" }}>{h.at ? new Date(h.at).toLocaleString("fr-FR", { dateStyle: "short", timeStyle: "short" }) : "—"}</span>
                          <span>·</span>
                          <span>{(h.roles || []).map((r) => ROLE_LABELS[r] || r).join(", ") || "—"}</span>
                          <span style={{ color: "var(--yalola-text-muted)" }}>· par {h.by}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
              <div className="sm:col-span-2"><Label>Nom complet</Label><Input value={form.name} onChange={e => setF("name", e.target.value)} data-testid="user-edit-name" /></div>
              <div><Label>Ville</Label><Input value={form.city} onChange={e => setF("city", e.target.value)} data-testid="user-edit-city" /></div>
              <div><Label>Pays (code)</Label><Input value={form.country} onChange={e => setF("country", e.target.value)} data-testid="user-edit-country" /></div>
              <div><Label>Date de naissance</Label><Input type="date" min="1900-01-01" max={new Date().toISOString().split("T")[0]} value={form.birth_date} onChange={e => setF("birth_date", e.target.value)} data-testid="user-edit-birthdate" /></div>
              <div><Label>Genre</Label>
                <Select value={form.gender} onValueChange={(v) => setF("gender", v)}>
                  <SelectTrigger data-testid="user-edit-gender"><SelectValue /></SelectTrigger>
                  <SelectContent>{GENDERS.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="sm:col-span-2"><Label>Dénomination</Label>
                <Select value={form.denomination || undefined} onValueChange={(v) => setF("denomination", v)}>
                  <SelectTrigger data-testid="user-edit-denom"><SelectValue placeholder="Choisir…" /></SelectTrigger>
                  <SelectContent>
                    {(() => {
                      const names = config?.denominations?.length ? config.denominations.map((d) => d.name) : [];
                      const opts = form.denomination && !names.includes(form.denomination) ? [form.denomination, ...names] : names;
                      return opts.map((n) => <SelectItem key={n} value={n}>{denominationLabel(t, n)}</SelectItem>);
                    })()}
                  </SelectContent>
                </Select>
              </div>
              <div className="sm:col-span-2"><Label>Bio</Label><Textarea rows={2} value={form.bio} onChange={e => setF("bio", e.target.value)} data-testid="user-edit-bio" /></div>
            </div>
            <div className="flex items-center justify-between flex-wrap gap-2">
              <div className="flex items-center gap-4 flex-wrap">
                <label className="flex items-center gap-2 text-sm"><Switch checked={form.is_verified} onCheckedChange={(v) => setF("is_verified", v)} data-testid="user-edit-verified" /> Badge vérifié</label>
                <label className="flex items-center gap-2 text-sm" title="Autorise la connexion depuis un nouvel appareil sans code de vérification (évaluateurs Google/Apple, testeurs internes).">
                  <Switch checked={!!form.device_verification_exempt} onCheckedChange={(v) => setF("device_verification_exempt", v)} data-testid="user-edit-device-exempt" />
                  Exempté de vérification d'appareil
                </label>
              </div>
              <Button className="pill-primary rounded-full" onClick={save} disabled={busy} data-testid="user-save">Enregistrer le profil</Button>
            </div>
            <FollowLockCard user={u} onChanged={() => { onSaved?.(); load(); }} />
            <div className="rounded-xl border p-4 space-y-2" style={{ borderColor: "var(--yalola-border)" }}>
              <div className="flex items-center gap-2 font-semibold text-sm"><Lock size={15} style={{ color: "#B91C1C" }} /> Suspension du compte</div>
              {u?.suspended_until && <p className="text-xs" style={{ color: "#B91C1C" }} data-testid="user-suspended-until">Suspendu jusqu'au {new Date(u.suspended_until).toLocaleString("fr-FR")}.</p>}
              {u?.is_active ? (
                <div className="flex items-end gap-2 flex-wrap">
                  <div>
                    <Label className="text-xs">Durée</Label>
                    <Select value={suspendDur} onValueChange={setSuspendDur}>
                      <SelectTrigger className="w-40" data-testid="suspend-duration"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="24h">24 heures</SelectItem>
                        <SelectItem value="7d">7 jours</SelectItem>
                        <SelectItem value="30d">30 jours</SelectItem>
                        <SelectItem value="permanent">Définitif</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <Input placeholder="Motif (visible par le membre)" value={suspendReason} onChange={(e) => setSuspendReason(e.target.value)} className="flex-1 min-w-[180px]" data-testid="suspend-reason" />
                  <Button variant="outline" className="rounded-full" style={{ color: "#B91C1C" }} onClick={() => suspend(suspendDur)} data-testid="user-suspend-btn"><Lock size={14} className="mr-1" /> Suspendre</Button>
                </div>
              ) : (
                <Button className="pill-primary rounded-full" onClick={() => suspend("none")} data-testid="user-reactivate-btn"><Check size={14} className="mr-1" /> Réactiver le compte</Button>
              )}
            </div>
            <div className="rounded-xl border p-4 space-y-2" style={{ borderColor: "var(--yalola-border)" }}>
              <div className="flex items-center gap-2 font-semibold text-sm"><ShieldAlert size={16} style={{ color: "#B45309" }} /> Avertir l'utilisateur</div>
              <p className="text-xs" style={{ color: "var(--yalola-text-muted)" }}>Prévenir un membre signalé de notre intention de bloquer son compte s'il ne corrige pas son comportement.</p>
              <Input placeholder="Motif (ex : contenu inapproprié)" value={warnReason} onChange={e => setWarnReason(e.target.value)} data-testid="warn-reason" />
              <Textarea rows={3} placeholder="Message adressé au membre…" value={warnMsg} onChange={e => setWarnMsg(e.target.value)} data-testid="warn-message" />
              <div className="flex items-center justify-between flex-wrap gap-2">
                <label className="flex items-center gap-2 text-sm"><Switch checked={warnEmail} onCheckedChange={setWarnEmail} data-testid="warn-email-toggle" /> Envoyer aussi par e-mail</label>
                <Button className="rounded-full" onClick={sendWarn} disabled={busy || warnMsg.trim().length < 3} data-testid="warn-send"><ShieldAlert size={14} className="mr-1" /> Envoyer l'avertissement</Button>
              </div>
              {Array.isArray(data?.warnings) && data.warnings.length > 0 && (
                <div className="pt-2 space-y-2" data-testid="warn-history">
                  <div className="text-xs font-semibold" style={{ color: "var(--yalola-text-muted)" }}>Historique des avertissements</div>
                  {data.warnings.map(w => (
                    <div key={w.id} className="text-xs rounded-lg p-2" style={{ background: "rgba(0,0,0,0.04)" }}>
                      <div className="flex items-center justify-between gap-2">
                        <span className="font-medium">{w.reason || "Avertissement"}</span>
                        <span style={{ color: "var(--yalola-text-muted)" }}>{new Date(w.created_at).toLocaleDateString("fr-FR")}{w.emailed ? " · e-mail" : ""}</span>
                      </div>
                      <div style={{ color: "var(--yalola-text-muted)" }}>{w.message}</div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

const KYC_STATUS_META = {
  pending: { label: "En attente", bg: "#FEF3C7", fg: "#92400E" },
  info_requested: { label: "Complément demandé", bg: "#DBEAFE", fg: "#1E40AF" },
  approved: { label: "Validée", bg: "#D1FAE5", fg: "#065F46" },
  rejected: { label: "Refusée", bg: "#FDECEC", fg: "#B42318" },
};
const KYC_SOURCE_FILTERS = [
  { v: "all", label: "Toutes" },
  { v: "intent", label: "Intentions d'inscription" },
  { v: "paid", label: "Vérifications payantes" },
];
const KYC_ACTION_META = {
  approve: { title: "Valider la demande", cta: "Valider", ok: "Demande validée ✓" },
  request_info: { title: "Demander un complément", cta: "Envoyer la demande", ok: "Complément demandé — le membre a été notifié" },
  reject: { title: "Refuser la demande", cta: "Refuser", ok: "Demande refusée — le membre a été notifié" },
};
const kycFmtDate = (iso) => iso ? new Date(iso).toLocaleString("fr-FR", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }) : "—";
const kycFmtDay = (iso) => iso ? new Date(iso).toLocaleDateString("fr-FR", { day: "2-digit", month: "long", year: "numeric" }) : "";
// What « Valider » grants for this request (rule: intent → role only; paid → role + badge).
const kycGrantText = (it) => {
  const role = ROLE_LABELS[it.requested_role] || it.requested_role;
  if (it.kind !== "paid") return `rôle ${role} (privilèges du rôle, SANS badge ✓)`;
  return `rôle ${role} + badge vérifié ✓ ${it.verified_until ? `jusqu'au ${kycFmtDay(it.verified_until)}` : "à vie"}`;
};

const KycKindChip = ({ kind, testId }) => kind === "paid"
  ? <span className="chip text-[10px]" style={{ background: "#D1FAE5", color: "#065F46" }} data-testid={testId}>Vérification payante · paiement reçu ✓</span>
  : <span className="chip text-[10px]" style={{ background: "#EEF0F3", color: "#475467" }} data-testid={testId}>Intention d'inscription · gratuite</span>;

const KycAccountState = ({ user }) => {
  if (!user) return <span>compte supprimé</span>;
  const roles = (user.roles || []).map((r) => ROLE_LABELS[r] || r).join(" · ") || "Membre";
  return <span>{roles} · {user.is_verified ? <b style={{ color: "#065F46" }}>vérifié ✓</b> : "non vérifié"}</span>;
};

const KycCard = ({ it, canManage, onAction }) => {
  const open = it.status === "pending" || it.status === "info_requested";
  const st = KYC_STATUS_META[it.status] || KYC_STATUS_META.pending;
  const u = it.user;
  return (
    <div className="yalola-card p-4 space-y-3" data-testid={`kyc-item-${it.id}`}>
      <div className="flex items-start gap-3 flex-wrap">
        {u?.avatar_url
          ? <img src={imageVariant(u.avatar_url, 44)} alt="" className="h-11 w-11 rounded-full object-cover shrink-0" onError={onMediaImgError} />
          : <div className="h-11 w-11 rounded-full grid place-items-center shrink-0 font-bold" style={{ background: "var(--yalola-primary-soft)", color: "var(--yalola-primary)" }}>{(u?.name || "?").slice(0, 1).toUpperCase()}</div>}
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-semibold" data-testid={`kyc-name-${it.id}`}>{u?.name || "Membre supprimé"}</span>
            <span className="text-xs" style={{ color: "var(--yalola-text-muted)" }}>{u?.email}</span>
            {u?.id && <Link to={`/profile/${u.id}`} className="text-xs underline inline-flex items-center gap-1" style={{ color: "var(--yalola-primary)" }} data-testid={`kyc-profile-link-${it.id}`}><ExternalLink size={11} /> Voir le profil</Link>}
          </div>
          <div className="flex items-center gap-1.5 flex-wrap mt-1.5">
            <span className="chip text-[10px]" style={{ background: st.bg, color: st.fg }} data-testid={`kyc-status-${it.id}`}>{st.label}</span>
            <KycKindChip kind={it.kind} testId={`kyc-kind-${it.id}`} />
            <span className="chip text-[10px]" style={{ background: "var(--yalola-primary-soft)", color: "var(--yalola-primary)" }} data-testid={`kyc-role-${it.id}`}>Rôle demandé : {ROLE_LABELS[it.requested_role] || it.requested_role}</span>
          </div>
          <div className="text-[11px] mt-1.5 flex items-center gap-1 flex-wrap" style={{ color: "var(--yalola-text-muted)" }}>
            <Clock size={11} /> Demande du {kycFmtDate(it.created_at)} · {timeAgo(it.created_at).startsWith("à") ? timeAgo(it.created_at) : `il y a ${timeAgo(it.created_at)}`}
            {it.resubmitted_at && <span> · complément reçu le {kycFmtDate(it.resubmitted_at)}</span>}
            <span> · Compte actuel : <KycAccountState user={u} /></span>
          </div>
        </div>
      </div>

      <div className="grid sm:grid-cols-2 gap-x-4 gap-y-1 text-xs" data-testid={`kyc-details-${it.id}`}>
        {it.ministry_name && <div><b>Ministère :</b> {it.ministry_name}</div>}
        {(it.country || u?.country) && <div><b>Pays :</b> {it.country || u?.country}</div>}
        {it.website && <div className="sm:col-span-2 truncate"><b>Preuve (lien) :</b> <a href={it.website} target="_blank" rel="noreferrer" className="underline" style={{ color: "var(--yalola-primary)" }}>{it.website}</a></div>}
        {it.notes && it.notes !== "Registration intent" && <div className="sm:col-span-2" style={{ color: "var(--yalola-text-muted)" }}>{it.notes}</div>}
        {it.notes === "Registration intent" && <div className="sm:col-span-2" style={{ color: "var(--yalola-text-muted)" }}>Le membre a choisi ce rôle à l'inscription, sans paiement ni preuve.</div>}
      </div>
      {Array.isArray(it.documents) && it.documents.length > 0 && (
        <div className="flex flex-wrap gap-2" data-testid={`kyc-docs-${it.id}`}>
          {it.documents.map((d, i) => kycDocRef(d)
            ? <SecureDocImage key={i} path={`/admin/kyc/documents/${kycDocRef(d)}`} label={`Justificatif · ${u?.name || ""}`} size="h-16 w-16" testId={`kyc-doc-${it.id}-${i}`} />
            : <a key={i} href={d} target="_blank" rel="noreferrer" className="chip text-[10px] underline" data-testid={`kyc-doc-link-${it.id}-${i}`}>{String(d).replace(/^https?:\/\//, "").slice(0, 40)}</a>)}
        </div>
      )}

      {(it.info_message || it.user_reply) && (
        <div className="rounded-xl p-3 text-xs space-y-1.5" style={{ background: "var(--yalola-bg)" }} data-testid={`kyc-thread-${it.id}`}>
          {it.info_message && <div><b>Complément demandé</b> par {it.info_requested_by_name || "un admin"} le {kycFmtDate(it.info_requested_at)} : « {it.info_message} »</div>}
          {it.user_reply && <div><b>Réponse du membre</b> le {kycFmtDate(it.resubmitted_at)} : « {it.user_reply} »</div>}
          {it.complement_reminder_sent_at && <div style={{ color: "var(--yalola-text-muted)" }}>Relance automatique envoyée le {kycFmtDate(it.complement_reminder_sent_at)} (7 jours sans réponse).</div>}
          {it.status === "info_requested" && <div style={{ color: "#1E40AF" }}>En attente de la réponse du membre.</div>}
        </div>
      )}

      {open ? (
        <div className="flex items-center justify-between gap-3 flex-wrap pt-1 border-t" style={{ borderColor: "var(--yalola-border)" }}>
          <div className="text-xs" data-testid={`kyc-grant-${it.id}`}><b>Valider accorde →</b> {kycGrantText(it)}</div>
          <div className="flex gap-2 flex-wrap">
            <Button size="sm" className="pill-primary rounded-full" onClick={() => onAction("approve", it)} disabled={!canManage} data-testid={`kyc-approve-${it.id}`}><Check size={14} className="mr-1" /> Valider</Button>
            {it.status === "pending" && <Button size="sm" variant="outline" className="rounded-full" onClick={() => onAction("request_info", it)} disabled={!canManage} data-testid={`kyc-request-info-${it.id}`}><MessagesSquare size={14} className="mr-1" /> Demander un complément</Button>}
            <Button size="sm" variant="outline" className="rounded-full" style={{ color: "#B42318" }} onClick={() => onAction("reject", it)} disabled={!canManage} data-testid={`kyc-reject-${it.id}`}><X size={14} className="mr-1" /> Refuser</Button>
          </div>
        </div>
      ) : (
        <div className="text-xs pt-1 border-t" style={{ borderColor: "var(--yalola-border)", color: "var(--yalola-text-muted)" }} data-testid={`kyc-resolution-${it.id}`}>
          <b style={{ color: st.fg }}>{st.label}</b> par {it.resolved_by_name || "—"} le {kycFmtDate(it.resolved_at)}
          {it.status === "approved" && <span> · accordé : {it.granted === "role_badge" ? "rôle + badge vérifié ✓" : "rôle seul (sans badge)"}</span>}
          {it.note && <span> · Motif : « {it.note} »</span>}
        </div>
      )}
    </div>
  );
};

const KycActionDialog = ({ target, onClose, onDone }) => {
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => { setNote(""); }, [target]);
  if (!target) return null;
  const { mode, it } = target;
  const meta = KYC_ACTION_META[mode];
  const needNote = mode !== "approve";
  const submit = async () => {
    if (needNote && !note.trim()) { toast.error("Écris un message pour le membre."); return; }
    setBusy(true);
    try {
      await api.post(`/admin/kyc/${it.id}/action`, { action: mode, note: note.trim() || null });
      toast.success(meta.ok); onDone();
    } catch (e) { toast.error(e?.friendlyMessage || "Erreur"); }
    finally { setBusy(false); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-md" data-testid="kyc-action-dialog">
        <DialogHeader>
          <DialogTitle className="font-display">{meta.title}</DialogTitle>
          <DialogDescription>{it.user?.name} · {ROLE_LABELS[it.requested_role] || it.requested_role} · {it.kind === "paid" ? "vérification payante" : "intention d'inscription"}</DialogDescription>
        </DialogHeader>
        {mode === "approve" ? (
          <div className="rounded-xl p-3 text-sm" style={{ background: "var(--yalola-primary-soft)" }} data-testid="kyc-approve-summary">
            <div><b>Le membre obtient :</b> {kycGrantText(it)}.</div>
            {it.kind !== "paid" && <div className="text-xs mt-1" style={{ color: "var(--yalola-text-muted)" }}>Le badge ✓ et les privilèges « vérifié » restent réservés à ceux qui passent par « Vérifier mon compte » (paiement + preuve validée).</div>}
          </div>
        ) : (
          <div>
            <Label className="text-xs">{mode === "reject" ? "Motif du refus (envoyé au membre)" : "Ce que le membre doit fournir (envoyé au membre)"}</Label>
            <Textarea value={note} onChange={(e) => setNote(e.target.value)} rows={4} className="mt-1"
              placeholder={mode === "reject" ? "Ex. : la preuve fournie ne permet pas de confirmer votre ministère." : "Ex. : merci d'ajouter un lien vers votre chaîne ou une photo d'un document officiel de votre église."}
              data-testid="kyc-action-note" />
          </div>
        )}
        <DialogFooter>
          <Button variant="ghost" className="rounded-full" onClick={onClose} data-testid="kyc-action-cancel">Annuler</Button>
          <Button className={`rounded-full ${mode === "reject" ? "" : "pill-primary"}`} variant={mode === "reject" ? "destructive" : "default"} onClick={submit} disabled={busy} data-testid="kyc-action-confirm">
            {busy ? <Loader2 size={14} className="animate-spin" /> : meta.cta}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const KycPanel = ({ canManage = true, pendingRoles = 0, pendingIdentity = 0 }) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const view = searchParams.get("view") === "identity" ? "identity" : "roles";
  const setView = (v) => { const next = new URLSearchParams(searchParams); if (v === "identity") next.set("view", "identity"); else next.delete("view"); setSearchParams(next, { replace: true }); };
  // lot 768 — nombre « à traiter » sur chaque onglet (mêmes compteurs que la pastille du menu, rafraîchis toutes les 45 s)
  const Count = ({ n, active, testId }) => (n > 0 ? (
    <span className="min-w-[18px] h-[18px] px-1 rounded-full grid place-items-center text-[10px] font-bold" style={active ? { background: "#fff", color: "#DC2626" } : { background: "#DC2626", color: "#fff" }} data-testid={testId}>{n}</span>
  ) : null);
  return (
    <div className="space-y-3">
      <div className="flex gap-2 flex-wrap" data-testid="kyc-view-switch">
        <button onClick={() => setView("roles")} className={`pill ${view === "roles" ? "pill-primary" : "pill-outline"} flex items-center gap-1.5`} data-testid="kyc-view-roles">Demandes de rôle <Count n={pendingRoles} active={view === "roles"} testId="kyc-view-roles-count" /></button>
        <button onClick={() => setView("identity")} className={`pill ${view === "identity" ? "pill-primary" : "pill-outline"} flex items-center gap-1.5`} data-testid="kyc-view-identity">Identité (KYC) <Count n={pendingIdentity} active={view === "identity"} testId="kyc-view-identity-count" /></button>
      </div>
      {view === "identity" ? <IdentityAdminPanel canManage={canManage} /> : <KycRolePanel canManage={canManage} />}
    </div>
  );
};

const KycRolePanel = ({ canManage = true }) => {
  const [items, setItems] = useState([]);
  const [counts, setCounts] = useState({});
  const [meta, setMeta] = useState({ pending_intents: 0, intent_auto_approve: false });
  const [status, setStatus] = useState("pending");
  const [source, setSource] = useState("all");
  const [q, setQ] = useState("");
  const [target, setTarget] = useState(null);
  const [bulkBusy, setBulkBusy] = useState(false);
  const kycPager = usePager(items);
  const load = () => api.get(`/admin/kyc/queue?status=${status}&source=${source}&q=${encodeURIComponent(q.trim())}`)
    .then(({ data }) => { setItems(data.items || []); setCounts(data.counts || {}); setMeta({ pending_intents: data.pending_intents || 0, intent_auto_approve: !!data.intent_auto_approve }); }).catch(() => {});
  useEffect(() => {
    const tm = setTimeout(load, q ? 300 : 0);
    return () => clearTimeout(tm);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status, source, q]);
  const approveAllIntents = async () => {
    if (!window.confirm(`Accorder maintenant leur rôle aux ${meta.pending_intents} intention(s) d'inscription en attente ?\n\nRôle uniquement (sans badge ✓). Les vérifications payantes ne sont pas concernées.`)) return;
    setBulkBusy(true);
    try {
      const { data } = await api.post("/admin/kyc/approve-pending-intents");
      toast.success(`${data.approved} rôle(s) accordé(s)${data.skipped ? ` · ${data.skipped} compte(s) supprimé(s) ignoré(s)` : ""}`);
      load();
    } catch (e) { toast.error(e?.friendlyMessage || "Erreur"); }
    finally { setBulkBusy(false); }
  };
  return (
    <div className="space-y-3" data-testid="admin-kyc-panel">
      <div className="yalola-card p-4 space-y-3">
        <div className="flex gap-2 flex-wrap">
          {Object.entries(KYC_STATUS_META).map(([s, m]) => (
            <button key={s} onClick={() => setStatus(s)} className={`pill ${status === s ? "pill-primary" : "pill-outline"} flex items-center gap-1.5`} data-testid={`kyc-filter-${s}`}>
              {m.label}
              <span className="min-w-[18px] h-[18px] px-1 rounded-full grid place-items-center text-[10px] font-bold" style={status === s ? { background: "#fff", color: "var(--yalola-primary)" } : { background: m.bg, color: m.fg }} data-testid={`kyc-count-${s}`}>{counts[s] ?? 0}</span>
            </button>
          ))}
        </div>
        <div className="flex gap-2 flex-wrap items-center">
          <Select value={source} onValueChange={setSource}>
            <SelectTrigger className="h-9 w-56 rounded-full" data-testid="kyc-source-filter"><SelectValue /></SelectTrigger>
            <SelectContent>{KYC_SOURCE_FILTERS.map((o) => <SelectItem key={o.v} value={o.v} data-testid={`kyc-source-${o.v}`}>{o.label}</SelectItem>)}</SelectContent>
          </Select>
          <div className="relative flex-1 min-w-[200px]">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2" style={{ color: "var(--yalola-text-muted)" }} />
            <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Rechercher un nom ou un e-mail…" className="h-9 pl-8 rounded-full" data-testid="kyc-search" />
          </div>
        </div>
        <div className="rounded-xl p-3 text-xs flex items-start gap-2" style={{ background: "var(--yalola-bg)", color: "var(--yalola-text-muted)" }} data-testid="kyc-rules-hint">
          <ShieldCheck size={14} className="shrink-0 mt-0.5" style={{ color: "var(--yalola-primary)" }} />
          <div>
            <b style={{ color: "var(--yalola-text)" }}>Intention d'inscription</b> (gratuite) → Valider active le <b>rôle</b> (Prédicateur, Artiste ou Influenceur : directs, offrandes…) <b>sans badge ✓</b>.
            {" "}<b style={{ color: "var(--yalola-text)" }}>Vérification payante</b> (paiement + preuve) → Valider active le rôle <b>et</b> le badge vérifié ✓ avec ses privilèges étendus.
          </div>
        </div>
        <div className="rounded-xl px-3 py-2.5 text-xs flex items-center justify-between gap-3 flex-wrap" style={{ background: meta.intent_auto_approve ? "#D1FAE5" : "var(--yalola-surface-alt)", color: meta.intent_auto_approve ? "#065F46" : "var(--yalola-text)" }} data-testid="kyc-intent-mode">
          <span>
            Validation du rôle à l'inscription : <b data-testid="kyc-intent-mode-label">{meta.intent_auto_approve ? "AUTOMATIQUE" : "MANUELLE"}</b>
            {meta.intent_auto_approve ? " — les nouveaux Prédicateurs/Artistes/Influenceurs reçoivent leur rôle immédiatement (badge ✓ toujours manuel)." : " — chaque intention attend ta décision ici."}
            {" "}<Link to="/admin?tab=settings&section=registration&focus=settings-kyc-intent-section" className="underline" data-testid="kyc-intent-mode-settings">Modifier dans Paramètres › Inscription</Link>
          </span>
          {meta.pending_intents > 0 && (
            <Button size="sm" variant="outline" className="rounded-full h-8" onClick={approveAllIntents} disabled={!canManage || bulkBusy} data-testid="kyc-approve-all-intents">
              {bulkBusy ? <Loader2 size={14} className="animate-spin" /> : <><Check size={14} className="mr-1" /> Accorder le rôle aux {meta.pending_intents} intention(s) en attente</>}
            </Button>
          )}
        </div>
      </div>
      {items.length === 0 && <div className="yalola-card p-8 text-center text-sm" style={{ color: "var(--yalola-text-muted)" }} data-testid="kyc-empty">Aucune demande {KYC_STATUS_META[status].label.toLowerCase()}{q ? ` pour « ${q} »` : ""}.</div>}
      <div className="space-y-3">
        {kycPager.slice.map((it) => <KycCard key={it.id} it={it} canManage={canManage} onAction={(mode, item) => setTarget({ mode, it: item })} />)}
      </div>
      <PagerBar page={kycPager.page} pages={kycPager.pages} total={kycPager.total} shown={kycPager.slice.length} onPage={kycPager.setPage} noun="demande" testId="kyc-pager" />
      <KycActionDialog target={target} onClose={() => setTarget(null)} onDone={() => { setTarget(null); load(); }} />
    </div>
  );
};

const QUICK_HIDE_LABEL = { story: "la story", post: "la publication", comment: "le commentaire", message: "le message", upload: "le fichier" };
// Blocage entre membres remonté dans la file (routes_blocks.block_user) : pas un contenu, juste un signal.
const isBlockItem = (it) => it.target_type === "user" && it.verdict?.reason === "user_block";

const ModerationPanel = ({ canManage = true, isSuper = false }) => {
  const [items, setItems] = useState([]);
  const [status, setStatus] = useState(() => {
    const s = new URLSearchParams(window.location.search).get("focus_status");
    return ["pending", "approved", "rejected"].includes(s) ? s : "pending";
  });
  const [cfg, setCfg] = useState(null);
  const [atRisk, setAtRisk] = useState(null);
  const [sanctions, setSanctions] = useState(null);
  const [csae, setCsae] = useState(null);
  const [csaeCfg, setCsaeCfg] = useState(null);
  const [csaeLog, setCsaeLog] = useState(null);
  const [logFilter, setLogFilter] = useState("all");
  const [logPeriod, setLogPeriod] = useState("all");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [suspendDur, setSuspendDur] = useState({});
  const [suspendBusy, setSuspendBusy] = useState(null);
  const [warnOpen, setWarnOpen] = useState(null);
  const [warnText, setWarnText] = useState("");
  const [warnBusy, setWarnBusy] = useState(null);
  const [actNotes, setActNotes] = useState({});
  // Pagination (20 par page) + recherche (auteur / signaleur / texte / identifiant) + filtre par type, dans l'onglet courant.
  const [page, setPage] = useState(() => {
    const p = Number(new URLSearchParams(window.location.search).get("focus_page"));
    return p > 0 ? p : 1;
  });
  const [pages, setPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [qInput, setQInput] = useState("");
  const [q, setQ] = useState("");
  const [ttype, setTtype] = useState("");
  const PAGE_SIZE = 20;
  const load = () => api.get("/admin/moderation/queue", { params: { status, page, limit: PAGE_SIZE, q, type: ttype } })
    .then(({ data }) => {
      setItems(data.items || []);
      setTotal(data.total || 0);
      setPages(data.pages || 1);
      if (data.page && data.page !== page) setPage(data.page);   // page clampée côté serveur (dernier élément d'une page traité)
    })
    .catch(() => {});
  const changeStatus = (s) => { setStatus(s); setPage(1); };
  const changeType = (v) => { setTtype(v); setPage(1); };
  const loadCfg = () => api.get("/admin/moderation/settings").then(({ data }) => setCfg(data)).catch(() => {});
  const loadAtRisk = () => api.get("/admin/moderation/at-risk").then(({ data }) => setAtRisk(data)).catch(() => setAtRisk({ threshold: 0, members: [] }));
  const loadSanctions = () => api.get("/admin/moderation/sanctions").then(({ data }) => setSanctions(data.sanctions || [])).catch(() => setSanctions([]));
  const loadCsae = () => api.get("/admin/moderation/csae-stats").then(({ data }) => setCsae(data)).catch(() => setCsae(null));
  const loadCsaeCfg = () => api.get("/admin/moderation/csae-settings").then(({ data }) => setCsaeCfg(data)).catch(() => setCsaeCfg(null));
  const csaeLogQuery = (f, period, from, to) => {
    const fil = f ?? logFilter;
    const per = period ?? logPeriod;
    let qs = `filter=${fil}`;
    if (per === "7d") qs += "&days=7";
    else if (per === "30d") qs += "&days=30";
    else if (per === "custom") {
      const df = from ?? dateFrom, dt = to ?? dateTo;
      if (df) qs += `&date_from=${df}`;
      if (dt) qs += `&date_to=${dt}`;
    }
    return qs;
  };
  const loadCsaeLog = (f, period, from, to) => api.get(`/admin/moderation/csae-log?${csaeLogQuery(f, period, from, to)}`).then(({ data }) => setCsaeLog(data.items || [])).catch(() => setCsaeLog([]));
  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status, page, q, ttype]);
  // « En attente » se rafraîchit toute seule : un signalement traité par un collègue disparaît chez les autres.
  useEffect(() => {
    if (status !== "pending") return undefined;
    const iv = setInterval(load, 45000);
    return () => clearInterval(iv);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status, page, q, ttype]);
  // Recherche : 350 ms après la dernière frappe, retour page 1.
  useEffect(() => {
    const t = setTimeout(() => { const v = qInput.trim(); if (v !== q) { setQ(v); setPage(1); } }, 350);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [qInput]);
  useEffect(() => { loadCfg(); loadAtRisk(); loadSanctions(); loadCsae(); loadCsaeCfg(); loadCsaeLog("all"); }, []); // eslint-disable-line react-hooks/exhaustive-deps
  // Deep link from the moderator notification: focus the reported item once (its tab + page, flash, URL params cleared).
  const focusedId = useModerationFocus({ items, status, setStatus, page, setPage });
  const saveCfg = async () => {
    try { await api.patch("/admin/moderation/settings", cfg); toast.success("Réglages de modération enregistrés"); loadAtRisk(); }
    catch (e) { toast.error(e?.friendlyMessage || "Erreur"); }
  };
  const patchMedia = async (partial) => {
    setCfg((c) => ({ ...c, media: { ...(c?.media || {}), ...partial } }));
    try {
      const { data } = await api.patch("/admin/moderation/settings", {
        auto_block_warnings: cfg?.auto_block_warnings || 0,
        auto_block_reports: cfg?.auto_block_reports || 0,
        media: partial,
      });
      setCfg(data);
      toast.success("Modération des médias enregistrée");
    } catch (e) { toast.error(e?.friendlyMessage || "Erreur"); loadCfg(); }
  };
  const patchCatAction = async (cat, val) => {
    const current = (cfg?.media?.category_actions) || {};
    const next = { ...current, [cat]: val };
    await patchMedia({ category_actions: next });
  };
  const setCsaeToggle = async (key, val) => {
    setCsaeCfg((c) => ({ ...(c || {}), [key]: val }));
    try { const { data } = await api.patch("/admin/moderation/csae-settings", { [key]: val }); setCsaeCfg(data); toast.success("Réglage CSAE enregistré"); }
    catch (e) { toast.error(e?.friendlyMessage || "Erreur"); loadCsaeCfg(); }
  };
  const exportCsaeLog = async (format) => {
    try {
      const res = await api.get(`/admin/moderation/csae-log/export?format=${format}&${csaeLogQuery()}`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = `yalola-csae-log.${format === "pdf" ? "pdf" : "csv"}`;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
      toast.success(`Journal exporté (${format.toUpperCase()})`);
    } catch (e) { toast.error(e?.friendlyMessage || "Export impossible"); }
  };
  const sendCsaeDigest = async () => {
    try {
      const { data } = await api.post("/admin/moderation/csae-digest/send-now");
      if (data.email_configured) toast.success(`Récap envoyé par e-mail à ${data.sent} super-admin(s)`);
      else toast.success(`Récap ajouté aux notifications (e-mail non configuré)`);
    } catch (e) { toast.error(e?.friendlyMessage || "Erreur"); }
  };
  const act = async (id, action) => {
    try {
      await api.post(`/admin/moderation/${id}/action`, { action, note: (actNotes[id] || "").trim() || undefined });
      toast.success(action === "approve" ? "Contenu conservé (jugé conforme)" : "Contenu masqué du profil");
      setActNotes((n) => { const c = { ...n }; delete c[id]; return c; });
      load(); loadCsae(); loadCsaeLog();
      return true;
    } catch (e) {
      toast.error(e?.friendlyMessage || e?.response?.data?.detail || "Erreur");
      if (e?.response?.status === 409) load();   // déjà traité par un collègue → la fiche quitte « En attente »
      return false;
    }
  };
  // Quick hide/unhide: takes the content off the profile NOW without resolving the report (stays pending, silent).
  const [quickBusy, setQuickBusy] = useState(null);
  const quickHide = async (item, hide) => {
    setQuickBusy(item.id);
    try {
      await api.post(`/admin/moderation/${item.id}/quick-hide`, { hide });
      toast.success(hide ? "Contenu masqué du profil (signalement toujours en attente de décision)" : "Contenu réaffiché sur le profil");
      setItems((cur) => cur.map((it) => it.id === item.id ? { ...it, target_is_hidden: hide, quick_hidden: hide } : it));
    } catch (e) {
      toast.error(e?.friendlyMessage || e?.response?.data?.detail || "Erreur");
      if (e?.response?.status === 400) load();   // « déjà traité » par un collègue → rafraîchir
    }
    finally { setQuickBusy(null); }
  };
  const warnAuthor = async (item) => {
    setWarnBusy(item.id);
    try {
      const { data } = await api.post(`/admin/moderation/${item.id}/warn-author`, { message: (warnText || "").trim() });
      toast.success(data?.auto_blocked ? "Auteur averti — compte bloqué (seuil atteint)" : "Auteur averti");
      setWarnOpen(null); setWarnText("");
      load(); loadCsae(); loadCsaeLog();
    } catch (e) { toast.error(e?.friendlyMessage || "Erreur"); }
    finally { setWarnBusy(null); }
  };
  const suspendUser = async (item) => {
    const dur = suspendDur[item.id] || "7d";
    setSuspendBusy(item.id);
    try {
      await api.post(`/admin/moderation/${item.id}/suspend-user`, { duration: dur });
      const label = { "24h": "24 heures", "7d": "7 jours", "30d": "30 jours", "permanent": "définitivement" }[dur] || dur;
      toast.success(`${item.target_user?.name || "Utilisateur"} suspendu (${label})`);
      load(); loadCsae(); loadCsaeLog();
    } catch (e) { toast.error(e?.friendlyMessage || "Erreur"); }
    finally { setSuspendBusy(null); }
  };
  return (
    <div className="space-y-3" data-testid="admin-moderation-panel">
      {csae && (
        <div className="yalola-card p-4" data-testid="mod-csae-stats-card" style={{ borderColor: csae.pending > 0 ? "#DC2626" : "var(--yalola-border)" }}>
          <div className="flex items-center gap-1.5 font-semibold text-sm"><ShieldCheck size={16} style={{ color: "#DC2626" }} /> Sécurité &amp; confiance — Signalements « sécurité des enfants »</div>
          <p className="text-xs mt-0.5 mb-3" style={{ color: "var(--yalola-text-muted)" }}>
            Suivi des signalements CSAE (mise en danger d'un mineur), en priorité absolue. Ces indicateurs attestent d'un traitement rapide et sérieux.
          </p>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2.5">
            <div className="rounded-xl p-3" style={{ background: "var(--yalola-surface-alt)" }} data-testid="mod-csae-pending">
              <div className="text-2xl font-bold" style={{ color: csae.pending > 0 ? "#DC2626" : "inherit" }}>{csae.pending}</div>
              <div className="text-[11px] uppercase tracking-wide" style={{ color: "var(--yalola-text-muted)" }}>En attente</div>
            </div>
            <div className="rounded-xl p-3" style={{ background: "var(--yalola-surface-alt)" }} data-testid="mod-csae-resolved">
              <div className="text-2xl font-bold">{csae.resolved}</div>
              <div className="text-[11px] uppercase tracking-wide" style={{ color: "var(--yalola-text-muted)" }}>Traités</div>
            </div>
            <div className="rounded-xl p-3" style={{ background: "var(--yalola-surface-alt)" }} data-testid="mod-csae-avg">
              <div className="text-2xl font-bold inline-flex items-center gap-1"><Clock size={16} />{csae.avg_resolution_hours != null ? `${csae.avg_resolution_hours} h` : "—"}</div>
              <div className="text-[11px] uppercase tracking-wide" style={{ color: "var(--yalola-text-muted)" }}>Délai moyen</div>
            </div>
            <div className="rounded-xl p-3" style={{ background: "var(--yalola-surface-alt)" }} data-testid="mod-csae-total">
              <div className="text-2xl font-bold">{csae.total}</div>
              <div className="text-[11px] uppercase tracking-wide" style={{ color: "var(--yalola-text-muted)" }}>Total ({csae.last_7d} / 7 j)</div>
            </div>
          </div>
        </div>
      )}
      {isSuper && csaeCfg && (
        <div className="yalola-card p-4" data-testid="mod-csae-toggles-card">
          <div className="flex items-center gap-1.5 font-semibold text-sm"><ShieldCheck size={16} style={{ color: "#DC2626" }} /> Fonctionnalités « sécurité des enfants »</div>
          <p className="text-xs mt-0.5 mb-3" style={{ color: "var(--yalola-text-muted)" }}>
            Active ou désactive les mécanismes CSAE. Recommandé : tout laisser activé pour la conformité Google Play.
          </p>
          <div className="space-y-2.5">
            {[
              { key: "report_option_enabled", label: "Option de signalement « Sécurité des enfants »", hint: "Affiche le motif prioritaire aux membres lors d'un signalement." },
              { key: "auto_hide_enabled", label: "Masquage automatique du contenu", hint: "Masque immédiatement un contenu signalé CSAE, en attente de revue humaine." },
              { key: "moderator_alert_enabled", label: "Alerte instantanée aux modérateurs", hint: "Notifie les modérateurs (push + in-app) à chaque signalement CSAE." },
            ].map((row) => (
              <label key={row.key} className="flex items-start justify-between gap-3 rounded-xl p-2.5" style={{ background: "var(--yalola-surface-alt)" }}>
                <span className="min-w-0">
                  <span className="block text-sm font-medium">{row.label}</span>
                  <span className="block text-[11px]" style={{ color: "var(--yalola-text-muted)" }}>{row.hint}</span>
                </span>
                <Switch
                  checked={csaeCfg[row.key] !== false}
                  onCheckedChange={(v) => setCsaeToggle(row.key, v)}
                  disabled={!canManage}
                  data-testid={`csae-toggle-${row.key}`}
                />
              </label>
            ))}
          </div>
          <div className="mt-3 flex items-center justify-between gap-2 flex-wrap border-t pt-3" style={{ borderColor: "var(--yalola-border)" }}>
            <span className="text-xs" style={{ color: "var(--yalola-text-muted)" }}>Récap sécurité envoyé automatiquement aux super-admins chaque lundi.</span>
            <Button size="sm" variant="outline" className="rounded-full" onClick={sendCsaeDigest} disabled={!canManage} data-testid="csae-digest-send-now"><Mail size={13} className="mr-1" /> Envoyer le récap maintenant</Button>
          </div>
        </div>
      )}
      {isSuper && csaeLog && (
        <details className="yalola-card p-4" data-testid="mod-csae-log-card">
          <summary className="cursor-pointer select-none font-semibold text-sm flex items-center gap-1.5">
            <FileText size={16} style={{ color: "#DC2626" }} /> Journal des actions CSAE
            <span className="ml-1 text-xs font-normal" style={{ color: "var(--yalola-text-muted)" }}>({csaeLog.length})</span>
          </summary>
          <p className="text-xs mt-1 mb-2" style={{ color: "var(--yalola-text-muted)" }}>
            Historique des signalements « sécurité des enfants » traités (suspensions, rejets, approbations) — pour tes rapports de conformité.
          </p>
          <div className="flex gap-2 mb-2 flex-wrap items-center">
            {[["all", "Tous"], ["suspension", "Suspensions"], ["rejet", "Rejets"], ["approbation", "Approbations"]].map(([f, lbl]) => (
              <button key={f} onClick={() => { setLogFilter(f); loadCsaeLog(f); }} className={`pill ${logFilter === f ? "pill-primary" : "pill-outline"}`} data-testid={`csae-log-filter-${f}`}>{lbl}</button>
            ))}
            <div className="ml-auto flex gap-2">
              <Button size="sm" variant="outline" className="rounded-full" onClick={() => exportCsaeLog("csv")} data-testid="csae-log-export-csv"><Download size={13} className="mr-1" /> CSV</Button>
              <Button size="sm" variant="outline" className="rounded-full" onClick={() => exportCsaeLog("pdf")} data-testid="csae-log-export-pdf"><Download size={13} className="mr-1" /> PDF</Button>
            </div>
          </div>
          <div className="flex gap-2 mb-3 flex-wrap items-center">
            <span className="text-xs font-semibold flex items-center gap-1" style={{ color: "var(--yalola-text-muted)" }}><CalendarDays size={13} /> Période :</span>
            {[["all", "Tout"], ["7d", "7 jours"], ["30d", "30 jours"], ["custom", "Personnalisé"]].map(([p, lbl]) => (
              <button key={p} onClick={() => { setLogPeriod(p); if (p !== "custom") loadCsaeLog(undefined, p); }} className={`pill ${logPeriod === p ? "pill-primary" : "pill-outline"}`} data-testid={`csae-log-period-${p}`}>{lbl}</button>
            ))}
            {logPeriod === "custom" && (
              <div className="flex items-center gap-1.5 flex-wrap" data-testid="csae-log-custom-range">
                <input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} className="rounded-lg border px-2 py-1 text-xs" style={{ borderColor: "var(--yalola-border)" }} data-testid="csae-log-date-from" />
                <span className="text-xs" style={{ color: "var(--yalola-text-muted)" }}>→</span>
                <input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} className="rounded-lg border px-2 py-1 text-xs" style={{ borderColor: "var(--yalola-border)" }} data-testid="csae-log-date-to" />
                <Button size="sm" className="rounded-full pill-primary" onClick={() => loadCsaeLog(undefined, "custom", dateFrom, dateTo)} data-testid="csae-log-apply-range">Appliquer</Button>
              </div>
            )}
          </div>
          {csaeLog.length === 0 ? (
            <div className="text-xs py-3 text-center" style={{ color: "var(--yalola-text-muted)" }} data-testid="mod-csae-log-empty">Aucune action enregistrée pour ce filtre.</div>
          ) : (
            <div className="space-y-1.5 max-h-96 overflow-y-auto pr-1">
              {csaeLog.map((it) => {
                const meta = { suspension: ["Suspension", "#DC2626"], rejet: ["Rejeté (masqué)", "#B45309"], approbation: ["Approuvé (rétabli)", "#2D9E68"] }[it.action] || [it.action, "var(--yalola-text-muted)"];
                const typeLabel = { user: "Profil : ", post: "Publication : ", comment: "Commentaire : ", story: "Story : ", message: "Message privé : " }[it.target_type] || "";
                const targetName = it.target_type === "user"
                  ? (it.target_user?.name || it.target_user?.email || it.target_id?.slice(0, 8))
                  : (it.target_snippet || `${it.target_id?.slice(0, 8)}`);
                return (
                  <div key={it.id} className="flex items-start gap-2.5 rounded-xl p-2.5 text-xs" style={{ background: "var(--yalola-surface-alt)" }} data-testid={`csae-log-item-${it.id}`}>
                    <span className="mt-0.5 shrink-0 rounded-full px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-white" style={{ background: meta[1] }}>{meta[0]}</span>
                    <div className="min-w-0 flex-1">
                      <div className="font-semibold truncate">{typeLabel}{targetName}</div>
                      {it.reason && <div className="mt-0.5" style={{ color: "var(--yalola-text-muted)" }}>Motif : {it.reason}</div>}
                      {it.note && <div className="mt-0.5" style={{ color: "var(--yalola-text-muted)" }}>{it.note}</div>}
                      <div className="mt-0.5 flex flex-wrap gap-x-2 gap-y-0.5" style={{ color: "var(--yalola-text-muted)" }}>
                        {it.resolver_name && <span>par {it.resolver_name}</span>}
                        {it.resolved_at && <span>· {new Date(it.resolved_at).toLocaleString("fr-FR")}</span>}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </details>
      )}
      {isSuper && cfg && (
        <div className="yalola-card p-4" data-testid="mod-autoblock-card">
          <div className="flex items-center gap-1.5 font-semibold text-sm"><ShieldAlert size={16} style={{ color: "#B45309" }} /> Blocage automatique</div>
          <p className="text-xs mt-0.5 mb-3" style={{ color: "var(--yalola-text-muted)" }}>Bloque automatiquement un compte au-delà d'un nombre d'avertissements. Mettre 0 pour désactiver.</p>
          <div className="flex items-end gap-3 flex-wrap">
            <div>
              <Label className="text-xs">Seuil d'avertissements</Label>
              <Input type="number" min={0} className="w-28" value={cfg.auto_block_warnings}
                onChange={(e) => setCfg({ ...cfg, auto_block_warnings: Math.max(0, parseInt(e.target.value || "0", 10)) })}
                disabled={!canManage} data-testid="mod-autoblock-warnings" />
            </div>
            <Button className="pill-primary rounded-full" onClick={saveCfg} disabled={!canManage} data-testid="mod-autoblock-save">Enregistrer</Button>
            <span className="text-xs" style={{ color: "var(--yalola-text-muted)" }}>
              {cfg.auto_block_warnings > 0 ? `Actif : blocage après ${cfg.auto_block_warnings} avertissement(s).` : "Désactivé."}
            </span>
          </div>
        </div>
      )}
      {isSuper && cfg && (
        <div className="yalola-card p-4" data-testid="mod-queue-alert-card">
          <div className="flex items-center gap-1.5 font-semibold text-sm"><ShieldAlert size={16} style={{ color: "#B45309" }} /> Alerte file de modération</div>
          <p className="text-xs mt-0.5 mb-3" style={{ color: "var(--yalola-text-muted)" }}>
            Prévient tous les modérateurs (notification) quand la file d'attente dépasse ce nombre de contenus en attente. Vérification toutes les 5 min, rappel toutes les 6 h tant que la file reste chargée. Mettre 0 pour désactiver.
          </p>
          <div className="flex items-end gap-3 flex-wrap">
            <div>
              <Label className="text-xs">Seuil de contenus en attente</Label>
              <Input type="number" min={0} className="w-28" value={cfg.queue_alert_threshold ?? 10}
                onChange={(e) => setCfg({ ...cfg, queue_alert_threshold: Math.max(0, parseInt(e.target.value || "0", 10)) })}
                disabled={!canManage} data-testid="mod-queue-alert-threshold" />
            </div>
            <Button className="pill-primary rounded-full" onClick={saveCfg} disabled={!canManage} data-testid="mod-queue-alert-save">Enregistrer</Button>
            <span className="text-xs" style={{ color: "var(--yalola-text-muted)" }}>
              {(cfg.queue_alert_threshold ?? 10) > 0 ? `Actif : alerte dès ${cfg.queue_alert_threshold ?? 10} contenu(s) en attente.` : "Désactivé."}
            </span>
          </div>
        </div>
      )}
      {isSuper && cfg && cfg.media && (
        <div className="yalola-card p-4" data-testid="mod-media-card">
          <div className="flex items-center gap-1.5 font-semibold text-sm"><ShieldAlert size={16} style={{ color: "#B45309" }} /> Modération des médias (anti-nudité)</div>
          <p className="text-xs mt-0.5 mb-3" style={{ color: "var(--yalola-text-muted)" }}>
            Analyse automatique par IA des images et vidéos à la publication. Le contenu explicite/dénudé est refusé ou masqué selon le niveau choisi.
          </p>
          <div className="space-y-3">
            <div className="flex items-center justify-between gap-3 rounded-xl p-3" style={{ background: "var(--yalola-surface-alt)" }}>
              <div className="min-w-0">
                <div className="text-sm font-medium">Modération automatique activée</div>
                <div className="text-xs" style={{ color: "var(--yalola-text-muted)" }}>Interrupteur principal. S'il est désactivé, aucune analyse média n'est faite.</div>
              </div>
              <Switch checked={!!cfg.media.enabled} disabled={!canManage}
                onCheckedChange={(v) => patchMedia({ enabled: v })} data-testid="mod-media-enabled" />
            </div>
            <div>
              <Label className="text-xs">Niveau de blocage (refus à la publication)</Label>
              <Select value={cfg.media.block_level || "explicit"} disabled={!canManage || !cfg.media.enabled}
                onValueChange={(v) => patchMedia({ block_level: v })}>
                <SelectTrigger className="mt-1" data-testid="mod-media-block-level"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="explicit" data-testid="mod-media-block-explicit">Bloquer uniquement le contenu explicite (nudité/sexe/gore avérés) — recommandé</SelectItem>
                  <SelectItem value="borderline" data-testid="mod-media-block-borderline">Bloquer aussi le contenu limite (nudité partielle, très suggestif)</SelectItem>
                  <SelectItem value="off" data-testid="mod-media-block-off">Ne jamais refuser (seulement masquer + mettre en file de modération)</SelectItem>
                </SelectContent>
              </Select>
              <p className="text-xs mt-1" style={{ color: "var(--yalola-text-muted)" }}>
                Concerne la <b>nudité / le contenu sexuel</b> (et tout contenu jugé explicite). La violence, l'alcool/drogue, le gore et les symboles haineux se règlent séparément ci-dessous.
              </p>
            </div>
            <div>
              <Label className="text-xs">Nudité / suggestif — cas limites (verdict « borderline »)</Label>
              <Select value={cfg.media.sexual_borderline_action || "review"} disabled={!canManage || !cfg.media.enabled}
                onValueChange={(v) => patchMedia({ sexual_borderline_action: v })}>
                <SelectTrigger className="mt-1" data-testid="mod-media-sexual-borderline"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="review" data-testid="mod-media-sexual-borderline-review">File de modération (masqué + validation humaine)</SelectItem>
                  <SelectItem value="allow" data-testid="mod-media-sexual-borderline-allow">Publier (seuls les cas avérés suivent le niveau de blocage)</SelectItem>
                  <SelectItem value="block" data-testid="mod-media-sexual-borderline-block">Bloquer (refusé à la publication)</SelectItem>
                </SelectContent>
              </Select>
              <p className="text-xs mt-1" style={{ color: "var(--yalola-text-muted)" }}>
                Que faire quand l'IA <b>hésite</b> (cas douteux) sur l'axe nudité/suggestif. Le contenu explicite avéré suit toujours le « Niveau de blocage » ci-dessus.
              </p>
            </div>
            <div>
              <Label className="text-xs">Tenues légères (maillot de bain, sous-vêtements, torse nu, vêtements transparents — cas évidents uniquement, doute = sûr) — sans nudité</Label>
              <Select value={cfg.media.revealing_action || "review"} disabled={!canManage || !cfg.media.enabled}
                onValueChange={(v) => patchMedia({ revealing_action: v })}>
                <SelectTrigger className="mt-1" data-testid="mod-media-revealing"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="review" data-testid="mod-media-revealing-review">File de modération (masqué + validation humaine) — recommandé</SelectItem>
                  <SelectItem value="block" data-testid="mod-media-revealing-block">Bloquer (refusé à la publication, message d'excuse à l'auteur)</SelectItem>
                  <SelectItem value="allow" data-testid="mod-media-revealing-allow">Autoriser (publié normalement)</SelectItem>
                </SelectContent>
              </Select>
              <p className="text-xs mt-1" style={{ color: "var(--yalola-text-muted)" }}>
                Photos et vidéos réelles de personnes en maillot de bain, sous-vêtements, torse nu ou tenue très courte/transparente, même dans un contexte innocent (plage, piscine, sport). Les œuvres religieuses (crucifixion, statues, peintures), les bébés et l'imagerie médicale ne sont pas concernés. L'auteur reçoit un message clair (« Nous sommes désolés… »).
              </p>
            </div>
            <div className="rounded-xl p-3 space-y-3" style={{ background: "var(--yalola-surface-alt)" }} data-testid="mod-media-category-actions">
              <div>
                <div className="text-sm font-medium">Violence, alcool/drogue, gore & symboles haineux (hors nudité)</div>
                <div className="text-xs" style={{ color: "var(--yalola-text-muted)" }}>
                  Que faire quand l'IA détecte ces contenus (ex. dessins animés avec armes). « Autoriser » = publié normalement ; « File si avéré » = les cas limites/douteux sont publiés, seuls les cas avérés sont masqués pour validation humaine ; « File de modération » = tout cas (même douteux) masqué et envoyé pour validation ; « Bloquer » = refusé à la publication.
                </div>
              </div>
              {[
                { k: "violence", label: "Violence (armes, bagarres, scènes violentes)" },
                { k: "drugs", label: "Alcool / drogue" },
                { k: "gore", label: "Gore (sang, blessures choquantes)" },
                { k: "hate", label: "Symboles haineux" },
              ].map((row) => (
                <div key={row.k} className="flex items-center justify-between gap-3">
                  <div className="text-sm min-w-0">{row.label}</div>
                  <Select value={(cfg.media.category_actions || {})[row.k] || "review"}
                    disabled={!canManage || !cfg.media.enabled}
                    onValueChange={(v) => patchCatAction(row.k, v)}>
                    <SelectTrigger className="w-52 shrink-0" data-testid={`mod-media-cat-${row.k}`}><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="allow" data-testid={`mod-media-cat-${row.k}-allow`}>Autoriser</SelectItem>
                      <SelectItem value="review_explicit" data-testid={`mod-media-cat-${row.k}-review-explicit`}>File uniquement si avéré (les cas limites passent)</SelectItem>
                      <SelectItem value="review" data-testid={`mod-media-cat-${row.k}-review`}>File de modération (tout cas, même douteux)</SelectItem>
                      <SelectItem value="block" data-testid={`mod-media-cat-${row.k}-block`}>Bloquer</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              ))}
            </div>
            {[
              { k: "moderate_images", label: "Images (upload standard)", desc: "Photos publiées via le composeur (≤ limite d'upload)." },
              { k: "moderate_direct_images", label: "Grandes images / upload direct", desc: "Images volumineuses envoyées directement au stockage (R2)." },
              { k: "moderate_videos", label: "Vidéos", desc: "Échantillonne plusieurs images de la vidéo et les analyse." },
              { k: "moderate_gifs", label: "GIF", desc: "Analyse la première image du GIF." },
            ].map((row) => (
              <div key={row.k} className="flex items-center justify-between gap-3 rounded-xl p-3" style={{ background: "var(--yalola-surface-alt)" }}>
                <div className="min-w-0">
                  <div className="text-sm font-medium">{row.label}</div>
                  <div className="text-xs" style={{ color: "var(--yalola-text-muted)" }}>{row.desc}</div>
                </div>
                <Switch checked={!!cfg.media[row.k]} disabled={!canManage || !cfg.media.enabled}
                  onCheckedChange={(v) => patchMedia({ [row.k]: v })} data-testid={`mod-media-${row.k}`} />
              </div>
            ))}
            <div className="flex items-end gap-3 flex-wrap">
              <div>
                <Label className="text-xs">Images analysées par vidéo</Label>
                <Input type="number" min={1} max={8} className="w-24" value={cfg.media.video_frames || 4}
                  disabled={!canManage || !cfg.media.enabled}
                  onChange={(e) => setCfg((c) => ({ ...c, media: { ...c.media, video_frames: Math.max(1, Math.min(8, parseInt(e.target.value || "4", 10))) } }))}
                  onBlur={(e) => patchMedia({ video_frames: Math.max(1, Math.min(8, parseInt(e.target.value || "4", 10))) })}
                  data-testid="mod-media-video-frames" />
              </div>
              <span className="text-xs pb-2" style={{ color: "var(--yalola-text-muted)" }}>Plus d'images = détection plus fiable, mais analyse plus longue.</span>
            </div>
          </div>
        </div>
      )}
      {isSuper && atRisk && atRisk.threshold > 1 && (
        <div className="yalola-card p-4" data-testid="mod-atrisk-card">
          <div className="flex items-center gap-1.5 font-semibold text-sm"><ShieldAlert size={16} style={{ color: "#B45309" }} /> Membres proches du blocage automatique</div>
          <p className="text-xs mt-0.5 mb-3" style={{ color: "var(--yalola-text-muted)" }}>
            Comptes actifs ayant atteint au moins {atRisk.threshold - 1} avertissement(s) sur {atRisk.threshold}. Un avertissement de plus entraîne le blocage automatique.
          </p>
          {(!atRisk.members || atRisk.members.length === 0) ? (
            <div className="text-xs py-3 text-center" style={{ color: "var(--yalola-text-muted)" }} data-testid="mod-atrisk-empty">Aucun membre proche du blocage pour l'instant. 🙏</div>
          ) : (
            <div className="space-y-2">
              {atRisk.members.map((m) => (
                <div key={m.id} className="flex items-center justify-between gap-3 rounded-xl p-2.5"
                     style={{ background: "var(--yalola-surface-alt)" }} data-testid={`mod-atrisk-${m.id}`}>
                  <div className="flex items-center gap-2.5 min-w-0">
                    {m.avatar_url
                      ? <img src={imageVariant(m.avatar_url, 32)} alt="" className="h-8 w-8 rounded-full object-cover shrink-0" />
                      : <div className="h-8 w-8 rounded-full grid place-items-center text-xs font-bold text-white shrink-0" style={{ background: "var(--yalola-primary)" }}>{(m.name || "?")[0]}</div>}
                    <div className="min-w-0">
                      <div className="text-sm font-semibold truncate">{m.name || m.email}</div>
                      <div className="text-[11px] truncate" style={{ color: "var(--yalola-text-muted)" }}>{m.email}</div>
                    </div>
                  </div>
                  <div className="text-right shrink-0">
                    <div className="text-sm font-bold" style={{ color: "#B45309" }}>{m.warning_count} / {m.threshold}</div>
                    <div className="text-[10px] font-semibold uppercase tracking-wide" style={{ color: m.remaining <= 1 ? "#B45309" : "var(--yalola-text-muted)" }}>
                      {m.remaining <= 0 ? "Seuil atteint" : m.remaining === 1 ? "Encore 1 avant blocage" : `Encore ${m.remaining} avant blocage`}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
      {sanctions && (
        <details className="yalola-card p-4" data-testid="mod-sanctions-card">
          <summary className="cursor-pointer select-none font-semibold text-sm flex items-center gap-1.5">
            <FileText size={16} style={{ color: "var(--yalola-primary)" }} /> Historique des sanctions
            <span className="ml-1 text-xs font-normal" style={{ color: "var(--yalola-text-muted)" }}>({sanctions.length})</span>
          </summary>
          <p className="text-xs mt-1 mb-2" style={{ color: "var(--yalola-text-muted)" }}>
            Avertissements, suspensions, blocages automatiques et réactivations, avec leur motif et l'administrateur responsable.
          </p>
          {sanctions.length === 0 ? (
            <div className="text-xs py-3 text-center" style={{ color: "var(--yalola-text-muted)" }} data-testid="mod-sanctions-empty">Aucune sanction enregistrée.</div>
          ) : (
            <div className="space-y-1.5 max-h-96 overflow-y-auto pr-1">
              {sanctions.map((s, idx) => {
                const meta = { warning: ["Avertissement", "#B45309"], suspend: ["Suspension", "#DC2626"], autoblock: ["Blocage auto", "#DC2626"], reactivate: ["Réactivation", "#2D9E68"] }[s.type] || [s.type, "var(--yalola-text-muted)"];
                return (
                  <div key={idx} className="flex items-start gap-2.5 rounded-xl p-2.5 text-xs" style={{ background: "var(--yalola-surface-alt)" }} data-testid={`mod-sanction-${idx}`}>
                    <span className="mt-0.5 shrink-0 rounded-full px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-white" style={{ background: meta[1] }}>{meta[0]}</span>
                    <div className="min-w-0 flex-1">
                      <div className="font-semibold truncate">{s.user?.name || s.user?.email || s.user_id || "—"}</div>
                      {(s.message || s.reason) && <div className="mt-0.5" style={{ color: "var(--yalola-text-muted)" }}>{s.message || s.reason}</div>}
                      <div className="mt-0.5 flex flex-wrap gap-x-2 gap-y-0.5" style={{ color: "var(--yalola-text-muted)" }}>
                        {s.duration && <span>Durée : {s.duration}</span>}
                        {s.warnings != null && <span>{s.warnings} avertissement(s)</span>}
                        {s.admin_name && <span>par {s.admin_name}</span>}
                        {s.date && <span>· {new Date(s.date).toLocaleString("fr-FR")}</span>}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </details>
      )}
      <div className="yalola-card p-4 flex flex-wrap items-center gap-2" data-testid="mod-toolbar">
        {[["pending", "En attente"], ["approved", "Gardés"], ["rejected", "Masqués"]].map(([s, lbl]) => (
          <button key={s} onClick={() => changeStatus(s)} className={`pill ${status === s ? "pill-primary" : "pill-outline"}`} data-testid={`mod-filter-${s}`}>{lbl}</button>
        ))}
        <div className="relative flex-1 min-w-[220px]">
          <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2" style={{ color: "var(--yalola-text-muted)" }} />
          <Input value={qInput} onChange={(e) => setQInput(e.target.value)} className="pl-9 pr-9" placeholder="Rechercher : auteur, signaleur, texte, identifiant…" aria-label="Rechercher dans les signalements" data-testid="mod-search-input" />
          {qInput && (
            <button type="button" onClick={() => setQInput("")} className="absolute right-2 top-1/2 -translate-y-1/2 h-6 w-6 rounded-full grid place-items-center hover:bg-black/5" aria-label="Effacer la recherche" data-testid="mod-search-clear"><X size={14} /></button>
          )}
        </div>
        <select value={ttype} onChange={(e) => changeType(e.target.value)} className="h-10 rounded-lg border bg-white px-3 text-sm" aria-label="Type de contenu" data-testid="mod-type-filter">
          {[["", "Tous les types"], ["post", "Publications"], ["story", "Stories"], ["comment", "Commentaires"], ["user", "Profils"], ["message", "Messages"], ["upload", "Fichiers"]].map(([v, l]) => <option key={v} value={v}>{l}</option>)}
        </select>
        <span className="text-xs font-semibold whitespace-nowrap" style={{ color: "var(--yalola-text-muted)" }} data-testid="mod-count">
          {total ? `${(page - 1) * PAGE_SIZE + 1}‑${Math.min(page * PAGE_SIZE, total)} sur ${total}` : "0 résultat"}
        </span>
      </div>
      {items.length === 0 && (
        <div className="yalola-card p-8 text-center" data-testid="mod-empty">
          {q || ttype ? <>Aucun signalement ne correspond{q ? <> à « <b>{q}</b> »</> : ""}{ttype ? " pour ce type de contenu" : ""}.</> : "Aucun élément à modérer."}
        </div>
      )}
      <div className="grid md:grid-cols-2 gap-4">
        {items.map(it => (
          <div key={it.id} className={`yalola-card p-4 transition-shadow${focusedId === it.id ? " mod-focus-flash" : ""}`} data-testid={`mod-item-${it.id}`} style={{ ...(it.priority === "critical" ? { borderColor: "#DC2626", borderWidth: 2 } : {}) }}>
            {(it.priority === "critical" || it.category === "child_safety") && (
              <div className="mb-2 inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-[10px] font-bold uppercase tracking-wide text-white" style={{ background: "#DC2626" }} data-testid={`mod-csae-badge-${it.id}`}>
                <ShieldAlert size={12} /> Sécurité enfant · Prioritaire
              </div>
            )}
            {it.is_appeal && (
              <div className="mb-2 inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-[10px] font-bold uppercase tracking-wide text-white" style={{ background: "#2563EB" }} data-testid={`mod-appeal-badge-${it.id}`}>
                <Scale size={12} /> Appel du membre · Demande de révision
              </div>
            )}
            <div className="text-xs" style={{ color: "var(--yalola-text-muted)" }} data-testid={`mod-kind-${it.id}`}>{isBlockItem(it) ? "Blocage entre membres · profil" : it.target_type} · {it.target_id.slice(0, 8)}</div>
            {it.target_type === "user" && it.target_user && (
              <a href={`/profile/${it.target_user.id}`} target="_blank" rel="noreferrer" className="mt-1.5 flex items-center gap-2 rounded-xl p-2 hover:bg-black/5 transition-colors" data-testid={`mod-user-${it.id}`}>
                {it.target_user.avatar_url
                  ? <img src={imageVariant(it.target_user.avatar_url, 32)} alt="" className="h-8 w-8 rounded-full object-cover shrink-0" />
                  : <div className="h-8 w-8 rounded-full grid place-items-center text-xs font-bold text-white shrink-0" style={{ background: "var(--yalola-primary)" }}>{(it.target_user.name || "?")[0]}</div>}
                <div className="min-w-0">
                  <div className="text-sm font-semibold truncate">{it.target_user.name || it.target_user.email}</div>
                  <div className="text-[11px] truncate" style={{ color: "var(--yalola-primary)" }}>Voir le profil signalé →</div>
                </div>
              </a>
            )}
            {it.target_type === "comment" && it.target_snippet && (
              <div className="mt-1.5 rounded-xl p-2 text-xs italic" style={{ background: "var(--yalola-surface-alt)" }} data-testid={`mod-comment-${it.id}`}>
                « {it.target_snippet} »{it.target_post_id && <a href={`/post/${it.target_post_id}`} target="_blank" rel="noreferrer" className="ml-1 not-italic" style={{ color: "var(--yalola-primary)" }}>· voir</a>}
              </div>
            )}
            {it.target_type === "post" && (
              <a href={`/post/${it.target_id}`} target="_blank" rel="noreferrer" className="mt-1.5 flex gap-2 rounded-xl p-2 hover:bg-black/5 transition-colors" data-testid={`mod-post-${it.id}`}>
                {it.target_thumb
                  ? (it.target_media_type === "video"
                      ? <div className="relative h-16 w-16 shrink-0 rounded-lg overflow-hidden bg-black grid place-items-center"><video src={resolveMediaUrl(it.target_thumb)} muted playsInline preload="metadata" className="h-full w-full object-cover" /><Play size={18} className="absolute text-white" fill="white" /></div>
                      : <img src={resolveMediaUrl(it.target_thumb)} alt="" className="h-16 w-16 shrink-0 rounded-lg object-cover" />)
                  : <div className="h-16 w-16 shrink-0 rounded-lg grid place-items-center text-white text-lg font-bold" style={{ background: "var(--yalola-primary)" }}>{it.target_media_type === "audio" ? "♪" : "T"}</div>}
                <div className="min-w-0 flex-1">
                  {it.target_user?.name && <div className="text-sm font-semibold truncate">{it.target_user.name}</div>}
                  <div className="text-xs line-clamp-2" style={{ color: "var(--yalola-text-muted)" }}>{it.target_deleted ? "⚠️ Publication supprimée" : (it.target_snippet || `Publication ${it.target_media_type || ""}`)}</div>
                  <div className="text-[11px] mt-0.5 font-medium" style={{ color: "var(--yalola-primary)" }}>{it.target_is_hidden && <span className="mr-1">🚫 Déjà masquée · </span>}Voir le contenu →</div>
                </div>
              </a>
            )}
            {it.target_type === "story" && <ModerationStoryPreview item={it} />}
            {it.target_type === "upload" && it.prayer_id && (
              // Lot 657 — prière vocale signalée par l'auteur de la demande urgente
              <div className="mt-1.5 rounded-xl p-2 text-xs" style={{ background: "var(--yalola-surface-alt)" }} data-testid={`mod-prayer-voice-${it.id}`}>
                <span className="font-semibold">🎙️ Prière vocale (chaîne de prière)</span>
                {it.target_user?.name && <span> — par <a href={`/profile/${it.target_user.id}`} target="_blank" rel="noreferrer" className="font-bold underline" data-testid={`mod-prayer-voice-author-${it.id}`}>{it.target_user.name}</a></span>}
                {it.target_deleted && <span className="ml-1">⚠️ fichier supprimé</span>}
                {it.target_is_hidden && !it.target_deleted && <span className="ml-1">🚫 déjà masquée</span>}
                <div className="mt-0.5" style={{ color: "var(--yalola-text-muted)" }}>{it.target_snippet}</div>
                <a href={`/prayers?focus=${it.prayer_id}`} target="_blank" rel="noreferrer" className="mt-0.5 inline-block font-medium" style={{ color: "var(--yalola-primary)" }} data-testid={`mod-prayer-voice-link-${it.id}`}>Voir la demande de prière →</a>
              </div>
            )}
            {it.target_type === "message" && (
              <div className="mt-1.5 rounded-xl p-2 text-xs" style={{ background: "var(--yalola-surface-alt)" }} data-testid={`mod-content-${it.id}`}>
                {it.target_user?.name && <span className="font-semibold">Expéditeur : {it.target_user.name} — </span>}
                <span className="italic">« {it.target_snippet || "Message"} »</span>
              </div>
            )}
            {it.target_type === "message" && <ModerationMessageContext itemId={it.id} messageId={it.target_id} />}
            {isBlockItem(it) ? <ModerationBlockBanner item={it} /> : <ModerationReason item={it} />}
            <ModerationReporter item={it} />
            <ModerationMedia item={it} canManage={canManage} status={status} note={actNotes[it.id] || ""}
              onNoteChange={(v) => setActNotes((n) => ({ ...n, [it.id]: v }))} onAct={(a) => act(it.id, a)}
              onItemPatched={(patch) => setItems((cur) => cur.map((x) => (x.id === it.id ? { ...x, ...patch } : x)))} />
            <div className="mt-2 text-xs" data-testid={`mod-verdict-${it.id}`}>
              {isBlockItem(it)
                ? <><b>Analyse IA :</b> <span style={{ color: "var(--yalola-text-muted)" }}>non applicable (blocage manuel entre membres)</span></>
                : <><b>Verdict IA :</b> {it.verdict?.level ? `${it.verdict.level} — ` : ""}{it.verdict?.reason || "—"}</>}
              {it.verdict?.focus_label && <span className="ml-1 inline-flex items-center gap-1 font-semibold" style={{ color: "var(--yalola-primary)" }} data-testid={`mod-focus-label-${it.id}`}><Crosshair size={11} /> {it.verdict.focus_label}</span>}
              <div className="flex flex-wrap gap-1 mt-1">
                {(it.verdict?.categories || []).map(c => <span key={c} className="chip text-[10px]">{c}</span>)}
              </div>
            </div>
            <ModerationDecision item={it} isSuper={isSuper} onDeleted={() => { load(); loadCsae(); loadCsaeLog(); }} />
            {status === "pending" && (
              <>
                <Input value={actNotes[it.id] || ""} onChange={(e) => setActNotes((n) => ({ ...n, [it.id]: e.target.value }))} placeholder="Note interne (optionnelle)…" className="mt-3 text-sm h-9" data-testid={`mod-note-${it.id}`} />
                <div className="flex gap-2 mt-2 flex-wrap">
                  <Button size="sm" className="pill-primary rounded-full" onClick={() => act(it.id, "approve")} disabled={!canManage} data-testid={`mod-approve-${it.id}`}><Check size={14} className="mr-1" /> {isBlockItem(it) ? "Classer sans suite" : "Garder le contenu"}</Button>
                  {!isBlockItem(it) && (
                    <Button size="sm" variant="outline" className="rounded-full" style={{ color: "#B91C1C", borderColor: "#FCA5A5" }} onClick={() => act(it.id, "reject")} disabled={!canManage} data-testid={`mod-reject-${it.id}`}><EyeOff size={14} className="mr-1" /> Masquer le contenu</Button>
                  )}
                  {it.author_warned ? (
                    <span className="pill pill-outline text-xs flex items-center" style={{ color: "#B45309" }} data-testid={`mod-warned-${it.id}`}><AlertTriangle size={12} className="mr-1" /> {isBlockItem(it) ? "Membre averti" : "Auteur averti"}</span>
                  ) : (
                    <Button size="sm" variant="outline" className="rounded-full" style={{ color: "#B45309", borderColor: "#FCD34D" }} onClick={() => { setWarnOpen(warnOpen === it.id ? null : it.id); setWarnText(""); }} disabled={!canManage} data-testid={`mod-warn-toggle-${it.id}`}><AlertTriangle size={14} className="mr-1" /> {isBlockItem(it) ? "Avertir le membre" : "Avertir l'auteur"}</Button>
                  )}
                </div>
                {QUICK_HIDE_LABEL[it.target_type] && !it.target_deleted && (
                  <div className="mt-2 flex items-center gap-2 flex-wrap rounded-xl p-2" style={{ background: "var(--yalola-surface-alt)" }} data-testid={`mod-quickhide-row-${it.id}`}>
                    {it.target_is_hidden ? (
                      <>
                        <span className="text-[11px] font-semibold inline-flex items-center gap-1" style={{ color: "#B45309" }} data-testid={`mod-hidden-chip-${it.id}`}><EyeOff size={12} /> Masquée du profil · décision en attente</span>
                        <Button size="sm" variant="outline" className="rounded-full ml-auto" onClick={() => quickHide(it, false)} disabled={!canManage || quickBusy === it.id} data-testid={`mod-unhide-${it.id}`}>{quickBusy === it.id ? <Loader2 size={14} className="animate-spin" /> : <><Eye size={14} className="mr-1" /> Réafficher</>}</Button>
                      </>
                    ) : (
                      <>
                        <span className="text-[11px]" style={{ color: "var(--yalola-text-muted)" }}>Retirer du profil sans clore le signalement :</span>
                        <Button size="sm" variant="outline" className="rounded-full ml-auto" style={{ color: "#B91C1C", borderColor: "#FCA5A5" }} onClick={() => quickHide(it, true)} disabled={!canManage || quickBusy === it.id} data-testid={`mod-quickhide-${it.id}`}>{quickBusy === it.id ? <Loader2 size={14} className="animate-spin" /> : <><EyeOff size={14} className="mr-1" /> Masquer {QUICK_HIDE_LABEL[it.target_type]} maintenant</>}</Button>
                      </>
                    )}
                  </div>
                )}
                <div className="mt-2 text-[11px]" style={{ color: "var(--yalola-text-muted)" }} data-testid={`mod-help-${it.id}`}>
                  {isBlockItem(it)
                    ? <><b>Classer</b> = rien à faire, le blocage est archivé (il reste compté). Pour sanctionner le profil bloqué : <b>Avertir</b> ou <b>Suspendre</b> ci-dessous.</>
                    : <><b>Garder</b> = le contenu est conforme et reste visible. <b>Masquer</b> = le contenu est retiré du profil.</>}
                </div>
                {warnOpen === it.id && (
                  <div className="mt-2 rounded-xl p-2 space-y-2" style={{ background: "var(--yalola-surface-alt)" }} data-testid={`mod-warn-form-${it.id}`}>
                    <Textarea rows={2} value={warnText} onChange={(e) => setWarnText(e.target.value)} placeholder="Message d'avertissement au membre (optionnel)…" className="text-sm" data-testid={`mod-warn-message-${it.id}`} />
                    <div className="flex gap-2 justify-end">
                      <Button size="sm" variant="ghost" className="rounded-full" onClick={() => { setWarnOpen(null); setWarnText(""); }}>Annuler</Button>
                      <Button size="sm" className="pill-primary rounded-full" disabled={warnBusy === it.id} onClick={() => warnAuthor(it)} data-testid={`mod-warn-send-${it.id}`}>{warnBusy === it.id ? <Loader2 size={14} className="animate-spin" /> : "Envoyer l'avertissement"}</Button>
                    </div>
                  </div>
                )}
              </>
            )}
            {status === "pending" && it.target_type === "user" && (
              <div className="flex items-center gap-2 mt-2 flex-wrap rounded-xl p-2" style={{ background: "var(--yalola-surface-alt)" }} data-testid={`mod-suspend-row-${it.id}`}>
                <span className="text-xs font-semibold" style={{ color: "var(--yalola-text-muted)" }}>Suspendre&nbsp;:</span>
                <Select value={suspendDur[it.id] || "7d"} onValueChange={(v) => setSuspendDur((s) => ({ ...s, [it.id]: v }))}>
                  <SelectTrigger className="h-8 w-32" data-testid={`mod-suspend-duration-${it.id}`}><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="24h">24 heures</SelectItem>
                    <SelectItem value="7d">7 jours</SelectItem>
                    <SelectItem value="30d">30 jours</SelectItem>
                    <SelectItem value="permanent">Définitif</SelectItem>
                  </SelectContent>
                </Select>
                <Button size="sm" className="rounded-full text-white" style={{ background: "#DC2626" }} onClick={() => suspendUser(it)} disabled={!canManage || suspendBusy === it.id} data-testid={`mod-suspend-${it.id}`}>
                  <Ban size={14} className="mr-1" /> {suspendBusy === it.id ? "…" : "Suspendre ce profil"}
                </Button>
              </div>
            )}
          </div>
        ))}
      </div>
      <AdminPager page={page} pages={pages} onPage={setPage} testId="mod-pager" />
    </div>
  );
};

const LivePanel = ({ canManage = true }) => {
  const [cfg, setCfg] = useState(null);
  const [saving, setSaving] = useState(false);
  useEffect(() => { api.get("/admin/live/settings").then(({ data }) => setCfg(data)).catch(() => {}); }, []);
  const save = async () => {
    setSaving(true);
    try {
      await api.patch("/admin/live/settings", {
        reel_max_seconds: Math.max(0, parseInt(cfg.reel_max_seconds, 10) || 0),
        mobile_max_duration_min: Math.max(0, parseInt(cfg.mobile_max_duration_min, 10) || 0),
        pc_max_duration_min: Math.max(0, parseInt(cfg.pc_max_duration_min, 10) || 0),
        hard_max_duration_min: Math.max(30, Math.min(1440, parseInt(cfg.hard_max_duration_min, 10) || 720)),
        storage_alert_pct: Math.max(50, Math.min(95, parseInt(cfg.storage_alert_pct, 10) || 80)),
        replay_offload_bunny: !!cfg.replay_offload_bunny,
      });
      toast.success("Règles de direct enregistrées");
    } catch (e) { toast.error(e?.friendlyMessage || "Erreur"); }
    finally { setSaving(false); }
  };
  const [purging, setPurging] = useState(false);
  const [confirmPurge, setConfirmPurge] = useState(false);
  const purgeEmpty = async () => {
    setPurging(true);
    try {
      const { data } = await api.post("/admin/live/purge-empty");
      toast.success(`${data.deleted} direct(s) terminé(s) sans rediffusion supprimé(s)`);
    } catch (e) { toast.error(e?.friendlyMessage || "Erreur"); }
    finally { setPurging(false); setConfirmPurge(false); }
  };
  if (!cfg) return <AdminSkeleton />;
  return (
    <div className="space-y-4" data-testid="admin-live-panel">
      <div className="yalola-card p-5" data-testid="live-privileges-note-card" style={{ borderLeft: "3px solid var(--yalola-primary)" }}>
        <div className="font-bold font-display text-lg mb-1">Accès & durée par rôle</div>
        <p className="text-sm" style={{ color: "var(--yalola-text-muted)" }}>
          Qui peut passer en direct, les <b>abonnés minimum requis</b> et la <b>durée maximale d'un direct</b> se règlent dans <b>Configuration → Privilèges</b>, par type de compte et selon la vérification (un compte vérifié a plus de droits qu'un non vérifié). Ces règles <b>s'appliquent réellement</b> : blocage à l'ouverture + arrêt automatique à la fin de la durée. Le plafond strict mobile/PC ci-dessous s'y combine — la limite la plus stricte l'emporte.
        </p>
      </div>

      <div className="yalola-card p-5" data-testid="live-reel-card">
        <div className="font-bold font-display text-lg mb-1">Rediffusion → Reel</div>
        <p className="text-sm mb-3" style={{ color: "var(--yalola-text-muted)" }}>
          Une rediffusion de direct <b>verticale</b> devient un <b>Reel</b> si sa durée est ≤ à cette limite ; au-delà (ou en format paysage), elle part dans le <b>fil</b> comme une vidéo. <b>0 = illimité</b> (toute rediffusion verticale devient un Reel).
        </p>
        <div className="sm:max-w-xs">
          <Label>Durée max pour un Reel (secondes)</Label>
          <Input type="number" min={0} value={cfg.reel_max_seconds ?? 90} disabled={!canManage}
                 onChange={(e) => setCfg({ ...cfg, reel_max_seconds: e.target.value })} data-testid="live-reel-max-seconds" />
        </div>
      </div>

      <div className="yalola-card p-5" data-testid="live-mobile-card">
        <div className="font-bold font-display text-lg mb-1">Direct mobile & PC</div>
        <p className="text-sm mb-3" style={{ color: "var(--yalola-text-muted)" }}>
          Plafond <b>strict</b> de durée appliqué en plus des limites par rôle (la plus stricte l'emporte). Le mobile diffuse en <b>RTMPS natif</b> : Cloudflare enregistre la rediffusion côté serveur (aucun envoi lourd depuis le téléphone). <b>0 = illimité.</b>
        </p>
        <div className="grid sm:grid-cols-2 gap-3">
          <div className="rounded-xl px-4 py-3" style={{ background: "var(--yalola-surface-alt)" }}>
            <Label className="text-xs">Durée max sur mobile (minutes)</Label>
            <Input type="number" min={0} value={cfg.mobile_max_duration_min ?? 60} disabled={!canManage}
                   onChange={(e) => setCfg((c) => ({ ...c, mobile_max_duration_min: e.target.value }))} data-testid="live-mobile-duration" />
          </div>
          <div className="rounded-xl px-4 py-3" style={{ background: "var(--yalola-surface-alt)" }}>
            <Label className="text-xs">Durée max sur PC / OBS (minutes)</Label>
            <Input type="number" min={0} value={cfg.pc_max_duration_min ?? 300} disabled={!canManage}
                   onChange={(e) => setCfg((c) => ({ ...c, pc_max_duration_min: e.target.value }))} data-testid="live-pc-duration" />
          </div>
        </div>
        <p className="text-xs mt-3" style={{ color: "var(--yalola-text-muted)" }}>
          Le direct depuis un téléphone se fait dans l'application (caméra → RTMPS natif, rediffusion enregistrée côté serveur). Depuis un ordinateur, la diffusion passe par OBS.
        </p>
      </div>

      <div className="yalola-card p-5" data-testid="live-hard-cap-card" style={{ borderLeft: "3px solid #B91C1C" }}>
        <div className="font-bold font-display text-lg mb-1">Plafond absolu & coupure automatique</div>
        <p className="text-sm mb-3" style={{ color: "var(--yalola-text-muted)" }}>
          Filet de sécurité <b>Cloudflare Stream</b> (chaque minute enregistrée est facturée) : ce plafond s'applique à <b>tous les comptes, super admin inclus</b>, même si les limites ci-dessus sont à 0. Au-delà, le direct est arrêté et <b>l'entrée Cloudflare est coupée</b>. « Terminer le direct » coupe aussi l'entrée immédiatement, et un garde-fou coupe toute source (OBS, téléphone) restée connectée après la fin. La rediffusion déjà enregistrée est conservée. <b>Jamais illimité</b> : entre 30 et 1440 minutes.
        </p>
        <div className="sm:max-w-xs rounded-xl px-4 py-3" style={{ background: "var(--yalola-surface-alt)" }}>
          <Label className="text-xs">Plafond absolu (minutes)</Label>
          <Input type="number" min={30} max={1440} value={cfg.hard_max_duration_min ?? 720} disabled={!canManage}
                 onChange={(e) => setCfg((c) => ({ ...c, hard_max_duration_min: e.target.value }))} data-testid="live-hard-cap" />
        </div>
      </div>

      <LiveStorageCard cfg={cfg} setCfg={setCfg} canManage={canManage} />

      {canManage && (
        <div className="yalola-card p-5" data-testid="live-cleanup-card">
          <div className="font-bold font-display text-lg mb-1">Nettoyage des directs</div>
          <p className="text-sm mb-3" style={{ color: "var(--yalola-text-muted)" }}>
            Supprime les <b>directs terminés qui n'ont pas de rediffusion</b> (terminés depuis plus de 6h) — les entrées « Terminé · Pas de rediffusion » qui encombrent « Mes directs ». Libère aussi l'entrée Cloudflare. <b>Action définitive.</b> N'affecte jamais un direct récent (rediffusion encore en préparation).
          </p>
          {!confirmPurge ? (
            <Button variant="outline" className="rounded-full text-red-700" onClick={() => setConfirmPurge(true)} data-testid="live-purge-empty-btn">
              <Trash2 size={15} className="mr-1.5" /> Purger les directs terminés sans rediffusion
            </Button>
          ) : (
            <div className="flex flex-wrap items-center gap-2">
              <span className="text-sm font-medium" style={{ color: "#DC2626" }}>Confirmer la suppression définitive ?</span>
              <Button className="rounded-full" style={{ background: "#DC2626", color: "#fff" }} onClick={purgeEmpty} disabled={purging} data-testid="live-purge-empty-confirm">
                {purging ? <Loader2 size={15} className="animate-spin mr-1.5" /> : <Trash2 size={15} className="mr-1.5" />} Oui, purger
              </Button>
              <Button variant="outline" className="rounded-full" onClick={() => setConfirmPurge(false)} disabled={purging} data-testid="live-purge-empty-cancel">Annuler</Button>
            </div>
          )}
        </div>
      )}

      {canManage && (
        <div className="flex justify-end">
          <Button className="pill pill-primary" onClick={save} disabled={saving} data-testid="live-settings-save">
            {saving ? "Enregistrement..." : "Enregistrer"}
          </Button>
        </div>
      )}
    </div>
  );
};


const VersesPanel = ({ isSuper, canManage = true }) => {
  const [verses, setVerses] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ reference: "", text_fr: "", text_en: "", text_es: "", text_pt: "", active_date: "" });
  const load = () => api.get("/admin/verses").then(({ data }) => setVerses(data.verses || [])).catch(() => {});
  useEffect(() => { load(); }, []);
  const create = async () => {
    try { await api.post("/admin/verses", form); toast.success("Verset ajouté"); setOpen(false); load(); }
    catch (e) { toast.error(e?.friendlyMessage || "Erreur"); }
  };
  const remove = async (id) => { try { await api.delete(`/admin/verses/${id}`); load(); } catch { toast.error("Erreur"); } };
  return (
    <div className="space-y-3" data-testid="admin-verses-panel">
      {canManage && (
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild><Button className="pill-primary rounded-full" data-testid="verse-add-btn">+ Ajouter un verset</Button></DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle>Nouveau verset</DialogTitle></DialogHeader>
            <div className="space-y-2 max-h-[60vh] overflow-y-auto">
              <div><Label>Référence</Label><Input value={form.reference} onChange={e => setForm({...form, reference: e.target.value})} data-testid="verse-ref" /></div>
              <div><Label>Texte FR</Label><Textarea rows={2} value={form.text_fr} onChange={e => setForm({...form, text_fr: e.target.value})} data-testid="verse-fr" /></div>
              <div><Label>Texte EN</Label><Textarea rows={2} value={form.text_en} onChange={e => setForm({...form, text_en: e.target.value})} data-testid="verse-en" /></div>
              <div><Label>Texte ES</Label><Textarea rows={2} value={form.text_es} onChange={e => setForm({...form, text_es: e.target.value})} data-testid="verse-es" /></div>
              <div><Label>Texte PT</Label><Textarea rows={2} value={form.text_pt} onChange={e => setForm({...form, text_pt: e.target.value})} data-testid="verse-pt" /></div>
              <div><Label>Date active (YYYY-MM-DD)</Label><Input type="date" min="2000-01-01" max="2100-12-31" value={form.active_date} onChange={e => setForm({...form, active_date: e.target.value})} data-testid="verse-date" /></div>
            </div>
            <DialogFooter><Button variant="outline" onClick={() => setOpen(false)}>Annuler</Button><Button className="pill-primary" onClick={create} data-testid="verse-submit">Ajouter</Button></DialogFooter>
          </DialogContent>
        </Dialog>
      )}
      <div className="yalola-card overflow-x-auto">
        <Table>
          <TableHeader><TableRow><TableHead>Référence</TableHead><TableHead>FR</TableHead><TableHead>Date active</TableHead>{canManage && <TableHead>Actions</TableHead>}</TableRow></TableHeader>
          <TableBody>
            {verses.map(v => (
              <TableRow key={v.id} data-testid={`verse-row-${v.id}`}>
                <TableCell className="font-medium">{v.reference}</TableCell>
                <TableCell className="text-xs max-w-md truncate">{v.text_fr}</TableCell>
                <TableCell className="text-xs">{v.active_date || "—"}</TableCell>
                {canManage && <TableCell><Button size="sm" variant="outline" onClick={() => remove(v.id)} data-testid={`verse-del-${v.id}`}><X size={12} /></Button></TableCell>}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </div>
  );
};

const DonationsPanel = ({ canManage = true }) => {
  const VIEWS = [["donations", "Dons"], ["withdrawals", "Retraits"], ["beneficiaries", "Bénéficiaires"], ["verified", "Yalola Vérifié"], ["adcredits", "Crédits pub"], ["ledger", "Revenus (journal)"]];
  const [searchParams] = useSearchParams();
  // ?view=withdrawals (lien du rappel « retraits en retard ») ouvre directement la bonne vue
  const [view, setView] = useState(() => (VIEWS.some(([k]) => k === searchParams.get("view")) ? searchParams.get("view") : "donations"));
  return (
    <div className="space-y-3" data-testid="admin-donations-panel">
      <div className="yalola-card p-4 flex gap-2 flex-wrap">
        {VIEWS.map(([k, l]) => (
          <button key={k} onClick={() => setView(k)} className={`pill ${view === k ? "pill-primary" : "pill-outline"}`} data-testid={`finance-view-${k}`}>{l}</button>
        ))}
      </div>
      {view === "donations" && <DonationsTable />}
      {view === "withdrawals" && <WithdrawalsAdmin canManage={canManage} />}
      {view === "beneficiaries" && <BeneficiariesAdmin canManage={canManage} />}
      {view === "verified" && <VerifiedSubscriptionsAdmin canManage={canManage} />}
      {view === "adcredits" && <AdCreditsAdmin canManage={canManage} />}
      {view === "ledger" && <RevenueLedgerAdmin />}
    </div>
  );
};

const TX_META = {
  donation: { label: "Don", color: "#D97757" },
  verification: { label: "Vérification", color: "#E0A63C" },
  withdrawal: { label: "Retrait", color: "#6B7280" },
  wallet: { label: "Portefeuille", color: "#2D5A4C" },
  ad: { label: "Pub en ligne", color: "#8A5A12" },
  ad_offline: { label: "Pub hors-ligne", color: "#5B3A0C" },
  ad_credit: { label: "Crédits pub", color: "#B45309" },
  ad_spend: { label: "Conso pub", color: "#7C2D12" },
};
const TX_FILTERS = [
  { key: "all", label: "Tout" },
  { key: "donation", label: "Dons" },
  { key: "verification", label: "Vérifications" },
  { key: "withdrawal", label: "Retraits" },
  { key: "wallet", label: "Portefeuille" },
  { key: "ad", label: "Pubs en ligne" },
  { key: "ad_offline", label: "Pubs hors-ligne" },
  { key: "ad_credit", label: "Crédits pub" },
  { key: "ad_spend", label: "Conso pub" },
];
// `pending` is a backend alias covering pending (ancien modèle) + requested + under_review.
const TX_STATUS = ["all", "paid", "pending", "approved", "processing", "rejected", "cancelled", "failed", "refunded", "settled", "free", "expired"];
const TX_STATUS_LABEL = {
  all: "Tous statuts", paid: "Payé", pending: "En attente", requested: "Demandé", under_review: "En examen", approved: "Approuvé",
  processing: "Versement en cours", rejected: "Rejeté", cancelled: "Annulé", failed: "Échoué", refunded: "Remboursé",
  settled: "Comptabilisé", free: "Offert", expired: "Expirée", active: "Actif",
};
const TX_STATUS_STYLE = {
  paid: { background: "#D1FAE5", color: "#065F46" },
  settled: { background: "#D1FAE5", color: "#065F46" },
  pending: { background: "#FEF3C7", color: "#92400E" },
  requested: { background: "#FEF3C7", color: "#92400E" },
  under_review: { background: "#FEF3C7", color: "#92400E" },
  approved: { background: "#DBEAFE", color: "#1E40AF" },
  processing: { background: "#E0E7FF", color: "#3730A3" },
  expired: { background: "#F3F4F6", color: "#6B7280" },
  free: { background: "#F3F4F6", color: "#374151" },
  cancelled: { background: "#F3F4F6", color: "#374151" },
  rejected: { background: "#FEE2E2", color: "#991B1B" },
  failed: { background: "#FEE2E2", color: "#991B1B" },
  refunded: { background: "#FEE2E2", color: "#991B1B" },
};
// Effect of a movement on the member's professional wallet (withdrawals / adjustments).
const TX_EFFECT_LABEL = { debited: "versé — sortie définitive", released: "recrédité au membre", held: "bloqué (en cours)", credited: "crédité au portefeuille" };
const TX_PROVIDER_LABEL = {
  stripe: "Stripe", apple: "App Store", google: "Google Play", offline: "Hors-ligne", admin: "Yalola (admin)", migration: "Migration",
  wallet: "Crédits pub", bank: "Virement bancaire", paypal: "PayPal", mobile_money: "Mobile Money", free: "Gratuit / offert",
  bank_transfer: "Virement bancaire", cash: "Espèces", other: "Autre",
};
const TxParty = ({ p }) => (
  p?.deleted
    ? <span className="italic" style={{ color: "var(--yalola-text-muted)" }} data-testid="tx-party-deleted">{p.name}</span>
    : <>{p?.name || "—"}</>
);
const txFmt = (n, c = "USD") => `${(Math.round((n || 0) * 100) / 100).toLocaleString("fr-FR")} ${c === "EUR" ? "€" : c === "USD" ? "$" : c}`;

// Single source of truth for money display: the app's default currency (admin setting).
const useAppCurrency = () => {
  const { config } = useApp();
  return config?.settings?.default_currency || "USD";
};

const TxSummaryCard = ({ label, value, color, hint, hintColor, testId }) => (
  <div className="yalola-card p-3.5" data-testid={testId}>
    <div className="text-[11px] uppercase font-bold tracking-wide" style={{ color: "var(--yalola-text-muted)" }}>{label}</div>
    <div className="text-lg md:text-xl font-extrabold mt-1 font-display" style={{ color }} data-testid={testId ? `${testId}-value` : undefined}>{value}</div>
    {hint && <div className="text-[11px] mt-0.5" style={{ color: hintColor || "var(--yalola-text-muted)" }} data-testid={testId ? `${testId}-hint` : undefined}>{hint}</div>}
  </div>
);

const EXPENSE_CATEGORIES = {
  salary: "Salaires",
  subscription: "Abonnements / SaaS",
  hosting: "Hébergement",
  database: "Base de données",
  emergent: "Crédits Emergent",
  marketing: "Marketing & Publicité",
  banking: "Frais bancaires & Stripe",
  equipment: "Matériel & équipement",
  services: "Prestataires & services",
  taxes: "Impôts & taxes",
  legal: "Frais juridiques & conformité",
  office: "Bureau & frais généraux",
  other: "Autres",
};

const emptyExpense = () => ({ label: "", amount: "", currency: "EUR", category: "other", country: "", date: new Date().toISOString().slice(0, 10), attachment_url: "", link: "", note: "" });

const EXPENSE_FIELD_LABELS = { label: "Libellé", amount: "Montant", currency: "Devise", category: "Catégorie", country: "Pays", attachment_url: "Pièce jointe", link: "Lien", note: "Note", date: "Date" };

const EXPENSE_ROLE_LABELS = { manager: "Manager", associate: "Associé", moderator: "Modérateur", support: "Support" };

const ExpensesPanel = ({ isSuper = false, viewRoles = [], onChanged }) => {
  const { user, config } = useApp();
  const platformCurrency = (config?.settings?.default_currency || "EUR").toUpperCase();
  const [data, setData] = useState(null);
  const [form, setForm] = useState(null); // null = dialog closed
  const [saving, setSaving] = useState(false);
  const [fCat, setFCat] = useState("all");
  const [fMonth, setFMonth] = useState("all");
  const editorRoles = data?.editor_roles || ["manager"];
  const canEncode = isSuper || viewRoles.some((r) => editorRoles.includes(r));
  const canEditRow = (e) => isSuper || (canEncode && e.created_by === user?.id);
  const load = () => api.get(`/admin/expenses?category=${fCat}&month=${fMonth}`).then(({ data: d }) => setData(d)).catch(() => setData({ expenses: [], total: 0, by_currency: {} }));
  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [fCat, fMonth]);
  const exportCsv = async () => {
    try {
      const { data: blob } = await api.get(`/admin/expenses/export.csv?category=${fCat}&month=${fMonth}`, { responseType: "blob" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a"); a.href = url;
      a.download = `depenses${fMonth !== "all" ? "-" + fMonth : ""}.csv`;
      document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
    } catch { toast.error("Export impossible"); }
  };
  const toggleEditorRole = async (r) => {
    const next = editorRoles.includes(r) ? editorRoles.filter((x) => x !== r) : [...editorRoles, r];
    try { await api.patch("/admin/expenses/editor-roles", { roles: next }); await load(); toast.success("Réglage mis à jour"); }
    catch (e) { toast.error(e?.response?.data?.detail || "Erreur"); }
  };
  const list = data?.expenses || [];
  const byCur = data?.by_currency || {};
  const totalLabel = Object.keys(byCur).length ? Object.entries(byCur).map(([c, v]) => txFmt(v, c)).join(" · ") : txFmt(0, "EUR");

  const openNew = () => setForm({ ...emptyExpense(), currency: platformCurrency });
  const openEdit = (e) => setForm({ ...emptyExpense(), ...e, amount: String(e.amount ?? "") });

  const save = async () => {
    if (!form.label.trim()) { toast.error("Le libellé est obligatoire"); return; }
    const amt = parseFloat(form.amount);
    if (!amt || amt <= 0) { toast.error("Montant invalide"); return; }
    setSaving(true);
    try {
      const payload = { ...form, amount: amt, currency: platformCurrency };
      if (form.id) await api.patch(`/admin/expenses/${form.id}`, payload);
      else await api.post("/admin/expenses", payload);
      toast.success(form.id ? "Dépense mise à jour" : "Dépense enregistrée");
      setForm(null); await load(); onChanged?.();
    } catch (e) { toast.error(e?.response?.data?.detail || "Erreur lors de l'enregistrement"); }
    setSaving(false);
  };

  const remove = async (e) => {
    if (!window.confirm(`Supprimer la dépense « ${e.label} » ?`)) return;
    try { await api.delete(`/admin/expenses/${e.id}`); await load(); onChanged?.(); toast.success("Dépense supprimée"); }
    catch (err) { toast.error(err?.response?.data?.detail || "Erreur"); }
  };

  return (
    <div className="yalola-card p-5 space-y-4" data-testid="admin-expenses-panel">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2">
          <div className="h-10 w-10 rounded-xl grid place-items-center" style={{ background: "#9A341215", color: "#9A3412" }}><Receipt size={20} /></div>
          <div>
            <div className="font-bold font-display">Dépenses</div>
            <div className="text-xs" style={{ color: "var(--yalola-text-muted)" }}>Coûts d'exploitation déduits du revenu net</div>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <div className="text-right">
            <div className="text-[10px] uppercase font-bold tracking-wide" style={{ color: "var(--yalola-text-muted)" }}>Total dépenses</div>
            <div className="text-lg font-extrabold" style={{ color: "#9A3412" }} data-testid="expenses-total">{totalLabel}</div>
          </div>
          {canEncode && (
            <Button className="pill-primary rounded-full" onClick={openNew} data-testid="expense-add-btn"><Plus size={16} className="mr-1" /> Ajouter une dépense</Button>
          )}
        </div>
      </div>

      <div className="flex items-center gap-2 flex-wrap">
        <Select value={fCat} onValueChange={setFCat}>
          <SelectTrigger className="w-52 h-9" data-testid="expense-filter-category"><SelectValue placeholder="Catégorie" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Toutes les catégories</SelectItem>
            {Object.entries(EXPENSE_CATEGORIES).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={fMonth} onValueChange={setFMonth}>
          <SelectTrigger className="w-44 h-9" data-testid="expense-filter-month"><SelectValue placeholder="Mois" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tous les mois</SelectItem>
            {(data?.months || []).map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}
          </SelectContent>
        </Select>
        <Button variant="outline" size="sm" className="rounded-full" onClick={exportCsv} data-testid="expense-export-csv"><Download size={14} className="mr-1" /> Exporter CSV</Button>
        {isSuper && (
          <Popover>
            <PopoverTrigger asChild><Button variant="outline" size="sm" className="rounded-full" data-testid="expense-editor-roles-btn"><Settings size={14} className="mr-1" /> Rôles autorisés</Button></PopoverTrigger>
            <PopoverContent className="w-72 text-sm" data-testid="expense-editor-roles-popover">
              <div className="font-semibold mb-1">Qui peut encoder des dépenses ?</div>
              <div className="text-[11px] mb-2" style={{ color: "var(--yalola-text-muted)" }}>Le Super admin peut toujours. Les autres ne gèrent que leurs propres dépenses.</div>
              <div className="flex flex-wrap gap-1.5">
                {["manager", "associate", "moderator", "support"].map((r) => (
                  <button key={r} onClick={() => toggleEditorRole(r)} className={`pill ${editorRoles.includes(r) ? "pill-primary" : "pill-outline"}`} data-testid={`expense-role-${r}`}>{EXPENSE_ROLE_LABELS[r]}</button>
                ))}
              </div>
              <div className="text-[11px] mt-2" style={{ color: "var(--yalola-text-muted)" }}>Modérateur / Support doivent aussi avoir accès aux Finances pour voir les dépenses.</div>
            </PopoverContent>
          </Popover>
        )}
      </div>

      {Object.keys(data?.by_category || {}).length > 0 && (
        <div className="flex flex-wrap gap-1.5" data-testid="expense-subtotals">
          {Object.entries(data.by_category).sort((a, b) => b[1] - a[1]).map(([k, v]) => (
            <span key={k} className="pill pill-outline text-xs">{EXPENSE_CATEGORIES[k] || k} : {txFmt(v, platformCurrency)}</span>
          ))}
        </div>
      )}

      {list.length === 0 ? (
        <div className="text-sm text-center py-6" style={{ color: "var(--yalola-text-muted)" }}>Aucune dépense encodée pour le moment.</div>
      ) : (
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Libellé</TableHead>
                <TableHead className="text-right">Montant</TableHead>
                <TableHead>Catégorie</TableHead>
                <TableHead>Pièce / Lien</TableHead>
                <TableHead>Pays</TableHead>
                <TableHead>Encodé par</TableHead>
                {canEncode && <TableHead className="text-right">Actions</TableHead>}
              </TableRow>
            </TableHeader>
            <TableBody>
              {list.map((e) => (
                <TableRow key={e.id} data-testid={`expense-row-${e.id}`}>
                  <TableCell className="font-medium">
                    <div>{e.label}</div>
                    <div className="text-[11px]" style={{ color: "var(--yalola-text-muted)" }}>{e.date}</div>
                  </TableCell>
                  <TableCell className="text-right font-bold" style={{ color: "#9A3412" }}>−{txFmt(e.amount, e.currency)}</TableCell>
                  <TableCell><span className="pill pill-outline text-xs">{EXPENSE_CATEGORIES[e.category] || e.category}</span></TableCell>
                  <TableCell>
                    {e.attachment_url ? (
                      <a href={resolveMediaUrl(e.attachment_url)} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-xs underline" style={{ color: "var(--yalola-primary)" }} data-testid={`expense-attach-${e.id}`}><Paperclip size={12} /> Justificatif</a>
                    ) : e.link ? (
                      <a href={e.link} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-xs underline" style={{ color: "var(--yalola-primary)" }} data-testid={`expense-link-${e.id}`}><ExternalLink size={12} /> Lien</a>
                    ) : <span className="text-xs" style={{ color: "var(--yalola-text-muted)" }}>—</span>}
                  </TableCell>
                  <TableCell className="text-sm">{e.country || "—"}</TableCell>
                  <TableCell className="text-sm">
                    <div className="flex items-center gap-1.5">
                      <span>{e.created_by_name}</span>
                      {e.history?.length > 0 && (
                        <Popover>
                          <PopoverTrigger asChild>
                            <button title="Historique des modifications" data-testid={`expense-history-${e.id}`}><Clock size={13} style={{ color: "var(--yalola-primary)" }} /></button>
                          </PopoverTrigger>
                          <PopoverContent className="w-80 max-h-72 overflow-y-auto text-xs" data-testid={`expense-history-content-${e.id}`}>
                            <div className="font-semibold mb-1.5">Historique des modifications</div>
                            {e.history.slice().reverse().map((h, hi) => (
                              <div key={hi} className="py-1.5 border-t" style={{ borderColor: "var(--yalola-border)" }}>
                                <div className="font-medium">{new Date(h.at).toLocaleString("fr-FR")} · {h.by_name} <span style={{ color: "var(--yalola-text-muted)" }}>({h.by_role === "super_admin" ? "Super admin" : "Manager"})</span></div>
                                {(h.changes || []).map((c, ci) => (
                                  <div key={ci} style={{ color: "var(--yalola-text-muted)" }}>{EXPENSE_FIELD_LABELS[c.field] || c.field} : <s>{String(c.from ?? "—")}</s> → {String(c.to ?? "—")}</div>
                                ))}
                              </div>
                            ))}
                          </PopoverContent>
                        </Popover>
                      )}
                    </div>
                    <div className="text-[11px]" style={{ color: "var(--yalola-text-muted)" }}>{e.created_by_role === "super_admin" ? "Super admin" : "Manager"}</div>
                  </TableCell>
                  {canEncode && (
                    <TableCell className="text-right whitespace-nowrap">
                      {canEditRow(e) ? (
                        <>
                          <button onClick={() => openEdit(e)} title="Modifier" className="mr-3" data-testid={`expense-edit-${e.id}`}><Pencil size={15} style={{ color: "var(--yalola-text-muted)" }} /></button>
                          <button onClick={() => remove(e)} title="Supprimer" data-testid={`expense-delete-${e.id}`}><Trash2 size={15} style={{ color: "#DC2626" }} /></button>
                        </>
                      ) : (
                        <span className="text-[11px]" style={{ color: "var(--yalola-text-muted)" }} title="Réservé au Super admin / à l'auteur">—</span>
                      )}
                    </TableCell>
                  )}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}

      <Dialog open={!!form} onOpenChange={(o) => { if (!o) setForm(null); }}>
        <DialogContent data-testid="expense-dialog">
          <DialogHeader>
            <DialogTitle>{form?.id ? "Modifier la dépense" : "Nouvelle dépense"}</DialogTitle>
            <DialogDescription>Renseigne le détail de la dépense d'exploitation.</DialogDescription>
          </DialogHeader>
          {form && (
            <div className="space-y-3">
              <div>
                <Label>Libellé</Label>
                <Input value={form.label} onChange={(ev) => setForm({ ...form, label: ev.target.value })} placeholder="Ex : Abonnement AWS — juin" data-testid="expense-label" />
              </div>
              <div>
                <Label>Montant ({platformCurrency})</Label>
                <Input type="number" min="0" step="0.01" value={form.amount} onChange={(ev) => setForm({ ...form, amount: ev.target.value })} data-testid="expense-amount" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Catégorie</Label>
                  <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                    <SelectTrigger data-testid="expense-category"><SelectValue /></SelectTrigger>
                    <SelectContent>{Object.entries(EXPENSE_CATEGORIES).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Pays</Label>
                  <Input value={form.country} onChange={(ev) => setForm({ ...form, country: ev.target.value })} placeholder="Ex : France, RD Congo, En ligne" data-testid="expense-country" />
                </div>
              </div>
              <div>
                <Label>Date</Label>
                <Input type="date" value={form.date} onChange={(ev) => setForm({ ...form, date: ev.target.value })} data-testid="expense-date" />
              </div>
              <div>
                <Label>Pièce jointe (justificatif)</Label>
                {form.attachment_url ? (
                  <div className="flex items-center gap-2 text-sm mt-1">
                    <a href={resolveMediaUrl(form.attachment_url)} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 underline" style={{ color: "var(--yalola-primary)" }}><Paperclip size={13} /> Justificatif joint</a>
                    <button onClick={() => setForm({ ...form, attachment_url: "" })} data-testid="expense-attach-remove"><X size={14} style={{ color: "#DC2626" }} /></button>
                  </div>
                ) : (
                  <div className="mt-1"><FileUpload accept="image/*" buttonLabel="Joindre une image" testId="expense-file" context="feed" onUploaded={(url) => setForm((f) => ({ ...f, attachment_url: url }))} /></div>
                )}
              </div>
              <div>
                <Label>… ou un lien (facture PDF, Drive, etc.)</Label>
                <Input value={form.link} onChange={(ev) => setForm({ ...form, link: ev.target.value })} placeholder="https://…" data-testid="expense-link-input" />
              </div>
              <div>
                <Label>Note (optionnel)</Label>
                <Textarea value={form.note} onChange={(ev) => setForm({ ...form, note: ev.target.value })} rows={2} data-testid="expense-note" />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setForm(null)} data-testid="expense-cancel">Annuler</Button>
            <Button className="pill-primary" onClick={save} disabled={saving} data-testid="expense-save">{saving ? "Enregistrement…" : "Enregistrer"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// Tuile « Autres devises » : « 11,53 $ → 9,80 $ net · 1 $ = 0,879 € (BCE 28/09) » ; sans ligne : « converties au taux BCE du jour ».
const fxHint = (c, cur, t) => {
  const det = Object.entries(c.fx_detail || {});
  const pending = c.fx_pending ? ` · ${c.fx_pending} ${t("finance.fx_pending", "en attente de taux")}` : "";
  if (!det.length) return t("finance.fx_hint_empty", "converties au taux BCE du jour du paiement") + pending;
  const parts = det.map(([k, v]) => {
    const rate = v.rate ? `1 ${k} = ${Number(v.rate).toLocaleString("fr-FR", { maximumFractionDigits: 4 })} ${cur === "EUR" ? "€" : cur}` : "";
    const when = v.date ? ` (BCE ${v.date.slice(8, 10)}/${v.date.slice(5, 7)})` : "";
    return `${txFmt(v.gross, k)} → ${txFmt(v.net, k)} net · ${rate}${when}${v.via ? ` via ${v.via}` : ""}`;
  });
  return parts.join(" · ") + pending;
};

const NetRevenueCard = ({ refreshToken = 0, isSuper = false }) => {
  const { t } = useApp();
  const [d, setD] = useState(null);
  const [tick, setTick] = useState(0);
  useEffect(() => { api.get("/admin/finance/net-revenue").then(({ data }) => setD(data)).catch(() => setD(false)); }, [refreshToken, tick]);
  if (d === false) return null;
  const cur = d?.currency || "USD";
  const c = d?.components || {};
  const rates = d?.store_rates || {};
  const pct = (v) => (v === null || v === undefined ? "—" : `${Number(v).toLocaleString("fr-FR")} %`);
  const parts = [
    { key: "donation_commissions", label: t("finance.donation_commissions", "Commissions sur dons"), value: c.donation_commissions, color: "#D97757", Icon: HeartHandshake, sign: "+" },
    { key: "verifications", label: t("finance.verifications_gross", "Vérifié (brut)"), value: c.verifications, color: "#E0A63C", Icon: ShieldCheck, sign: "+" },
    { key: "ad_credits", label: "Crédits pub (brut)", value: c.ad_credits, color: "#8A5A12", Icon: Wallet, sign: "+" },
    { key: "ads_stripe", label: t("finance.ads_stripe", "Publicités (en ligne — ancien modèle)"), value: c.ads_stripe, color: "#8A5A12", Icon: Megaphone, sign: "+" },
    { key: "ads_offline", label: t("finance.ads_offline", "Publicités (hors-ligne)"), value: c.ads_offline, color: "#2D5A4C", Icon: Megaphone, sign: "+" },
    { key: "store_fees", label: t("finance.store_fees", "Frais stores / Stripe"), value: c.store_fees, color: "#B91C1C", Icon: Percent, sign: "−", hint: `App Store ${pct(rates.apple)} · Google Play ${pct(rates.google)} · Stripe réel` },
    { key: "store_tax", label: t("finance.store_tax", "TVA stores"), value: c.store_tax, color: "#6B7280", Icon: Landmark, sign: "−", hint: "collectée par Apple / Google" },
    { key: "expenses", label: t("finance.expenses", "Dépenses"), value: c.expenses, color: "#9A3412", Icon: Receipt, sign: "−" },
    { key: "refunds", label: t("finance.refunds", "Remboursements"), value: c.refunds, color: "#B91C1C", Icon: RotateCcw, sign: "−" },
    { key: "fx_in", label: t("finance.fx_in", "Autres devises"), value: c.fx_in, color: "#2D5A4C", Icon: ArrowLeftRight, sign: "+", hint: fxHint(c, cur, t) },
  ];
  const isNeg = (d?.net_revenue ?? 0) < 0;
  return (
    <div className="yalola-card p-5" data-testid="admin-net-revenue">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2">
          <div className="h-10 w-10 rounded-xl grid place-items-center" style={{ background: "var(--yalola-primary-soft)", color: "var(--yalola-primary)" }}>
            <TrendingUp size={20} />
          </div>
          <div>
            <div className="font-bold font-display">{t("finance.net_revenue_title", "Revenu net de la plateforme")}</div>
            <div className="text-xs" style={{ color: "var(--yalola-text-muted)" }} data-testid="admin-net-revenue-sub">{t("finance.net_revenue_sub", "Commissions + Vérifié + pubs − frais stores/Stripe − TVA stores − dépenses − remboursements")}</div>
          </div>
        </div>
        <div className="text-2xl md:text-3xl font-extrabold font-display" style={{ color: isNeg ? "#B91C1C" : "var(--yalola-primary)" }} data-testid="admin-net-revenue-total">
          {d === null ? "…" : txFmt(d.net_revenue, cur)}
        </div>
      </div>
      {d && (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-2 mt-4">
          {parts.map(({ key, label, value, color, Icon, sign, hint }) => (
            <div key={key} className="rounded-xl p-3" style={{ background: "var(--yalola-surface-alt)" }} data-testid={`net-rev-${key}`}>
              <div className="flex items-center gap-1.5 text-[10px] uppercase font-bold tracking-wide" style={{ color: "var(--yalola-text-muted)" }}>
                <Icon size={12} /> {label}
              </div>
              <div className="text-sm font-extrabold mt-1" style={{ color }}>{sign}{txFmt(value, cur)}</div>
              {hint && <div className="text-[10px] mt-0.5" style={{ color: "var(--yalola-text-muted)" }} data-testid={`net-rev-${key}-hint`}>{hint}</div>}
            </div>
          ))}
        </div>
      )}
      {d && (
        <p className="text-xs mt-3" style={{ color: "var(--yalola-text-muted)" }} data-testid="net-rev-formula">
          Net Yalola = brut encaissé − frais du store ou de Stripe − TVA collectée par le store, + le net des autres devises converti au taux BCE du jour du paiement. Vérifié net {txFmt(c.verifications_net, cur)} · crédits pub nets {txFmt(c.ad_credits_net, cur)}{c.fx_in ? ` · autres devises ${txFmt(c.fx_in, cur)}` : ""}. Même formule sur la carte de lancement, le bilan hebdomadaire et le PDF mensuel.
        </p>
      )}
      {d && <RevenueByChannel byProvider={c.by_provider} rates={rates} rateAlerts={d.rate_alerts || {}} currency={cur} isSuper={isSuper} onRecomputed={() => setTick((x) => x + 1)} />}
      {d && (c.donation_fees_charged > 0 || c.donation_fees_actual > 0 || c.refunds_fees > 0) && (
        <p className="text-xs mt-3" style={{ color: "var(--yalola-text-muted)" }} data-testid="net-rev-fee-info">
          {t("finance.fee_info", "Frais de traitement des soutiens (payés par les donateurs, reversés à Stripe — 0 % Yalola, hors revenu net) : facturés {charged} · Stripe réels {actual} · écart {variance}")
            .replace("{charged}", txFmt(c.donation_fees_charged, cur)).replace("{actual}", txFmt(c.donation_fees_actual, cur)).replace("{variance}", txFmt(c.donation_fee_variance, cur))}
          {c.refunds_fees > 0 ? ` · ${t("finance.fee_info_refunds", "frais conservés par Stripe sur remboursements {amount}").replace("{amount}", txFmt(c.refunds_fees, cur))}` : ""}
        </p>
      )}
      {d && Object.keys(d.by_currency || {}).length > 0 && (
        <div className="rounded-lg border p-3 mt-3 text-xs space-y-1" style={{ borderColor: "var(--yalola-border)" }} data-testid="net-rev-other-currencies">
          <p className="font-semibold" style={{ color: "var(--yalola-text-muted)" }}>
            Autres devises — montants encaissés tels quels ; leur net converti au taux BCE du jour du paiement ({txFmt(c.fx_in, cur)}) est inclus dans le total via la tuile « Autres devises »
          </p>
          {Object.entries(d.by_currency).map(([c, b]) => {
            const k = b.components || {};
            return (
              <p key={c} data-testid={`net-rev-currency-${c}`}>
                <b>{c}</b> — Commissions dons {txFmt(k.donation_commissions, c)} · Vérifié brut {txFmt(k.verifications, c)} · Crédits pub brut {txFmt(k.ad_credits, c)} · Pubs {txFmt(k.ads_stripe, c)} · Frais stores −{txFmt(k.store_fees, c)} · TVA stores −{txFmt(k.store_tax, c)} · Dépenses −{txFmt(k.expenses, c)} · Remboursements −{txFmt(k.refunds, c)} → <b>net {txFmt(b.net_revenue, c)}</b>
              </p>
            );
          })}
        </div>
      )}
    </div>
  );
};

const buildMonthlyRevenue = (ads, count = 6) => {
  const map = {};
  for (const a of ads || []) {
    const m = (a.created_at || "").slice(0, 7);
    if (m) map[m] = (map[m] || 0) + (a.price || 0);
  }
  const out = [];
  const now = new Date();
  for (let i = count - 1; i >= 0; i--) {
    const dt = new Date(now.getFullYear(), now.getMonth() - i, 1);
    const key = `${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, "0")}`;
    out.push({ key, label: dt.toLocaleDateString("fr-FR", { month: "short" }), total: Math.round((map[key] || 0) * 100) / 100 });
  }
  return out;
};

const OfflineAdRevenueCard = () => {
  const [d, setD] = useState(null);
  const [months, setMonths] = useState(6);
  useEffect(() => { api.get("/admin/finance/offline-ads").then(({ data }) => setD(data)).catch(() => setD(false)); }, []);
  const downloadCsv = async () => {
    try {
      const { data } = await api.get("/admin/finance/offline-ads.csv", { responseType: "blob" });
      const url = URL.createObjectURL(data);
      const a = document.createElement("a"); a.href = url; a.download = "revenus-hors-ligne.csv";
      document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
    } catch { toast.error("Export impossible"); }
  };
  if (!d) return null;
  const cur = d.currency || "USD";
  const money = (n) => txFmt(n, cur);
  const byMode = Object.entries(d.by_mode || {}).filter(([, v]) => v > 0).sort((x, y) => y[1] - x[1]);
  return (
    <div className="yalola-card p-4" data-testid="admin-offline-revenue">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2">
          <div className="h-9 w-9 rounded-xl grid place-items-center" style={{ background: "var(--yalola-primary-soft)", color: "var(--yalola-primary)" }}><Megaphone size={17} /></div>
          <div>
            <div className="font-bold font-display">Revenus hors-ligne (pubs facturées hors plateforme)</div>
            <div className="text-xs" style={{ color: "var(--yalola-text-muted)" }} data-testid="admin-offline-revenue-counts">{d.paid_count ?? d.count} pub(s) facturée(s) · {d.free_count ?? 0} offerte(s) · virement, espèces, PayPal…</div>
          </div>
        </div>
        <Button variant="outline" className="rounded-full" onClick={downloadCsv} data-testid="admin-offline-revenue-csv"><Download size={14} className="mr-1" /> Export CSV</Button>
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-3">
        <TxSummaryCard label="Total encaissé" value={money(d.total)} color="var(--yalola-primary)" />
        <TxSummaryCard label="Ce mois-ci" value={money(d.this_month)} color="#E0A63C" />
        <TxSummaryCard label="Pubs facturées" value={d.paid_count ?? d.count} color="#6B7280" />
        <TxSummaryCard label="Pubs offertes" value={d.free_count ?? 0} color="#2D5A4C" />
      </div>
      {byMode.length > 0 && (
        <div className="flex flex-wrap gap-1.5 mt-3" data-testid="admin-offline-revenue-by-mode">
          {byMode.map(([m, v]) => <span key={m} className="chip text-[11px]" data-testid={`admin-offline-mode-${m}`}>{AD_BILLING_LABEL[m] || "Mode non précisé"} · {money(v)}</span>)}
        </div>
      )}
      {(() => {
        const monthly = buildMonthlyRevenue(d.ads, months);
        if (!monthly.some((m) => m.total > 0)) return null;
        return (
          <div className="mt-4" data-testid="admin-offline-revenue-chart">
            <div className="flex items-center justify-between mb-2">
              <div className="text-xs font-bold uppercase tracking-wide" style={{ color: "var(--yalola-text-muted)" }}>Revenus par mois</div>
              <div className="flex items-center gap-1" data-testid="admin-revenue-period">
                {[3, 6, 12].map((m) => (
                  <button
                    key={m}
                    onClick={() => setMonths(m)}
                    className="text-xs font-semibold rounded-full px-2.5 py-1 transition-colors"
                    style={months === m
                      ? { background: "var(--yalola-primary)", color: "#fff" }
                      : { background: "var(--yalola-surface-alt)", color: "var(--yalola-text-muted)" }}
                    data-testid={`admin-revenue-period-${m}`}
                  >
                    {m} mois
                  </button>
                ))}
              </div>
            </div>
            <div style={{ width: "100%", height: 170 }}>
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={monthly} margin={{ top: 5, right: 5, bottom: 0, left: -18 }}>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="var(--yalola-border)" />
                  <XAxis dataKey="label" tick={{ fontSize: 11 }} axisLine={false} tickLine={false} interval={0} />
                  <YAxis tick={{ fontSize: 11 }} axisLine={false} tickLine={false} width={48} />
                  <ChartTooltip formatter={(v) => [txFmt(v, cur), "Revenu"]} labelFormatter={(l) => `Mois : ${l}`} />
                  <Bar dataKey="total" fill="var(--yalola-primary)" radius={[6, 6, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        );
      })()}
    </div>
  );
};

// Shared "Comptabilité" overview — the SAME component (same data, same layout) for
// super admin, manager and associate, for transparency. The transactions
// search/filters/table remain exclusive to the super-admin view (TransactionsPanel).
const FinanceOverview = ({ isSuper = false, expenseViewRoles = [] }) => {
  const { t } = useApp();
  const cur = useAppCurrency();
  const [sum, setSum] = useState(null);
  const [bump, setBump] = useState(0);
  const months = React.useMemo(() => {
    const out = [];
    const now = new Date();
    for (let i = 0; i < 12; i += 1) {
      const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
      const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
      out.push({ key, label: d.toLocaleDateString("fr-FR", { month: "long", year: "numeric" }) });
    }
    return out;
  }, []);
  const [reportMonth, setReportMonth] = useState(months[0].key);
  const [downloading, setDownloading] = useState(false);
  const downloadReport = async () => {
    setDownloading(true);
    try {
      const res = await api.get(`/admin/finance/report.pdf?month=${reportMonth}`, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `yalola-compta-${reportMonth}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch { toast.error("Erreur lors de la génération du rapport"); }
    finally { setDownloading(false); }
  };
  useEffect(() => {
    api.get("/admin/transactions?limit=1").then(({ data }) => setSum(data.summary || {})).catch(() => setSum({}));
  }, [bump]);
  const s = sum || {};
  return (
    <div className="space-y-3" data-testid="finance-overview">
      <div className="yalola-card p-3 flex flex-wrap items-center justify-between gap-2" data-testid="finance-report-card">
        <div className="text-sm font-semibold flex items-center gap-2"><FileText size={16} style={{ color: "var(--yalola-primary)" }} /> {t("finance.report_title", "Rapport comptable mensuel (PDF)")}
          <span className="text-[11px] font-normal" style={{ color: "var(--yalola-text-muted)" }} data-testid="finance-report-weekly-hint">{t("finance.report_weekly_hint", "· inclut le détail par semaine")}</span>
        </div>
        <div className="flex items-center gap-2">
          <select
            value={reportMonth}
            onChange={(e) => setReportMonth(e.target.value)}
            className="h-9 rounded-md border px-2 text-sm capitalize"
            style={{ borderColor: "var(--yalola-border)", background: "var(--yalola-surface)" }}
            data-testid="finance-report-month"
          >
            {months.map((m) => <option key={m.key} value={m.key} className="capitalize">{m.label}</option>)}
          </select>
          <Button size="sm" className="rounded-full" onClick={downloadReport} disabled={downloading} data-testid="finance-report-download">
            {downloading ? "…" : "Télécharger"}
          </Button>
        </div>
      </div>
      <NetRevenueCard refreshToken={bump} isSuper={isSuper} />
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <TxSummaryCard label={t("finance.donations_paid", "Dons (payés)")} value={txFmt(s.donations_total, cur)} color="#D97757" />
        <TxSummaryCard label={t("finance.verifications", "Vérifications")} value={txFmt(s.verifications_total, cur)} color="#E0A63C" testId="tx-summary-verifications"
          hint={s.verifications_total ? `brut · frais stores/Stripe ${txFmt(s.verifications_fees, cur)} · TVA stores ${txFmt(s.verifications_tax, cur)} → net ${txFmt((s.verifications_total || 0) - (s.verifications_fees || 0) - (s.verifications_tax || 0), cur)}` : undefined} />
        <TxSummaryCard label={t("finance.withdrawals_paid", "Retraits payés")} value={txFmt(s.withdrawals_total, cur)} color="#6B7280" testId="tx-summary-withdrawals"
          hint={s.wallet ? (s.wallet.withdrawals_reconciled
            ? `${s.wallet.withdrawals_paid_count} retrait(s) · journal du portefeuille ✓${s.wallet.held ? ` · ${txFmt(s.wallet.held, cur)} bloqué(s) en cours` : ""}`
            : `⚠ écart journal ${txFmt(s.wallet.withdrawals_paid, cur)} / retraits ${txFmt(s.wallet.withdrawals_paid_collection, cur)}`) : undefined}
          hintColor={s.wallet && !s.wallet.withdrawals_reconciled ? "#B91C1C" : undefined} />
        <TxSummaryCard label={t("finance.ads_paid", "Publicités")} value={txFmt(s.ads_total, cur)} color="#8A5A12" />
      </div>
      {Array.isArray(s.currencies) && s.currencies.filter((c) => c !== cur).length > 0 && (
        <div className="rounded-lg border p-3 text-xs space-y-1" style={{ borderColor: "var(--yalola-border)" }} data-testid="tx-other-currencies">
          <p className="font-semibold" style={{ color: "var(--yalola-text-muted)" }}>
            Autres devises (montants tels quels — leur équivalent{s.fx_in_count ? ` : ${txFmt(s.fx_in, cur)} nets` : ""} entre dans le revenu net via la tuile « Autres devises »)
          </p>
          {s.currencies.filter((c) => c !== cur).map((c) => {
            const b = (s.by_currency || {})[c] || {};
            return (
              <p key={c} data-testid={`tx-currency-${c}`}>
                <b>{c}</b> — Dons {txFmt(b.donations_total, c)} · Vérif {txFmt(b.verifications_total, c)} · Retraits {txFmt(b.withdrawals_total, c)} · Pubs {txFmt(b.ads_total, c)}
              </p>
            );
          })}
        </div>
      )}
      <OfflineAdRevenueCard />
      <ExpensesPanel isSuper={isSuper} viewRoles={expenseViewRoles} onChanged={() => setBump((b) => b + 1)} />
      {sum === null && <AdminSkeleton />}
    </div>
  );
};

// Associate/manager accounting view — identical shared overview (transparency),
// aggregated amounts only, without the admin transactions filters/table.
// Lot 758 — la carte « Ma part » n'y reste que si l'onglet Finances › Associés est coupé (garde-fou `associate_my_card`).
const AccountingPanel = ({ isSuper = false, expenseViewRoles = [], showMyShare = false }) => (
  <div className="space-y-3" data-testid="admin-accounting-panel">
    {showMyShare && <MyAssociateShareCard />}
    <LaunchReportCard isSuper={isSuper} />
    <FinanceOverview isSuper={isSuper} expenseViewRoles={expenseViewRoles} />
    <WeeklyReportCard />
  </div>
);



const TransactionsPanel = ({ isSuper = false, expenseViewRoles = [] }) => {
  const [data, setData] = useState(null);
  const [type, setType] = useState("all");
  const [status, setStatus] = useState("all");
  const [q, setQ] = useState("");
  const [resetKey, setResetKey] = useState(0); // bumped after a financial reset / restore → everything reloads
  useEffect(() => {
    const id = setTimeout(() => {
      const params = new URLSearchParams();
      if (type !== "all") params.set("type", type);
      if (status !== "all") params.set("status", status);
      if (q.trim()) params.set("q", q.trim());
      api.get(`/admin/transactions?${params.toString()}`).then(({ data: d }) => setData(d)).catch(() => setData({ transactions: [], summary: {} }));
    }, 250);
    return () => clearTimeout(id);
  }, [type, status, q, resetKey]);

  const txs = data?.transactions || [];
  const txPager = usePager(txs);
  const [exporting, setExporting] = useState(false);
  const [reminding, setReminding] = useState("");
  // « Voir le journal » (Vue d'ensemble › Relances) : /admin/transactions?focus=journal → on arrive directement sur le tableau.
  const [journalParams] = useSearchParams();
  const journalRef = useRef(null);
  const focusJournal = journalParams.get("focus") === "journal";
  useEffect(() => {
    if (!focusJournal || !data || !journalRef.current) return;
    const t = setTimeout(() => journalRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }), 80);
    return () => clearTimeout(t);
  }, [focusJournal, data]);
  // « Relancer maintenant » : tentative abandonnée (badge Vérifié via Stripe, don) → même rappel doux que le job horaire (1 / 24 h).
  const canRemind = (tx) => ["verification", "donation"].includes(tx.type) && ["pending", "expired", "cancelled", "failed"].includes(tx.status)
    && (tx.type !== "verification" || tx.provider === "stripe") && !tx.is_test;
  const remindNow = async (tx) => {
    setReminding(tx.id);
    try {
      const { data: r } = await api.post(`/admin/transactions/${tx.type}/${tx.id}/remind`);
      toast.success(`${tx.from?.name || "Le membre"} a reçu un rappel doux`);
      setData((d) => ({ ...d, transactions: d.transactions.map((x) => (x.id === tx.id || (x.type === tx.type && x.from?.id === tx.from?.id && canRemind(x)) ? { ...x, reminder_sent_at: r.reminder_sent_at, reminder_skipped: null } : x)) }));
    } catch (e) { toast.error(e?.response?.data?.detail || e?.friendlyMessage || "Relance impossible"); }
    finally { setReminding(""); }
  };
  const exportCsv = async () => {
    setExporting(true);
    try {
      const params = new URLSearchParams();
      if (type !== "all") params.set("type", type);
      if (status !== "all") params.set("status", status);
      if (q.trim()) params.set("q", q.trim());
      const { data: blob } = await api.get(`/admin/transactions.csv?${params.toString()}`, { responseType: "blob" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a"); a.href = url; a.download = "yalola-comptabilite.csv";
      document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
    } catch { toast.error("Export impossible"); }
    finally { setExporting(false); }
  };

  return (
    <div className="space-y-3" data-testid="admin-transactions-panel">
      {isSuper && <FinanceResetCard onDone={() => setResetKey((k) => k + 1)} />}
      <LaunchReportCard key={`launch-${resetKey}`} isSuper={isSuper} />
      <FinanceOverview key={resetKey} isSuper={isSuper} expenseViewRoles={expenseViewRoles} />
      <WeeklyReportCard key={`weekly-${resetKey}`} />

      <div ref={journalRef} className="yalola-card p-4 space-y-3 scroll-mt-20" data-testid="tx-journal">
        <div className="flex items-center gap-2">
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Rechercher (nom, libellé, référence, provider)..." data-testid="tx-search" />
          <Button size="sm" variant="outline" className="rounded-full shrink-0" onClick={exportCsv} disabled={exporting} data-testid="tx-export-csv">
            <Download size={13} className="mr-1" /> {exporting ? "…" : "Exporter CSV"}
          </Button>
        </div>
        <div className="flex gap-2 flex-wrap">
          {TX_FILTERS.map((f) => (
            <button key={f.key} onClick={() => setType(f.key)} className={`pill ${type === f.key ? "pill-primary" : "pill-outline"}`} data-testid={`tx-type-${f.key}`}>{f.label}</button>
          ))}
        </div>
        <div className="flex gap-2 flex-wrap">
          {TX_STATUS.map((st) => (
            <button key={st} onClick={() => setStatus(st)} className={`pill text-xs ${status === st ? "pill-primary" : "pill-outline"}`} data-testid={`tx-status-${st}`}>
              {TX_STATUS_LABEL[st] || st}
            </button>
          ))}
        </div>
        {status === "all" && (
          <p className="text-[11px]" style={{ color: "var(--yalola-text-muted)" }} data-testid="tx-expired-hint">
            Les tentatives de paiement abandonnées (session Stripe expirée après 24 h, ou fenêtre de paiement fermée sans payer) sont masquées — filtres « Expirée » / « Annulé » pour les revoir. Elles ne comptent jamais dans les totaux.
            Retraits : seul un retrait « Payé » est une sortie définitive ; un retrait rejeté, annulé ou échoué est recrédité au membre.
          </p>
        )}
      </div>

      {data === null ? (
        <AdminSkeleton />
      ) : txs.length === 0 ? (
        <div className="yalola-card p-10 text-center" style={{ color: "var(--yalola-text-muted)" }} data-testid="tx-empty">Aucune transaction.</div>
      ) : (
        <div className="yalola-card overflow-x-auto" data-testid="admin-transactions-table">
          <Table>
            <TableHeader><TableRow>
              <TableHead>Date</TableHead><TableHead>Type</TableHead><TableHead>Libellé</TableHead>
              <TableHead>De</TableHead><TableHead>À / Bénéficiaire</TableHead><TableHead>Provider / méthode</TableHead>
              <TableHead>Brut</TableHead><TableHead>Frais · Commission</TableHead><TableHead>Net</TableHead><TableHead>Référence</TableHead><TableHead>Statut</TableHead>
            </TableRow></TableHeader>
            <TableBody>
              {txPager.slice.map((tx) => {
                const meta = TX_META[tx.type] || {};
                const isOut = (tx.type === "withdrawal" && tx.wallet_effect === "debited") || (tx.signed_amount ?? 0) < 0;
                const pending = tx.type === "withdrawal" && tx.wallet_effect !== "debited";
                return (
                  <TableRow key={tx.id} data-testid={`tx-row-${tx.id}`} style={pending ? { opacity: 0.85 } : undefined}>
                    <TableCell className="text-xs whitespace-nowrap">{tx.date ? new Date(tx.date).toLocaleDateString("fr-FR", { day: "2-digit", month: "short", year: "numeric" }) : "—"}</TableCell>
                    <TableCell>
                      <span className="chip text-[10px]" style={{ background: `${meta.color}1a`, color: meta.color }}>{meta.label || tx.type}</span>
                      {tx.legacy && <div className="text-[9px] mt-0.5 uppercase tracking-wide" style={{ color: "var(--yalola-text-muted)" }} data-testid={`tx-legacy-${tx.id}`}>historique</div>}
                    </TableCell>
                    <TableCell className="text-xs max-w-[200px]">
                      <div className="truncate" title={tx.label}>{tx.label}</div>
                      {tx.wallet_effect && <div className="text-[10px]" style={{ color: tx.wallet_effect === "released" ? "#92400E" : "var(--yalola-text-muted)" }} data-testid={`tx-effect-${tx.id}`}>{TX_EFFECT_LABEL[tx.wallet_effect] || tx.wallet_effect}</div>}
                      {tx.reason && <div className="text-[10px] truncate" title={tx.reason} style={{ color: "var(--yalola-text-muted)" }}>Motif : {tx.reason}</div>}
                    </TableCell>
                    <TableCell className="text-xs"><TxParty p={tx.from} /></TableCell>
                    <TableCell className="text-xs">
                      <TxParty p={tx.to} />
                      {tx.destination && <div className="text-[10px] truncate max-w-[180px]" title={tx.destination} style={{ color: "var(--yalola-text-muted)" }} data-testid={`tx-destination-${tx.id}`}>{tx.destination}</div>}
                    </TableCell>
                    <TableCell className="text-xs whitespace-nowrap" data-testid={`tx-provider-${tx.id}`}>{TX_PROVIDER_LABEL[tx.provider] || tx.provider || "—"}{tx.platform && tx.platform !== "web" ? ` · ${tx.platform}` : ""}</TableCell>
                    <TableCell className="font-semibold whitespace-nowrap" data-testid={`tx-amount-${tx.id}`}>{isOut ? "−" : ""}{txFmt(tx.amount, tx.currency)}</TableCell>
                    <TableCell className="text-xs whitespace-nowrap" style={{ color: "var(--yalola-text-muted)" }}>
                      {tx.fee ? txFmt(tx.fee, tx.currency) : "—"}{tx.commission ? ` · ${txFmt(tx.commission, tx.currency)}` : ""}{tx.tax ? ` · TVA ${txFmt(tx.tax, tx.currency)}` : ""}
                    </TableCell>
                    <TableCell className="text-xs whitespace-nowrap">{tx.net != null ? txFmt(tx.net, tx.currency) : "—"}</TableCell>
                    <TableCell className="text-[10px] font-mono max-w-[140px] truncate" title={tx.ref || ""}>{tx.ref || "—"}</TableCell>
                    <TableCell>
                      <span className="chip text-[10px]" style={TX_STATUS_STYLE[tx.status] || {}} data-testid={`tx-status-chip-${tx.id}`}>{TX_STATUS_LABEL[tx.status] || tx.status}</span>
                      {canRemind(tx) && (
                        <div className="mt-1 flex items-center gap-1 flex-wrap">
                          {tx.reminder_sent_at && !tx.reminder_skipped && (
                            <span className="text-[10px] whitespace-nowrap" style={{ color: "#2D5A4C" }} data-testid={`tx-reminded-${tx.id}`}>
                              relancé le {new Date(tx.reminder_sent_at).toLocaleDateString("fr-FR", { day: "2-digit", month: "short" })}
                            </span>
                          )}
                          {(!tx.reminder_sent_at || tx.reminder_skipped || Date.now() - new Date(tx.reminder_sent_at).getTime() > 24 * 3600 * 1000) && (
                            <button type="button" onClick={() => remindNow(tx)} disabled={reminding === tx.id}
                                    className="inline-flex items-center gap-1 text-[10px] font-semibold px-2 py-0.5 rounded-full"
                                    style={{ background: "#E7F1EE", color: "#2D5A4C" }} title="Envoyer un rappel doux (notification) à ce membre" data-testid={`tx-remind-${tx.id}`}>
                              {reminding === tx.id ? <Loader2 size={10} className="animate-spin" /> : <BellRing size={10} />} Relancer maintenant
                            </button>
                          )}
                        </div>
                      )}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </div>
      )}
      <PagerBar page={txPager.page} pages={txPager.pages} total={txPager.total} shown={txPager.slice.length} onPage={txPager.setPage} noun="mouvement" testId="tx-pager" />
    </div>
  );
};

const DONATION_STATUS_FR = { pending: "En attente", paid: "Payé", cancelled: "Annulé", failed: "Échoué", expired: "Expiré", refunded: "Remboursé" };

const DonationsTable = () => {
  const [donations, setDonations] = useState([]);
  useEffect(() => { api.get("/admin/donations").then(({ data }) => setDonations(data.donations || [])).catch(() => {}); }, []);
  return (
    <div className="yalola-card overflow-x-auto" data-testid="admin-donations-table">
      <Table>
        <TableHeader><TableRow>
          <TableHead>Date</TableHead><TableHead>De</TableHead><TableHead>À</TableHead><TableHead>Montant</TableHead><TableHead>Commission</TableHead><TableHead>Statut</TableHead>
        </TableRow></TableHeader>
        <TableBody>
          {donations.map(d => (
            <TableRow key={d.id} data-testid={`donation-${d.id}`}>
              <TableCell className="text-xs">{new Date(d.created_at).toLocaleString()}</TableCell>
              <TableCell>{d.sender?.name}</TableCell>
              <TableCell>{d.receiver?.name} <span className="chip text-[10px] capitalize">{d.receiver?.role}</span></TableCell>
              <TableCell className="font-semibold">{d.amount} {d.currency === "EUR" ? "€" : "$"}</TableCell>
              <TableCell>{d.commission} {d.currency === "EUR" ? "€" : "$"}</TableCell>
              <TableCell>
                <span className="chip" data-testid={`donation-status-${d.id}`}>{DONATION_STATUS_FR[d.payment_status] || d.payment_status}</span>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
};

const AUDIT_MEDIA_ACTIONS = {
  "media.allow": { label: "Autorisé", bg: "#DCFCE7", color: "#15803D" },
  "media.review": { label: "File de modération", bg: "#FEF3C7", color: "#B45309" },
  "media.block": { label: "Bloqué", bg: "#FEE2E2", color: "#B91C1C" },
};
const AUDIT_CAT_FR = {
  nudity: "Nudité", sexual: "Sexuel", suggestive: "Suggestif", violence: "Violence",
  gore: "Gore", hate: "Symboles haineux", drugs: "Alcool/drogue", safe: "Sûr",
};
const AUDIT_TARGET_FR = { image: "Image", video: "Vidéo", post: "Publication", story: "Story", media: "Média" };
const AUDIT_CTX_FR = {
  feed: "fil", story: "story", chat: "chat", direct: "upload direct", upload: "upload",
  post: "publication", avatar: "avatar", cover: "couverture", group: "groupe", reel: "reel",
};

const AuditPanel = () => {
  const [logs, setLogs] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [filter, setFilter] = useState("all");
  const [q, setQ] = useState("");
  const pages = Math.max(1, Math.ceil(total / ADMIN_PAGE_SIZE));
  useEffect(() => { setPage(1); }, [filter, q]);
  useEffect(() => {
    const tm = setTimeout(() => {
      api.get(`/admin/audit-logs?filter=${filter}&q=${encodeURIComponent(q.trim())}&limit=${ADMIN_PAGE_SIZE}&skip=${(page - 1) * ADMIN_PAGE_SIZE}`)
        .then(({ data }) => { setLogs(data.logs || []); setTotal(data.total ?? (data.logs || []).length); })
        .catch(() => {});
    }, q ? 300 : 0);
    return () => clearTimeout(tm);
  }, [filter, page, q]);
  return (
    <div className="space-y-3" data-testid="admin-audit-panel">
      <div className="flex items-center gap-2 flex-wrap">
        {[{ k: "all", l: "Tout" }, { k: "media", l: "Décisions médias (IA)" }, { k: "posts", l: "Publications (republier · annuler · supprimer)" }, { k: "admin", l: "Actions admin" }].map((f) => (
          <button key={f.k} onClick={() => setFilter(f.k)} data-testid={`audit-filter-${f.k}`}
            className="px-3 py-1.5 rounded-full text-xs font-semibold transition-colors"
            style={filter === f.k
              ? { background: "var(--yalola-primary)", color: "#fff" }
              : { background: "var(--yalola-surface-alt)", color: "var(--yalola-text-muted)" }}>
            {f.l}
          </button>
        ))}
      </div>
      <div className="relative max-w-md" data-testid="audit-search-wrap">
        <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2" style={{ color: "var(--yalola-text-muted)" }} />
        <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Rechercher un membre (nom, e-mail), une action, une cible…" className="pl-9 rounded-full" data-testid="audit-search" />
        {q && <button type="button" onClick={() => setQ("")} className="absolute right-3 top-1/2 -translate-y-1/2 text-xs" style={{ color: "var(--yalola-text-muted)" }} data-testid="audit-search-clear">Effacer</button>}
      </div>
      <p className="text-xs" style={{ color: "var(--yalola-text-muted)" }}>
        « Décisions médias (IA) » montre pourquoi chaque image/vidéo a été autorisée, mise en file de modération ou bloquée, avec la catégorie détectée.
      </p>
      <div className="yalola-card overflow-x-auto">
        <Table>
          <TableHeader><TableRow><TableHead>Date</TableHead><TableHead>Action</TableHead><TableHead>Acteur</TableHead><TableHead>Cible</TableHead><TableHead>Détails</TableHead></TableRow></TableHeader>
          <TableBody>
            {logs.map(l => {
              const media = AUDIT_MEDIA_ACTIONS[l.action];
              const meta = l.meta || {};
              return (
                <TableRow key={l.id} data-testid={`audit-${l.id}`}>
                  <TableCell className="text-xs whitespace-nowrap">{new Date(l.created_at).toLocaleString()}</TableCell>
                  <TableCell className="text-xs">
                    {media ? (
                      <span className="px-2 py-0.5 rounded-full font-semibold whitespace-nowrap" style={{ background: media.bg, color: media.color }} data-testid={`audit-media-decision-${l.id}`}>
                        {media.label}
                      </span>
                    ) : (
                      <span className="font-mono">{l.action}</span>
                    )}
                  </TableCell>
                  <TableCell className="text-xs">{l.actor?.name || l.actor_id?.slice(0, 8) || "—"}</TableCell>
                  <TableCell className="text-xs whitespace-nowrap">
                    {media
                      ? `${AUDIT_TARGET_FR[l.target_type] || l.target_type}${meta.context ? ` · ${AUDIT_CTX_FR[meta.context] || meta.context}` : ""}`
                      : `${l.target_type}:${l.target_id?.slice(0, 8)}`}
                  </TableCell>
                  <TableCell className="text-xs">
                    {media ? (
                      <div className="max-w-md">
                        <div className="flex items-center gap-1 flex-wrap">
                          {(meta.categories || []).filter(c => c && c !== "safe").map((c) => (
                            <span key={c} className="px-1.5 py-0.5 rounded-full text-[10px] font-semibold" style={{ background: "var(--yalola-surface-alt)" }}>
                              {AUDIT_CAT_FR[c] || c}
                            </span>
                          ))}
                          {meta.level && (
                            <span className="text-[10px] uppercase tracking-wide" style={{ color: "var(--yalola-text-muted)" }}>
                              {meta.level === "explicit" ? "explicite" : meta.level === "borderline" ? "limite" : "sûr"}
                            </span>
                          )}
                        </div>
                        {meta.reason && <div className="mt-0.5" style={{ color: "var(--yalola-text-muted)" }}>{meta.reason}</div>}
                      </div>
                    ) : (
                      JSON.stringify(l.meta)
                    )}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
        {logs.length === 0 && (
          <div className="p-6 text-center text-sm" style={{ color: "var(--yalola-text-muted)" }} data-testid="audit-empty">{q ? `Aucune entrée pour « ${q} ».` : "Aucune entrée pour ce filtre."}</div>
        )}
      </div>
      <PagerBar page={page} pages={pages} total={total} shown={logs.length} onPage={setPage} noun="entrée" testId="audit-pager" />
    </div>
  );
};

const EV_FILTERS = [
  { key: "all", label: "Tout" },
  { key: "quarantined", label: "En quarantaine" },
  { key: "reported", label: "Signalés" },
  { key: "csae", label: "CSAE" },
];

// Authenticated media loader for a quarantined evidence item (blob → object URL).
const EvidenceMedia = ({ id, mediaType }) => {
  const [src, setSrc] = useState(null);
  const [err, setErr] = useState(false);
  useEffect(() => {
    let url; let alive = true;
    api.get(`/admin/moderation/evidence/${id}/media`, { responseType: "blob" })
      .then((res) => { if (!alive) return; url = URL.createObjectURL(res.data); setSrc(url); })
      .catch(() => setErr(true));
    return () => { alive = false; if (url) URL.revokeObjectURL(url); };
  }, [id]);
  if (err) return <div className="h-40 grid place-items-center text-xs rounded-lg" style={{ background: "var(--yalola-surface-alt)", color: "var(--yalola-text-muted)" }}>Média indisponible</div>;
  if (!src) return <div className="h-40 grid place-items-center rounded-lg" style={{ background: "var(--yalola-surface-alt)" }}><Loader2 className="animate-spin" size={18} /></div>;
  if (mediaType === "video") return <video src={src} poster={NEUTRAL_POSTER} controls className="w-full max-h-56 rounded-lg bg-black" data-testid={`evidence-video-${id}`} />;
  return <img src={src} alt="preuve" className="w-full max-h-56 object-contain rounded-lg bg-black" data-testid={`evidence-img-${id}`} />;
};

// SUPER ADMIN ONLY: viewer for blocked media kept as evidence. CSAE imagery is never
// auto-displayed (legal protocol); only a "report to authorities" logging action.
const EvidencePanel = () => {
  const [items, setItems] = useState([]);
  const evPager = usePager(items);
  const [filter, setFilter] = useState("all");
  const [stats, setStats] = useState({ total: 0, csae_count: 0 });
  const [busy, setBusy] = useState(null);
  const [reveal, setReveal] = useState({});
  const load = () => api.get(`/admin/moderation/evidence?filter=${filter}`)
    .then(({ data }) => { setItems(data.items || []); setStats({ total: data.total || 0, csae_count: data.csae_count || 0 }); })
    .catch(() => {});
  useEffect(() => { load(); }, [filter]); // eslint-disable-line react-hooks/exhaustive-deps
  const act = async (id, path, body, okMsg) => {
    setBusy(id);
    try { await api.post(`/admin/moderation/evidence/${id}/${path}`, body || {}); toast.success(okMsg); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Erreur"); }
    finally { setBusy(null); }
  };
  const reportAuthorities = (id) => {
    const ref = window.prompt("Référence du signalement aux autorités (n° dossier / NCMEC / police) :", "");
    if (ref === null) return;
    act(id, "report", { ref }, "Signalé aux autorités (action journalisée)");
  };
  const ban = (id) => {
    const reason = window.prompt("Motif du bannissement :", "Contenu inapproprié (preuve modération)");
    if (reason === null) return;
    act(id, "ban", { reason }, "Auteur banni");
  };
  const del = async (id) => {
    if (!window.confirm("Supprimer définitivement cette preuve ?")) return;
    setBusy(id);
    try { await api.delete(`/admin/moderation/evidence/${id}`); toast.success("Preuve supprimée"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Erreur"); }
    finally { setBusy(null); }
  };
  return (
    <div className="space-y-4" data-testid="admin-evidence-panel">
      <div className="yalola-card p-4">
        <div className="flex items-center gap-2 mb-1"><ShieldAlert size={18} style={{ color: "var(--yalola-primary)" }} /><h3 className="font-bold">Quarantaine des médias bloqués</h3></div>
        <p className="text-xs" style={{ color: "var(--yalola-text-muted)" }}>Médias refusés automatiquement par la modération, conservés comme preuve (super admin uniquement). Chaque ouverture est journalisée. Total : {stats.total} · CSAE : {stats.csae_count}.</p>
        <div className="flex gap-2 mt-3 flex-wrap">
          {EV_FILTERS.map((f) => (
            <button key={f.key} onClick={() => setFilter(f.key)} className="px-3 py-1.5 rounded-full text-xs font-medium" style={filter === f.key ? { background: "var(--yalola-primary)", color: "#fff" } : { background: "var(--yalola-surface-alt)", color: "var(--yalola-text)" }} data-testid={`evidence-filter-${f.key}`}>{f.label}</button>
          ))}
        </div>
      </div>
      {items.length === 0 && <div className="yalola-card p-8 text-center" style={{ color: "var(--yalola-text-muted)" }} data-testid="evidence-empty">Aucune preuve en quarantaine.</div>}
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
        {evPager.slice.map((it) => (
          <div key={it.id} className="yalola-card p-3 space-y-2" data-testid={`evidence-${it.id}`} style={{ border: it.is_csae ? "2px solid #e11d48" : undefined }}>
            <div className="flex items-center justify-between gap-2 flex-wrap">
              <div className="flex items-center gap-1.5">
                {it.is_csae && <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-red-600 text-white font-bold">CSAE</span>}
                <span className="text-[10px] px-1.5 py-0.5 rounded-full font-semibold capitalize" style={{ background: "var(--yalola-surface-alt)" }}>{it.status}</span>
                <span className="text-[10px]" style={{ color: "var(--yalola-text-muted)" }}>{it.media_type}</span>
              </div>
              <span className="text-[10px]" style={{ color: "var(--yalola-text-muted)" }}>{new Date(it.created_at).toLocaleString()}</span>
            </div>
            {it.is_csae && !reveal[it.id] ? (
              <div className="h-40 grid place-items-center rounded-lg text-center p-3" style={{ background: "#7f1d1d", color: "#fff" }}>
                <div>
                  <Lock size={20} className="mx-auto mb-1" />
                  <div className="text-xs font-semibold">Contenu CSAE — non affiché</div>
                  <div className="text-[10px] opacity-80 mt-0.5">Signalez aux autorités. Ne pas visionner sauf nécessité légale.</div>
                </div>
              </div>
            ) : (
              <EvidenceMedia id={it.id} mediaType={it.media_type} />
            )}
            <div className="text-[11px]" style={{ color: "var(--yalola-text-muted)" }}>
              Auteur : <span className="font-semibold" style={{ color: "var(--yalola-text)" }}>{it.uploader?.name || it.uploader_id?.slice(0, 8) || "?"}</span>
              {it.uploader?.email ? ` · ${it.uploader.email}` : ""}
              {it.uploader_banned && <span className="ml-1 text-red-600 font-semibold">· banni</span>}
            </div>
            {it.verdict?.reason && <div className="text-[11px]" style={{ color: "var(--yalola-text-muted)" }}>Verdict : {it.verdict.level} — {it.verdict.reason}</div>}
            <div className="flex flex-wrap gap-1.5 pt-1">
              <Button size="sm" variant="outline" className="rounded-full text-xs h-7" onClick={() => reportAuthorities(it.id)} disabled={busy === it.id} data-testid={`evidence-report-${it.id}`}><AlertTriangle size={13} className="mr-1" />Signaler aux autorités</Button>
              {!it.is_csae && <Button size="sm" variant="outline" className="rounded-full text-xs h-7" onClick={() => act(it.id, "flag-csae", {}, "Marqué comme CSAE")} disabled={busy === it.id} data-testid={`evidence-flag-${it.id}`}><ShieldAlert size={13} className="mr-1" />Marquer CSAE</Button>}
              {it.is_csae && (reveal[it.id]
                ? <Button size="sm" variant="outline" className="rounded-full text-xs h-7" onClick={() => setReveal((r) => ({ ...r, [it.id]: false }))} data-testid={`evidence-hide-${it.id}`}><EyeOff size={13} className="mr-1" />Masquer</Button>
                : <Button size="sm" variant="outline" className="rounded-full text-xs h-7 text-red-700" onClick={() => { if (window.confirm("Révéler ce contenu CSAE ? Action strictement réservée à une nécessité légale et journalisée.")) setReveal((r) => ({ ...r, [it.id]: true })); }} data-testid={`evidence-reveal-${it.id}`}><Eye size={13} className="mr-1" />Révéler (journalisé)</Button>
              )}
              <Button size="sm" variant="outline" className="rounded-full text-xs h-7 text-red-600" onClick={() => ban(it.id)} disabled={busy === it.id || it.uploader_banned} data-testid={`evidence-ban-${it.id}`}><Ban size={13} className="mr-1" />Bannir</Button>
              <Button size="sm" variant="outline" className="rounded-full text-xs h-7" onClick={() => del(it.id)} disabled={busy === it.id} data-testid={`evidence-delete-${it.id}`}><Trash2 size={13} /></Button>
            </div>
            {it.status === "reported" && <div className="text-[10px] text-green-700 font-semibold" data-testid={`evidence-reported-${it.id}`}>✓ Signalé aux autorités{it.report_ref ? ` (réf. ${it.report_ref})` : ""}</div>}
          </div>
        ))}
      </div>
      <PagerBar page={evPager.page} pages={evPager.pages} total={evPager.total} shown={evPager.slice.length} onPage={evPager.setPage} noun="preuve" testId="evidence-pager" />
    </div>
  );
};

export default Admin;